import { debugLog } from "../utils/debugLog.js";
import {
  MagnifyingGlassIcon,
  ClockIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  MinusIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  CurrencyDollarIcon,
  UserCircleIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
  ChatBubbleOvalLeftIcon,
  ChatBubbleLeftRightIcon,
  EyeIcon,
  CloudIcon,
  CloudArrowUpIcon,
  ArrowUpIcon,
  PlusCircleIcon,
  TrashIcon,
  HeartIcon,
  BookmarkIcon,
  Squares2X2Icon,
  Bars3Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  BoltIcon,
  WrenchScrewdriverIcon,
  CubeTransparentIcon,
} from "@heroicons/react/24/outline";
import { ReactTyped as Typed } from "react-typed";
import {
  useState,
  Fragment,
  useEffect,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Listbox, Dialog, Transition } from "@headlessui/react";
import { ChevronUpDownIcon, SparklesIcon } from "@heroicons/react/20/solid";
import {
  CheckIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/20/solid";
import Airtable from "airtable";
import { XMarkIcon, ArrowUpOnSquareIcon } from "@heroicons/react/24/outline";
import { automationCards, getAutomationCards } from "./automationCards";
import { ShieldCheckIcon } from "@heroicons/react/24/outline";
import { useUser } from "@clerk/clerk-react";
import AuthPromptModal from "./AuthPromptModal";
import Joyride, { STATUS } from "react-joyride";
import { syncAutomationsToAirtable } from "../utils/airtableSync";
import { useLocation } from "react-router-dom";
import { PencilIcon, PlusIcon, PhotoIcon } from "@heroicons/react/24/outline";
import {
  automationSchema,
  generateFieldPrompt,
  validateField,
  generateExampleOutputsPrompt,
  formatForAirtable,
} from "../utils/automationSchema";
import { iconMap } from "./iconMap";
// OpenAI import removed - all API calls now go through server-side routes for security
import React, { memo } from "react";
import Notification from "../Notification";
import { setShowNotif, setContents } from "../helpers";
import Fuse from "fuse.js";
import { Popover } from "@headlessui/react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { currencyFormat, formatFormulaPercentages } from "../helpers";
import { evaluate } from "mathjs";
import { formatDistanceToNow } from "date-fns";
import * as HeroIcons from "@heroicons/react/24/outline";

const base = new Airtable({
  apiKey: process.env.REACT_APP_AIR_KEY,
}).base(process.env.REACT_APP_AIR_BASEID);
const debugAutomation = process.env.REACT_APP_DEBUG_VERBOSE === 'true';

// Add this before the AutomationDirectory component definition
const getCompanyFromEmail = async (email) => {
  if (!email) return null;
  const domain = email.split("@")[1];

  try {
    const records = await base("companies")
      .select({
        maxRecords: 1,
        filterByFormula: `REGEX_MATCH({Domain}, '^${domain}$')`,
      })
      .all();

    return records[0] || null;
  } catch (error) {
    console.error("Error fetching company:", error);
    return null;
  }
};

const apiKey = process.env.REACT_APP_AIR_KEY;
const baseId = process.env.REACT_APP_AIR_BASEID;
const usersTable = process.env.REACT_APP_AIR_USERS;

// Add field type definitions
const FIELD_TYPES = {
  Name: "string",
  Domain: "string",
  Industry: "string",
  Address: "string",
  ListSize: "number",
  AnnualRevenue: "number",
  OperationalCosts: "number",
  Debt: "number",
  ConversionRate: "number", // Decimal between 0-1
  FollowerCount: "number",
  AvgDealSize: "number",
  Technologies: "string",
};

// const STRIPE_DEPOSIT_LINK = 'https://buy.stripe.com/6oEdUAfk68WM6LS7sP';
const STRIPE_DEPOSIT_LINK = "https://buy.stripe.com/7sI03K3Bo1ukgms4gG";

const createSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
};

// Helper function to format titles with asterisks and line breaks
const formatTitle = (title) => {
  if (!title) return "";

  // First, remove everything after the colon (existing behavior)
  const titleWithoutColon = title.split(":")[0];

  // Then, add line breaks before text with asterisks on either side
  return titleWithoutColon.replace(/\s*\*[^*]+\*\s*/g, (match) => {
    return `\n${match.trim()}`;
  });
};

// SECURITY FIX: Removed hardcoded API key and client-side OpenAI calls
// All OpenAI API calls must go through server-side routes for security
// If you need to test OpenAI, create a server-side API route instead

// Add this near the top of the file, outside of any component
const DEFAULT_AVATAR =
  "https://media.licdn.com/dms/image/v2/D5603AQG02L5jUZc7mg/profile-displayphoto-shrink_200_200/profile-displayphoto-shrink_200_200/0/1689863043738?e=2147483647&v=beta&t=Iwe-SLYHEhrwBDkeKXSwOjnq0lr83MteuMpkzm5uRbE";

// Create a context to share user data
const UsersContext = React.createContext();

// Create a provider component
const UsersProvider = ({ children }) => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const records = await base("tbln16qEmZSpV1gr8")
          .select({
            maxRecords: 1000, // Adjust based on your needs
          })
          .all();

        // Convert records to array of users, using the record ID as the user ID
        // const usersArray = records.map(record => ({
        //   id: record.id, // This is the Airtable record ID
        //   userId: record.fields.UserID,
        //   profileImage: record.fields.ProfileImage ||
        //                record.fields.Avatar ||
        //                record.fields.Image ||
        //                DEFAULT_AVATAR,
        //   name: record.fields.Name,
        //   email: record.fields.Email,
        // }));
        if (debugAutomation) debugLog("records", records);
        setUsers(records);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllUsers();
  }, []);

  return (
    <UsersContext.Provider value={{ users, isLoading }}>
      {children}
    </UsersContext.Provider>
  );
};

// Simplified UserAvatarList component with performance optimizations
const UserAvatarList = memo(({ userIds, maxDisplay = 3 }) => {
  const { users, isLoading } = useContext(UsersContext);

  const displayUsers = useMemo(() => {
    if (!userIds?.length) return [];
    return users
      .filter((user) => userIds.includes(user.id))
      .slice(0, maxDisplay);
  }, [users, userIds, maxDisplay]);

  if (isLoading) {
    return (
      <div className="flex -space-x-1 overflow-hidden">
        {[...Array(Math.min(maxDisplay, userIds?.length || 0))].map(
          (_, idx) => (
            <div
              key={idx}
              className="animate-pulse inline-block h-6 w-6 rounded-full bg-gray-200 ring-2 ring-white"
            />
          )
        )}
      </div>
    );
  }

  if (!displayUsers.length) return null;

  return (
    <div className="flex -space-x-1 overflow-hidden">
      {displayUsers.map((user) => {
        // Get the first two letters of the email for fallback
        const emailInitials = user.fields?.Email
          ? user.fields.Email.substring(0, 2).toUpperCase()
          : "??";

        return (
          <div key={user.id} className="inline-block h-6 w-6">
            <img
              className="h-6 w-6 rounded-full ring-2 ring-white"
              src={user.fields?.ProfileImage}
              alt=""
              onError={(e) => {
                // Replace with initials circle on error
                const parent = e.target.parentElement;
                parent.innerHTML = `<div class="h-6 w-6 rounded-full font-bold ring-2 ring-white bg-[#9CA3AF] text-white flex items-center justify-center text-xs" title="${
                  user.fields?.Email || "Unknown user"
                }">${emailInitials}</div>`;
              }}
            />
          </div>
        );
      })}
    </div>
  );
});

UserAvatarList.displayName = "UserAvatarList";

// Add this new component above the main component
const RequirementTextArea = ({
  name,
  label,
  value,
  onChange,
  placeholder,
  error,
}) => {
  const [localValue, setLocalValue] = useState(value || "");

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  return (
    <div className="space-y-2 mb-4">
      <label htmlFor={name} className="block text-sm font-medium text-gray-900">
        {label}
        <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <textarea
          id={name}
          name={name}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={() => onChange(localValue)}
          placeholder={placeholder}
          rows={2}
          className="block w-full rounded-md border-gray-300 shadow-sm 
                focus:border-blue-500 focus:ring-blue-500 
                transition-colors placeholder:whitespace-pre-wrap"
          required
        />
        {error && <div className="text-red-500 text-xs mt-1">{error}</div>}
      </div>
    </div>
  );
};

// Add these constants near the top of the file (before the AutomationDirectory component)
const INITIAL_CARDS_TO_LOAD = 40;
const CARDS_PER_BATCH = 20;
const INITIAL_CARDS_TO_SHOW = 40;

// In a custom hook (useInfiniteScroll.js)
export function useInfiniteScroll(fetchMore) {
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (!isFetching) return;

    // Fix the problematic line by adding proper checks
    if (fetchMore && typeof fetchMore === "function") {
      try {
        const result = fetchMore();

        // Check if result is a Promise before calling .then()
        if (result && typeof result.then === "function") {
          result
            .then((hasMoreItems) => {
              setIsFetching(false);
              // Handle hasMoreItems if needed
            })
            .catch((error) => {
              console.error("Error in fetchMore:", error);
              setIsFetching(false);
            });
        } else {
          // Handle case where fetchMore doesn't return a Promise
          console.warn("fetchMore did not return a Promise");
          setIsFetching(false);
        }
      } catch (error) {
        console.error("Error executing fetchMore:", error);
        setIsFetching(false);
      }
    } else {
      // Handle case where fetchMore is not a function
      console.error("fetchMore is not a function or is undefined");
      setIsFetching(false);
    }
  }, [isFetching, fetchMore]);

  // Rest of the hook implementation...

  return [isFetching, setIsFetching];
}

// Helper to get prioritized cards in order
const getPrioritizedCards = (prioritizedIds, allCards) =>
  prioritizedIds
    .map((id) => allCards.find((card) => card.id === id))
    .filter(Boolean);

/** Type → symbol (icon-only, light blue). Tooltip keeps the readable label. */
const AUTOMATION_TYPE_META = {
  template: { label: "Template", Icon: DocumentDuplicateIcon },
  email_template: { label: "Template", Icon: DocumentDuplicateIcon },
  prompt: { label: "Prompt", Icon: SparklesIcon },
  sop: { label: "SOP", Icon: ClipboardDocumentCheckIcon },
  custom: { label: "Custom", Icon: WrenchScrewdriverIcon },
  automation: { label: "Automation", Icon: BoltIcon },
  workflow: { label: "Workflow", Icon: BoltIcon },
};

const AutomationTypeBadge = ({ type }) => {
  if (!type) return null;
  const key = String(type).toLowerCase().trim().replace(/\s+/g, "_");
  const meta = AUTOMATION_TYPE_META[key] || {
    label: typeof type === "string" ? type.charAt(0).toUpperCase() + type.slice(1) : String(type),
    Icon: CubeTransparentIcon,
  };
  const Icon = meta.Icon;
  return (
    <span className="group/type relative inline-flex flex-shrink-0">
      <span
        aria-label={meta.label}
        className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sky-50 text-sky-700 ring-1 ring-sky-100"
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-full top-1/2 z-30 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover/type:opacity-100"
      >
        {meta.label}
        <span
          className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"
          aria-hidden="true"
        />
      </span>
    </span>
  );
};

const DEFAULT_FALLBACK_ICONS = [iconMap.ai, iconMap.upgraded].filter(Boolean);

const AutomationDirectory = ({
  company,
  runTour = false,
  setRunTour = () => {},
  admin = false,
  isAdmin,
  isDarkMode = false,
  // Add filter props
  isFiltersOpen,
  setIsFiltersOpen,
  filters = { customerFocus: [], industries: [], sizes: [], stages: [], automationStatus: [] },
  setFilters,
  companyData,
  setCompanyData,
  // Add category and stage props
  categories = [],
  setCategories,
  stages = [],
  setStages,
  selectedCategory = "all",
  setSelectedCategory,
  selectedStage = "all",
  setSelectedStage,
  showPrioritizedList = false,
  setShowPrioritizedList,
  handleStageChange,
  // Add clipboard list props
  prioritizedIds = [],
  setPrioritizedIds,
  allCards = [],
  setAllCards,
  // Add action buttons hover state
  isHoveringActionButtons = false,
  // Add animation pause state
  isAnimationPaused = false,
  // Add reset animation callback ref
  resetAnimationRef = null,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCard, setSelectedCard] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showRequirementsForm, setShowRequirementsForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [isOneClickInstalling, setIsOneClickInstalling] = useState(false);
  // allCards now passed as props
  const [filteredCards, setFilteredCards] = useState([]);
  const [displayedCards, setDisplayedCards] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreCards, setHasMoreCards] = useState(true);
  const [isFocusedLayout, setIsFocusedLayout] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'card' or 'list'
  const { isLoaded, isLoaded: isUsersLoading, isSignedIn, user } = useUser();
  const [urlParams] = useState(new URLSearchParams(window.location.search));
  const [heartedCards, setHeartedCards] = useState(() => {
    const saved = localStorage.getItem("heartedCards");
    return saved ? JSON.parse(saved) : [];
  });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedCard, setEditedCard] = useState(null);
  const [showCopyFeedback, setShowCopyFeedback] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentImageSet, setCurrentImageSet] = useState(0);
  const [showGuideImage, setShowGuideImage] = useState(true);
  const [offerAmount, setOfferAmount] = useState("");
  const [offerNotification, setOfferNotification] = useState({
    showNotif: false,
    contents: {
      type: "",
      heading: "",
      message: "",
    },
  });
  const [showOfferNotification, setShowOfferNotification] = useState(false);
  const [notification, setNotification] = useState({
    showNotif: false,
    contents: {
      type: "",
      heading: "",
      message: "",
    },
  });
  const [fuseInstance, setFuseInstance] = useState(null);
  // Add this to your state declarations at the top
  const [exactMatches, setExactMatches] = useState([]);
  // Add these state variables at the top
  const [exactResults, setExactResults] = useState([]);
  const [fuzzyResults, setFuzzyResults] = useState([]);
  // Add this near the top where other state is managed
  const [isSubmittingInterest, setIsSubmittingInterest] = useState(false);
  // Add this state to track if initial load is complete
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  const [isLoadingCompany, setIsLoadingCompany] = useState(false);

  // Add these state variables near your existing state declarations
  const [notes, setNotes] = useState([]);
  const [noteContent, setNoteContent] = useState("");
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [showAddNoteForm, setShowAddNoteForm] = useState(false);

  // Add these new state variables near the top of your component
  const [viewedGuides, setViewedGuides] = useState([]);
  const [remainingGuides, setRemainingGuides] = useState(5);
  const MAX_FREE_GUIDES = 5;

  const [referralEmail, setReferralEmail] = useState("");
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);

  // Add these state variables near the top of your component where other state is managed
  const [userRecord, setUserRecord] = useState(null);
  const [userPaidStatus, setUserPaidStatus] = useState("unpaid");
  const [isLoadingUserRecord, setIsLoadingUserRecord] = useState(true);
  const [subscribedAutomations, setSubscribedAutomations] = useState([]);

  // Add these constants near other field definitions (around line 102)
  const INITIAL_CARDS_TO_LOAD = 40;
  const CARDS_PER_BATCH = 20;

  // Guide variables
  const [isGuideVisible, setIsGuideVisible] = useState(false);
  const [isEditingGuideText, setIsEditingGuideText] = useState(false);
  const [editedGuideText, setEditedGuideText] = useState(``);

  const [isEditingRequirements, setIsEditingRequirements] = useState(false);
  const [editedRequirementsText, setEditedRequirementsText] = useState("");

  const [generalAutomationEmails, setGeneralAutomationEmails] = useState([]);

  // Hover animation state for list view - inverted: animation runs by default unless hovering over specific areas
  const [isHoveringSearchArea, setIsHoveringSearchArea] = useState(false);
  const [isHoveringViewToggle, setIsHoveringViewToggle] = useState(false);
  // Animation should run when NOT hovering over these areas AND not manually paused (action buttons hover comes from parent)
  const shouldRunAnimation = viewMode === 'list' && !isHoveringSearchArea && !isHoveringViewToggle && !isHoveringActionButtons && !isAnimationPaused;
  const [animatingCardIndex, setAnimatingCardIndex] = useState(0);
  const animatingCardIndexRef = useRef(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [completedCardIndices, setCompletedCardIndices] = useState(new Set());
  const completedCardIndicesRef = useRef(new Set());
  const cardHeightRef = useRef(null);
  const [measuredCardHeight, setMeasuredCardHeight] = useState(96); // Default fallback

  // Store only IDs - now passed as props

  // --- TEAM SHARED PRIORITIZED LIST ---
  const [companyRecord, setCompanyRecord] = useState(null);


  // Add phone number state
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [phoneMsg, setPhoneMsg] = useState("");

  // Add edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);

  // Placeholder handler for phone call
  const handlePhoneCall = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setPhoneMsg("");
    debugLog("[Bland] Starting phone call submission");

    // Format phone number (ensure it starts with +)
    let formattedPhone = phone.trim();
    if (!formattedPhone.startsWith("+")) {
      formattedPhone = "+1" + formattedPhone.replace(/[^0-9]/g, "");
    }
    debugLog("[Bland] Formatted phone number:", formattedPhone);

    const blandApiKey =
      "org_2bc8933c5047403e0800b9ab43ad22f894655b61edb36eb938c0b1d001f85b577dd0b12f8e441b04e34c69";
    const blandUrl =
      "https://api.bland.ai/api/events/7fdaf1b3-531e-4931-9d75-ac8889172b96/handler";

    // Build the payload
    const body = {
      from: "",
      amd: false,
      analysis_prompt: "",
      analysis_preset: "",
      sensitive_voicemail_detection: false,
      answered_by_enabled: false,
      block_interruptions: false,
      noise_cancellation: false,
      first_sentence:
        "Hi! This is your AI demo call from Upgraded. Want to see what AI can do for your business?",
      interruption_threshold: 50,
      language: "en-US",
      max_duration: 30,
      model: "base",
      pathway_id: "",
      phone_number: formattedPhone,
      record: true,
      summary_prompt: "",
      task: `Upgraded AI Business Coaching
Your name is Sarah, and you’re a business performance and accountability coach from Upgraded AI Business Coaching working with entrepreneurs to double their earnings, focus and effectiveness. Every week you guide your client through a 30-minute “Review → Clarify → Commit” ritual:

Share examples of what’s high leverage and what’s low leverage for each applicable questions and conversations.

Review & Analyze
01 How much of what you did last week lived on your calendar? (on a scale 0% - 100%)
02 What tasks filled the free space in your calendar?
03 What work had the biggest impact or RoI?
04 What did you not need to do?
05 What are you going to change about your approach?

Clarify High Leverage
Important & Non-Urgent - Optimistic and future-oriented projects to strategically advance your biggest opportunities.
Growth Driving - Prioritizing growth to create momentum, resources and further optionality that makes everything else easier.
Courageous - Beyond working hard or even “smart” the best opportunity lies on the other side of bold action.
On versus In - Build the machine instead of being a cog in the machine. What job are you hiring yourself to do?
Lacks Accountability - Spend your precious willpower and focus executing on the next-level ideas no one expects from you.
Alignment - Sidestep all self-sabotage by prioritizing projects that get you what you deeply value.

Commited Task 1
01  Describe the first important project you will work on this week in general terms. 
02  What can you realistically and optimistically accomplish on this in the next 7 days? 
03  How will you know precisely when you have reached your 7 day goal? 
04  What scheduled commitments can you make to focus on this in the next 7days? 
05  Write an implementation intention (e.g. When it’s 9:30 AM on Tuesday the 9th of July, then I will turn off all non-essential notifications, close my office door, and focus on writing the job description for my new key hire.) 
06  What will happen if you follow through on your implementation intention,and who will know? 

Commited Task 2
01  Describe the first important project you will work on this week in general terms. 
02  What can you realistically and optimistically accomplish on this in the next 7 days? 
03  How will you know precisely when you have reached your 7 day goal? 
04  What scheduled commitments can you make to focus on this in the next 7days? 
05  Write an implementation intention (e.g. When it’s 9:30 AM on Tuesday the 9th of July, then I will turn off all non-essential notifications, close my office door, and focus on writing the job description for my new key hire.) 
06  What will happen if you follow through on your implementation intention,and who will know? 

Commited Task 3
01  Describe the first important project you will work on this week in general terms. 
02  What can you realistically and optimistically accomplish on this in the next 7 days? 
03  How will you know precisely when you have reached your 7 day goal? 
04  What scheduled commitments can you make to focus on this in the next 7days? 
05  Write an implementation intention (e.g. When it’s 9:30 AM on Tuesday the 9th of July, then I will turn off all non-essential notifications, close my office door, and focus on writing the job description for my new key hire.) 
06  What will happen if you follow through on your implementation intention,and who will know? 

Begin each session by introducing yourself and explaining that this structured ritual is designed to help them reflect, pinpoint high-impact work, and lock in accountability every week.`,
      temperature: 0.7,
      tools: [],
      transfer_phone_number: formattedPhone,
      voice: "60fec350-03ff-48fa-9f31-c180f37b1a38",
      background_track: "none",
      voicemail_message: "",
      voicemail_action: "hangup",
      wait_for_greeting: false,
      webhook: "",
    };

    debugLog("[Bland] Request body:", body);

    try {
      const response = await fetch(blandUrl, {
        method: "POST",
        headers: {
          authorization: blandApiKey,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      debugLog("[Bland] Raw response:", response);
      const data = await response.json();
      debugLog("[Bland] Response JSON:", data);
      if (response.ok) {
        setPhoneMsg("AI call is on its way! Check your phone.");
      } else {
        setPhoneMsg("There was an error sending the call. Please try again.");
      }
    } catch (err) {
      console.error("[Bland] Error:", err);
      setPhoneMsg("Error sending call. See console for details.");
    } finally {
      setSubmitting(false);
      setPhone("");
    }
  };

  const handlePhoneCallTwo = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setPhoneMsg("");
    debugLog("[Bland] Starting phone call submission");

    // Format phone number (ensure it starts with +)
    let formattedPhone = phone.trim();
    if (!formattedPhone.startsWith("+")) {
      formattedPhone = "+1" + formattedPhone.replace(/[^0-9]/g, "");
    }
    debugLog("[Bland] Formatted phone number:", formattedPhone);

    const headers = {
      authorization:
        "org_2bc8933c5047403e0800b9ab43ad22f894655b61edb36eb938c0b1d001f85b577dd0b12f8e441b04e34c69",
      "Content-Type": "application/json",
    };

    const data = {
      phone_number: formattedPhone,
      task: `Freedom Habits Coaching
  Your name is Sarah, and you're a business performance and accountability coach from Freedom Habits Coaching working with entrepreneurs to double their focus and effectiveness. Every week you guide your client through a 30-minute "Review -> Clarify -> Commit" ritual: ...`, // Use your full prompt here
      voice_id: 0,
      language: "eng",
      record: true,
      reduce_latency: true,
      ivr_mode: false,
      voice: "Alexa",
    };
    debugLog("[Bland] Request body:", data);

    try {
      const response = await fetch("https://api.bland.ai/v1/calls", {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });
      debugLog("[Bland] Raw response:", response);
      const result = await response.json();
      debugLog("[Bland] Response JSON:", result);
      if (response.ok) {
        setPhoneMsg("AI call is on its way! Check your phone.");
      } else {
        setPhoneMsg("There was an error sending the call. Please try again.");
      }
    } catch (err) {
      console.error("[Bland] Error:", err);
      setPhoneMsg("Error sending call. See console for details.");
    } finally {
      setSubmitting(false);
      setPhone("");
    }
  };

  // On mount or company load, fetch prioritized list from company record
  useEffect(() => {
    if (!company?.fields?.Domain) {
      debugLog("[DEBUG] No company domain found, trying user email domain");
      // Try to get domain from user email if no company domain
      if (user?.primaryEmailAddress?.emailAddress) {
        const domain = getDomainFromEmail(user.primaryEmailAddress.emailAddress);
        debugLog("[DEBUG] User email domain:", domain);
        if (domain) {
          // Fetch company record by user's email domain
          (async () => {
            try {
              const record = await fetchCompanyRecordByDomain(domain);
              setCompanyRecord(record);
              if (record) {
                let ids = record?.fields?.PrioritizedAutomationIds;
                if (!ids) ids = [];
                if (typeof ids === "string") {
                  try {
                    ids = JSON.parse(ids);
                  } catch {
                    ids = [];
                  }
                }
                if (setPrioritizedIds) {
                  setPrioritizedIds(Array.isArray(ids) ? ids : []);
                }
                debugLog("[DEBUG] Loaded PrioritizedAutomationIds from user domain:", ids);
              } else {
                debugLog("[DEBUG] No company record found for user domain:", domain);
                if (setPrioritizedIds) {
                  setPrioritizedIds([]);
                }
              }
            } catch (e) {
              console.error("[DEBUG] Error loading from user domain:", e);
              if (setPrioritizedIds) {
                setPrioritizedIds([]);
              }
            }
          })();
        }
      }
      return;
    }
    
    if (debugAutomation) debugLog("[DEBUG] Company domain found:", company.fields.Domain);
    (async () => {
      try {
        const response = await fetch(
          `https://api.airtable.com/v0/${baseId}/companies?filterByFormula={Domain}='${encodeURIComponent(
            company.fields.Domain
          )}'`,
          {
            headers: {
              Authorization: `Bearer ${apiKey}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        const record = data.records[0];
        setCompanyRecord(record);
        let ids = record?.fields?.PrioritizedAutomationIds;
        if (!ids) ids = [];
        if (typeof ids === "string") {
          try {
            ids = JSON.parse(ids);
          } catch {
            ids = [];
          }
        }
        if (setPrioritizedIds) {
          setPrioritizedIds(Array.isArray(ids) ? ids : []);
        }
        if (debugAutomation) debugLog("[DEBUG] Loaded PrioritizedAutomationIds from company:", ids);
      } catch (e) {
        console.error(
          "[DEBUG] Error loading PrioritizedAutomationIds from company:",
          e
        );
      }
    })();
  }, [company?.fields?.Domain, user?.primaryEmailAddress?.emailAddress]);

  // Use the exact field name from Airtable for the prioritized list
  const COMPANY_PRIORITIZED_FIELD = "PrioritizedAutomationIds"; // Update if your field name is different

  async function updateCompanyPrioritizedIds(companyRecordId, prioritizedIds) {
    try {
      const body = {
        fields: {
          [COMPANY_PRIORITIZED_FIELD]: JSON.stringify(prioritizedIds),
        },
      };
      debugLog("[DEBUG] PATCH body:", body);

      const res = await fetch(
        `https://api.airtable.com/v0/${baseId}/companies/${companyRecordId}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${apiKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );
      const data = await res.json();
      debugLog("[DEBUG] Airtable PATCH response:", data);
      if (data.error) {
        alert("Airtable error: " + data.error.message);
      }
    } catch (e) {
      console.error("Error updating company PrioritizedAutomationIds:", e);
      alert("Error updating company PrioritizedAutomationIds: " + e.message);
    }
  }

  // Helper to extract domain from email
  function getDomainFromEmail(email) {
    return email?.split("@")[1]?.toLowerCase();
  }

  // Fetch company record by domain
  const fetchCompanyRecordByDomain = async (domain) => {
    const response = await fetch(
      `https://api.airtable.com/v0/${baseId}/companies?filterByFormula={Domain}='${encodeURIComponent(
        domain
      )}'`,
      {
        headers: {
          Authorization: `Bearer ${apiKey}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    const data = await response.json();
    return data.records[0];
  };

  // Create a new company record
  const createCompanyRecord = async (domain, user) => {
    const response = await fetch(
      `https://api.airtable.com/v0/${baseId}/companies`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${apiKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fields: {
            Domain: domain,
            Name: domain.split('.')[0], // Use domain name as company name
            PrioritizedAutomationIds: JSON.stringify([]),
            // Add any other default fields you want
          },
        }),
      }
    );
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    const data = await response.json();
    debugLog("[DEBUG] Created new company record:", data);
    return data;
  };

  const handleAddToList = async (cardId, position) => {
    let newIds = [...prioritizedIds];
    newIds = newIds.filter((id) => id !== cardId);
    if (position === "end") {
      newIds.push(cardId);
    } else {
      newIds.unshift(cardId);
    }
    if (setPrioritizedIds) {
      setPrioritizedIds(newIds);
    }

    let record = companyRecord;
    if (!record && user?.primaryEmailAddress?.emailAddress) {
      const domain = getDomainFromEmail(user.primaryEmailAddress.emailAddress);
      if (debugAutomation) debugLog("[DEBUG] Looking for company record with domain:", domain);
      record = await fetchCompanyRecordByDomain(domain);
      setCompanyRecord(record);
    }
    
    if (record?.id) {
      await updateCompanyPrioritizedIds(record.id, newIds);
      if (debugAutomation) debugLog("[DEBUG] Added to prioritizedIds (company):", newIds);
    } else {
      // Try to create a company record if none exists
      if (user?.primaryEmailAddress?.emailAddress) {
        const domain = getDomainFromEmail(user.primaryEmailAddress.emailAddress);
        if (debugAutomation) debugLog("[DEBUG] No company record found, creating one for domain:", domain);
        try {
          const newCompanyRecord = await createCompanyRecord(domain, user);
          setCompanyRecord(newCompanyRecord);
          if (newCompanyRecord?.id) {
            await updateCompanyPrioritizedIds(newCompanyRecord.id, newIds);
            debugLog("[DEBUG] Created new company record and added to prioritizedIds:", newIds);
          }
        } catch (error) {
          console.error("[DEBUG] Error creating company record:", error);
          alert("Could not create company record. Please try again.");
        }
      } else {
        alert("No company record found for your domain and unable to create one!");
      }
    }
  };

  const handleRemoveFromList = async (cardId) => {
    const newIds = prioritizedIds.filter((id) => id !== cardId);
    if (setPrioritizedIds) {
      setPrioritizedIds(newIds);
    }

    let record = companyRecord;
    if (!record && user?.primaryEmailAddress?.emailAddress) {
      const domain = getDomainFromEmail(user.primaryEmailAddress.emailAddress);
      record = await fetchCompanyRecordByDomain(domain);
      setCompanyRecord(record);
    }
    if (record?.id) {
      await updateCompanyPrioritizedIds(record.id, newIds);
    } else {
      alert("No company record found for your domain!");
    }
    debugLog("[DEBUG] Removed from prioritizedIds (company):", newIds);
  };

  // Debug logging for My List
  useEffect(() => {
    if (showPrioritizedList) {
      debugLog("[DEBUG] prioritizedIds (company):", prioritizedIds);
      debugLog(
        "[DEBUG] allCards IDs:",
        allCards.map((c) => c.id)
      );
      const filtered = prioritizedIds
        .map((id) => allCards.find((card) => card.id === id))
        .filter(Boolean);
      debugLog("[DEBUG] Filtered cards for My List (company):", filtered);
    }
  }, [showPrioritizedList, prioritizedIds, allCards]);

  useEffect(() => {
    if (!showRequirementsForm) return;
    // Fetch and debug emails for General Automation
    const fetchEmails = async () => {
      try {
        const base = new Airtable({
          apiKey: process.env.REACT_APP_AIR_KEY,
        }).base(process.env.REACT_APP_AIR_BASEID);
        // 1. Get the General Automation category ID
        const categories = await base("Categories").select().all();
        const generalCat = categories.find(
          (cat) => cat.fields.Name === "General Automation"
        );
        if (!generalCat) {
          setGeneralAutomationEmails([
            "(No 'General Automation' category found)",
          ]);
          return;
        }
        const generalCatId = generalCat.id;
        // 2. Get all automation partners
        const partners = await base("automationpartners").select().all();
        // 3. Filter for those subscribed to General Automation
        const emails = partners
          .filter((p) =>
            (p.fields.SubscribedCategories || "")
              .split(",")
              .map((s) => s.trim())
              .includes(generalCatId)
          )
          .map((p) => p.fields.Email)
          .filter(Boolean);
        setGeneralAutomationEmails(emails);
      } catch (err) {
        setGeneralAutomationEmails([`Error: ${err.message}`]);
      }
    };
    fetchEmails();
  }, [showRequirementsForm]);

  useEffect(() => {
    if (isModalOpen && selectedCard) {
      fetchNotes(selectedCard.id);
    }
  }, [isModalOpen, selectedCard]);

  // Add a debug log to verify the initial state
  // useEffect(() => {
  //   // debugLog("Current filters state:", filters);
  // }, [filters]);

  // debugLog("All HeroIcons:", Object.keys(HeroIcons).join(", "));

  // Add this debug function
  // const debugAutomationRecord = (card) => {
  //   debugLog("Automation Record Debug:");
  //   debugLog("Title:", card.title);
  //   debugLog("Customer Focus:", card.customerFocus);
  //   debugLog("Industry:", card.industry);
  //   debugLog("Company Size:", card.companySize);
  //   debugLog("Stage:", card.stage);
  //   debugLog("Full Record:", card);
  // };

  // Unified filtering function
  const filterCards = (cards, options = {}) => {
    const {
      searchQuery: query = '',
      selectedStage = 'all',
      selectedCategory = 'all',
      showPrioritizedList = false,
      prioritizedIds = [],
      user = null
    } = options;

    return cards.filter((card) => {
      // Apply search filter
      if (query) {
        const searchQuery = query.toLowerCase();
        const matchesSearch = 
          card.title?.toLowerCase().includes(searchQuery) ||
          card.description?.toLowerCase().includes(searchQuery) ||
          card.tools?.some((tool) => tool.toLowerCase().includes(searchQuery)) ||
          card.type?.toLowerCase().includes(searchQuery);
        
        if (!matchesSearch) return false;
      }

      // Apply stage filter
      if (selectedStage !== "all") {
        if (selectedStage === "my_list") {
          try {
            const heartedBy =
              typeof card.heartedBy === "string"
                ? JSON.parse(card.heartedBy)
                : card.heartedBy || [];
            if (!user?.id || !heartedBy.includes(user.id)) {
              return false;
            }
          } catch (error) {
            console.error("Error parsing heartedBy:", error);
            return false;
          }
        } else {
          // For prioritization stages (value_menu, value_rank, start_grow), don't filter by stage
          // These are sorting methods, not stage filters
          // Note: ready, coming_soon, requested are now handled by filters.automationStatus
        }
      }

      // Apply category filter
      if (selectedCategory !== "all") {
        const cardCategories = Array.isArray(card.categories)
          ? card.categories
          : [card.category || card.fields?.Categories].flat().filter(Boolean);

        if (
          !cardCategories.some(
            (cat) => cat?.toLowerCase() === selectedCategory.toLowerCase()
          )
        ) {
          return false;
        }
      }

      // Apply prioritized list filter
      if (showPrioritizedList) {
        if (prioritizedIds.length === 0) return false;
        if (!prioritizedIds.includes(card.id)) return false;
      }

      // Apply legacy filters (Customer Focus, Industry, Size, Stage)
      if (filters?.customerFocus?.length > 0) {
        const cardFocus = Array.isArray(card.CustomerFocus)
          ? card.CustomerFocus
          : [card.CustomerFocus];
        if (!cardFocus.some((focus) => filters.customerFocus.includes(focus))) {
          return false;
        }
      }

      if (filters?.industries?.length > 0) {
        const cardIndustries = Array.isArray(card.BestForIndustry)
          ? card.BestForIndustry
          : [card.BestForIndustry];
        if (
          !cardIndustries.some((industry) =>
            filters.industries.includes(industry)
          )
        ) {
          return false;
        }
      }

      if (filters?.sizes?.length > 0) {
        const cardSizes = Array.isArray(card.BestForCompanySize)
          ? card.BestForCompanySize
          : [card.BestForCompanySize];
        if (!cardSizes.some((size) => filters.sizes.includes(size))) {
          return false;
        }
      }

      if (filters?.stages?.length > 0) {
        const cardStages = Array.isArray(card.BestForCompanyStage)
          ? card.BestForCompanyStage
          : [card.BestForCompanyStage];
        if (!cardStages.some((stage) => filters.stages.includes(stage))) {
          return false;
        }
      }

      // Apply automation status filter (ready, coming_soon, requested)
      if (filters?.automationStatus?.length > 0) {
        const cardStage = card.stage;
        if (!filters.automationStatus.includes(cardStage)) {
          return false;
        }
      }

      return true;
    });
  };

  // Modify company loading effect to ensure data loads
  useEffect(() => {
    const loadCompanyData = async () => {
      // Only skip if we've already completed initial load
      if (isInitialLoadComplete) return;

      // Set isInitialLoadComplete to true for non-logged-in users
      if (!isSignedIn) {
        debugLog("User not signed in, proceeding with default data");
        setIsLoadingCompany(false);
        return;
      }

      setIsInitialLoadComplete(true);

      // Don't return early if no email - let's log it
      if (!user?.primaryEmailAddress?.emailAddress) {
        debugLog("Waiting for user email...");
        return;
      }

      setIsLoadingCompany(true);
      try {
        const company = await getCompanyFromEmail(
          user.primaryEmailAddress.emailAddress
        );
        if (debugAutomation) debugLog("Loaded company data:", company);
        if (company) {
          setCompanyData(company);
          setIsInitialLoadComplete(true);
        } else {
          debugLog(
            "No company found for email:",
            user.primaryEmailAddress.emailAddress
          );
        }
      } catch (error) {
        console.error("Error loading company data:", error);
      } finally {
        setIsLoadingCompany(false);
        // Always set isInitialLoadComplete to true after attempting to load company data
        setIsInitialLoadComplete(true);
      }
    };

    loadCompanyData();
  }, [user, isInitialLoadComplete, isSignedIn]); // Add isSignedIn to dependencies

  // Add debug logging to track state changes
  // useEffect(() => {
  //   debugLog("State updated:", {
  //     isLoadingCompany,
  //     hasCompanyData: !!companyData,
  //     isInitialLoadComplete,
  //     userEmail: user?.primaryEmailAddress?.emailAddress,
  //   });
  // }, [isLoadingCompany, companyData, user]);

  const popularAutomations = [
    "Automatically process and categorize incoming emails",
    "Generate meeting summaries and action items using AI",
    "Automate data entry from documents and forms",
    "Schedule social media posts across platforms",
  ];

  // Helper functions for favorite sorting
  const isFavorited = (card) => {
    return card.Favourite === true || card.fields?.Favourite === true;
  };

  // Get heart count for a card
  const getHeartCount = (card) => {
    return Array.isArray(card.HeartedBy)
      ? card.HeartedBy.length
      : typeof card.HeartedBy === "string"
        ? JSON.parse(card.HeartedBy || "[]").length
        : 0;
  };

  // Unified sorting function that handles all sort types
  const sortCards = (cards, sortType = 'favorites_and_popularity') => {
    return [...cards].sort((a, b) => {
      switch (sortType) {
        case 'value_menu':
          // Sort by Monthly Price (lowest first)
          const aPrice = a.MonthlyPrice || a.monthlyPrice || a.pricing?.monthly || 999999;
          const bPrice = b.MonthlyPrice || b.monthlyPrice || b.pricing?.monthly || 999999;
          return aPrice - bPrice;

        case 'value_rank':
          // Sort by calculateNetImpactSimpleValue (highest first)
          if (!companyData) {
            debugLog('[Value Rank Sort] No companyData, returning 0 for comparison');
            return 0; // Can't sort without company data
          }
          const aValue = calculateNetImpactSimpleValue(a, companyData).value;
          const bValue = calculateNetImpactSimpleValue(b, companyData).value;
          
          // Debug first few comparisons
          if (typeof aValue === 'number' && typeof bValue === 'number') {
            // Only log occasionally to avoid spam
            if (Math.random() < 0.01) {
              debugLog('[Value Rank Sort] Sample comparison', {
                aTitle: a.title,
                aValue,
                bTitle: b.title,
                bValue,
                comparison: bValue - aValue
              });
            }
          }
          
          // Handle undefined values - put them at the end
          if (aValue === undefined && bValue === undefined) return 0;
          else if (aValue === undefined) return 1;
          else if (bValue === undefined) return -1;
          
          // Ensure we have valid numbers
          if (typeof aValue !== 'number' || typeof bValue !== 'number') {
            console.warn('[Value Rank Sort] Invalid value types', {
              aTitle: a.title,
              aValue,
              aValueType: typeof aValue,
              bTitle: b.title,
              bValue,
              bValueType: typeof bValue
            });
            return 0;
          }
          
          return bValue - aValue; // Descending order (highest first)

        case 'start_grow':
          // Sort by StartAndGrow field (lowest first)
          const aStartGrow = a.StartAndGrow || 999999;
          const bStartGrow = b.StartAndGrow || 999999;
          return aStartGrow - bStartGrow;

        case 'favorites_and_popularity':
        default:
          // Default sorting: favorites first, then popularity
          const aIsFavorited = isFavorited(a);
          const bIsFavorited = isFavorited(b);

          // If one is favorited and the other isn't, favorited comes first
          if (aIsFavorited && !bIsFavorited) return -1;
          if (!aIsFavorited && bIsFavorited) return 1;

          // If both are favorited or both are not favorited, sort by popularity
          const aHearts = getHeartCount(a);
          const bHearts = getHeartCount(b);
          return bHearts - aHearts;
      }
    });
  };

  // Legacy function for backward compatibility
  const sortCardsByFavoritesAndPopularity = (cards) => {
    return sortCards(cards, 'favorites_and_popularity');
  };

  // Update the useEffect for loading cards
  useEffect(() => {
    const loadCards = async () => {
      if (isLoadingCompany || !companyData || !isInitialLoadComplete) return;

      try {
        const cards = await getAutomationCards();
        debugLog(
          "Initial cards from Airtable:",
          cards.map((card) => ({
            title: card.title,
            hasImage: card.Image ? "yes" : "no",
            imageLength: card.Image?.length,
          }))
        );

        const parsedCards = cards.map((card) => ({
          ...card,
          slug: createSlug(card.title),
          requirements:
            typeof card.requirements === "string"
              ? JSON.parse(card.requirements)
              : card.requirements || {},
          fields: {
            ...card.fields,
            requirements:
              typeof card.fields?.requirements === "string"
                ? JSON.parse(card.fields.requirements)
                : card.fields?.requirements || {},
          },
        }));

        // Sort by favorites first, then popularity (for initial load only)
        const sortedByFavoritesAndPopularity =
          sortCardsByFavoritesAndPopularity(parsedCards);

        if (setAllCards) {
          setAllCards(sortedByFavoritesAndPopularity);
        }
        // Don't set filteredCards here - let the sorting effect handle it
        // This prevents overwriting sorted results when selectedStage is set
        // setFilteredCards(sortedByFavoritesAndPopularity);
        debugLog(
          "Cards after initial load:",
          sortedByFavoritesAndPopularity.map((card) => ({
            title: card.title,
            hasImage: card.Image ? "yes" : "no",
            imageLength: card.Image?.length,
            hearts: Array.isArray(card.HeartedBy)
              ? card.HeartedBy.length
              : typeof card.HeartedBy === "string"
                ? JSON.parse(card.HeartedBy || "[]").length
                : 0,
          }))
        );
      } catch (error) {
        console.error("Error loading cards:", error);
      }
    };
    loadCards();
  }, [isLoadingCompany, companyData]);

  // Add useEffect to initialize displayed cards when filtered cards change
  useEffect(() => {
    if (filteredCards.length > 0) {
      setDisplayedCards(filteredCards.slice(0, INITIAL_CARDS_TO_SHOW));
      setHasMoreCards(filteredCards.length > INITIAL_CARDS_TO_SHOW);
    } else {
      setDisplayedCards([]);
      setHasMoreCards(false);
    }
  }, [filteredCards]);

  // Update displayed cards when showPrioritizedList changes
  useEffect(() => {
    if (showPrioritizedList) {
      // For prioritized list, show all cards at once (no infinite scroll needed)
      const cardsToShow = getPrioritizedCards(prioritizedIds, allCards);
      setDisplayedCards(cardsToShow);
      setHasMoreCards(false);
    } else {
      // For regular view, use filteredCards (which is already sorted correctly by the main sorting effect)
      // Don't call getFilteredCards() here as it would use default sorting and overwrite value_rank sorting
      if (filteredCards.length > 0) {
        setDisplayedCards(filteredCards.slice(0, INITIAL_CARDS_TO_SHOW));
        setHasMoreCards(filteredCards.length > INITIAL_CARDS_TO_SHOW);
      } else {
        setDisplayedCards([]);
        setHasMoreCards(false);
      }
    }
  }, [showPrioritizedList, prioritizedIds, allCards, filteredCards]);

  // Add scroll event listener for infinite scrolling
  useEffect(() => {
    const handleScroll = () => {
      // Don't trigger infinite scroll when showing prioritized list
      if (isLoadingMore || !hasMoreCards || showPrioritizedList) return;

      const scrollPosition = window.innerHeight + window.scrollY;
      const pageHeight = document.documentElement.scrollHeight;
      const scrollThreshold = 0.8; // Load more when user scrolls 80% down the page

      if (scrollPosition >= pageHeight * scrollThreshold) {
        loadMoreCards();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isLoadingMore, hasMoreCards, displayedCards, filteredCards, showPrioritizedList]);

  // Function to load more cards
  const loadMoreCards = () => {
    setIsLoadingMore(true);

    setTimeout(() => {
      const currentLength = displayedCards.length;
      const newCards = filteredCards.slice(
        currentLength,
        currentLength + CARDS_PER_BATCH
      );

      if (newCards.length > 0) {
        setDisplayedCards((prev) => [...prev, ...newCards]);
        setHasMoreCards(currentLength + newCards.length < filteredCards.length);
      } else {
        setHasMoreCards(false);
      }

      setIsLoadingMore(false);
    }, 300); // Small delay to prevent rapid loading
  };

  // Unified filtering and sorting effect
  useEffect(() => {
    if (!isInitialLoadComplete || !allCards.length) {
      if (debugAutomation) debugLog('[Sorting Effect] Skipping - not ready', { 
        isInitialLoadComplete, 
        allCardsLength: allCards.length 
      });
      return;
    }

    // Determine sort type: Focused layout always ranks by estimated value
    let sortType = 'favorites_and_popularity';
    if (isFocusedLayout) sortType = 'value_rank';
    else if (selectedStage === 'value_menu') sortType = 'value_menu';
    else if (selectedStage === 'value_rank') sortType = 'value_rank';
    else if (selectedStage === 'start_grow') sortType = 'start_grow';

    if (debugAutomation) debugLog('[Sorting Effect] Starting', { 
      selectedStage,
      isFocusedLayout,
      sortType, 
      hasCompanyData: !!companyData,
      allCardsCount: allCards.length
    });
    
    const filteredAndSorted = getFilteredAndSortedCards(sortType);
    
    if (debugAutomation) debugLog('[Sorting Effect] Result', {
      resultCount: filteredAndSorted.length,
      first5Cards: filteredAndSorted.slice(0, 5).map(c => ({
        title: c.title,
        value: companyData ? calculateNetImpactSimpleValue(c, companyData).value : 'N/A',
        valueRaw: companyData ? calculateNetImpactSimpleValue(c, companyData) : 'N/A'
      }))
    });
    
    setFilteredCards(filteredAndSorted);
  }, [
    searchQuery,
    selectedStage,
    selectedCategory,
    allCards,
    companyData,
    isInitialLoadComplete,
    user?.id,
    showPrioritizedList,
    prioritizedIds,
    filters, // Add filters to dependencies so sorting re-runs when filters change
    isFocusedLayout,
  ]);

  // NOTE: Filter changes are now handled by the main sorting effect above (line 1419)
  // This ensures filters and sorting are applied together consistently
  // Removed redundant filter-only effect that was overwriting sorted results

  // Add this useEffect to handle initial load
  useEffect(() => {
    if (allCards?.length === 0 || !urlParams) return;
    const automationSlug = urlParams.get("automation");
    if (automationSlug) {
      const matchingCard = allCards.find(
        (card) => card.slug === automationSlug
      );
      debugLog("Matching card:", matchingCard);
      if (matchingCard) {
        setSelectedCard(matchingCard);
        setIsModalOpen(true);
      }
    }
  }, [allCards, urlParams]);

  // Reset animation function - clears completed cards and resets index
  const resetAnimation = useCallback(() => {
    setCompletedCardIndices(new Set());
    completedCardIndicesRef.current = new Set();
    setAnimatingCardIndex(0);
    animatingCardIndexRef.current = 0;
    setIsAnimating(false);
  }, []);

  // Expose reset function via ref if provided
  useEffect(() => {
    if (resetAnimationRef) {
      resetAnimationRef.current = resetAnimation;
    }
  }, [resetAnimationRef, resetAnimation]);

  // Keep refs in sync with state
  useEffect(() => {
    completedCardIndicesRef.current = completedCardIndices;
  }, [completedCardIndices]);
  
  useEffect(() => {
    animatingCardIndexRef.current = animatingCardIndex;
  }, [animatingCardIndex]);

  // Hover animation effect for list view - runs by default unless hovering over specific areas
  useEffect(() => {
    if (!shouldRunAnimation) {
      // Reset when hovering over areas that should stop animation
      setIsAnimating(false);
      return;
    }
    
    const cardsToAnimate = searchQuery.trim() 
      ? [...exactResults, ...fuzzyResults]
      : (showPrioritizedList 
          ? getPrioritizedCards(prioritizedIds, allCards)
          : displayedCards);
    
    if (cardsToAnimate.length === 0) return;

    // Reset index when starting a new hover session
    setAnimatingCardIndex(0);
    animatingCardIndexRef.current = 0;

    let intervalId;
    let timeoutId;

    const startAnimation = () => {
      const currentIndex = animatingCardIndexRef.current;
      const completed = completedCardIndicesRef.current;
      
      // Skip if current card is already completed
      if (completed.has(currentIndex)) {
        // Find next non-completed card
        let nextIndex = (currentIndex + 1) % cardsToAnimate.length;
        let attempts = 0;
        while (completed.has(nextIndex) && attempts < cardsToAnimate.length) {
          nextIndex = (nextIndex + 1) % cardsToAnimate.length;
          attempts++;
        }
        if (attempts >= cardsToAnimate.length) {
          // All cards completed, reset
          setCompletedCardIndices(new Set());
          completedCardIndicesRef.current = new Set();
          setAnimatingCardIndex(0);
          animatingCardIndexRef.current = 0;
          return;
        }
        setAnimatingCardIndex(nextIndex);
        animatingCardIndexRef.current = nextIndex;
        return;
      }
      
      setIsAnimating(true);
      
      // After animation completes, mark card as completed and move to next card
      timeoutId = setTimeout(() => {
        setCompletedCardIndices((prev) => {
          const newSet = new Set(prev);
          newSet.add(currentIndex);
          completedCardIndicesRef.current = newSet;
          return newSet;
        });
        setAnimatingCardIndex((prev) => {
          // Find next non-completed card
          const newCompleted = new Set(completedCardIndicesRef.current);
          let nextIndex = (prev + 1) % cardsToAnimate.length;
          let attempts = 0;
          while (newCompleted.has(nextIndex) && attempts < cardsToAnimate.length) {
            nextIndex = (nextIndex + 1) % cardsToAnimate.length;
            attempts++;
          }
          setIsAnimating(false);
          const finalIndex = attempts >= cardsToAnimate.length ? 0 : nextIndex;
          animatingCardIndexRef.current = finalIndex;
          return finalIndex;
        });
      }, 1600); // Match animation duration (1.6 seconds)
    };

    // Start first animation immediately
    startAnimation();

    // Then set up interval for subsequent animations
    intervalId = setInterval(() => {
      startAnimation();
    }, 2000); // Cycle every 2 seconds (slight gap after animation completes)

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
      setIsAnimating(false);
    };
  }, [shouldRunAnimation, displayedCards, exactResults, fuzzyResults, searchQuery, showPrioritizedList, prioritizedIds, allCards]);

  // Measure card height for accurate animation
  useEffect(() => {
    if (cardHeightRef.current && viewMode === 'list') {
      const cardElement = cardHeightRef.current;
      const cardRect = cardElement.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(cardElement);
      const marginBottom = parseFloat(computedStyle.marginBottom) || 8; // space-y-2 = 8px
      const totalHeight = cardRect.height + marginBottom;
      setMeasuredCardHeight(Math.round(totalHeight));
    }
  }, [viewMode, displayedCards, exactResults, fuzzyResults, cardHeightRef.current]);

  // Add this useEffect to monitor selectedCard changes
  // useEffect(() => {
  //   debugLog("Selected card data:", {
  //     id: selectedCard?.id,
  //     title: selectedCard?.title,
  //     fields: selectedCard?.fields,
  //     // Log the specific fields we're trying to access
  //     bestFor: selectedCard?.fields?.BestFor,
  //     notGoodFor: selectedCard?.fields?.NotGoodFor,
  //   });
  // }, [selectedCard]);

  // Add this function before the component definition
  const calculateAutomationValue = (automationCard, companyData) => {
    // Early return with detailed logging if missing data
    if (!automationCard?.formula || !companyData?.fields) {
      debugLog("Missing required data for calculation:", {
        hasFormula: !!automationCard?.formula,
        hasVariables: !!automationCard?.variables,
        hasCompanyFields: !!companyData?.fields,
        automationTitle: automationCard?.title,
      });
      return { value: undefined, finalFormula: "" };
    }

    try {
      // Parse variables once
      // const variables = automationCard.variables.split(',').map(item => item.trim()).filter(Boolean);

      // Extract variables by matching word characters (letters, numbers, underscores)
      const variables = [
        ...new Set(automationCard.formula.match(/[a-zA-Z_]\w*/g) || []),
      ].filter(
        // Filter out common math functions that might be used in formulas
        (word) =>
          !["Math", "abs", "ceil", "floor", "round", "min", "max"].includes(
            word
          )
      );

      // Create a copy of the formula for manipulation
      let calculatedFormula = automationCard.formula;

      // Debug state before replacement
      const debugState = {
        originalFormula: calculatedFormula,
        variables: variables,
        availableFields: Object.keys(companyData.fields),
        values: {},
      };

      // Replace all variables
      variables.forEach((variable) => {
        const value = companyData.fields[variable];
        debugState.values[variable] = value;

        if (value === undefined || value === null) {
          console.warn(
            `Missing value for variable ${variable} in company data`
          );
          // Instead of throwing an error, use a default value of 0
          calculatedFormula = calculatedFormula.replace(
            new RegExp(variable, "g"),
            "0"
          );
          return;
        }

        calculatedFormula = calculatedFormula.replace(
          new RegExp(variable, "g"),
          value
        );
      });

      // Log complete debug state
      // debugLog("Calculation debug:", {
      //   ...debugState,
      //   finalFormula: calculatedFormula,
      // });

      // Evaluate and return
      const result = evaluate(calculatedFormula);

      // Validate result
      if (isNaN(result) || !isFinite(result)) {
        console.warn("Calculation resulted in invalid number, returning 0");
        return { value: 0, finalFormula: calculatedFormula };
      }

      return { value: result, finalFormula: calculatedFormula };
    } catch (error) {
      console.error("Error calculating automation value:", {
        error: error.message,
        automation: automationCard?.title || "Unknown",
        formula: automationCard?.formula || "No formula",
        variables: automationCard?.variables || "No variables",
      });
      return { value: 0, finalFormula: "" };
    }
  };

  /**
   * Soft realism for formula outputs without editing each Airtable formula.
   * Caps relative to company size, then log-compresses overflow so outliers still rank
   * differently instead of all stacking at the same ceiling (e.g. former $1M hard max).
   */
  const dampenAutomationValue = (raw, companyFields) => {
    if (typeof raw !== "number" || !Number.isFinite(raw)) return 0;
    const positive = Math.max(raw, 0);

    const revenue = Number(companyFields?.AnnualRevenue);
    const employees = Number(companyFields?.Employees);

    let softMax = Infinity;
    if (Number.isFinite(revenue) && revenue > 0) {
      softMax = Math.min(softMax, revenue * 0.25);
    }
    if (Number.isFinite(employees) && employees > 0) {
      softMax = Math.min(softMax, Math.max(employees * 22000, 25000));
    }
    if (!Number.isFinite(softMax)) {
      softMax = 500000;
    }
    softMax = Math.max(softMax, 10000);

    if (positive <= softMax) return positive;
    return Math.round(softMax * (1 + Math.log10(positive / softMax)));
  };

  /** Always a finite number for display/sort — never NaN/undefined. */
  const safeImpactAmount = (value, { min = 0, fallback = 1000 } = {}) => {
    const n = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(n)) return fallback;
    return Math.max(n, min);
  };

  const calculateNetImpactSimpleValue = (automationCard, companyData) => {
    if (!companyData) {
      return { value: 0, message: "Sign in to see value" };
    }

    // Early return with detailed logging if missing data
    if (!companyData?.fields) {
      debugLog("Missing company data for calculation:", {
        hasCompanyFields: !!companyData?.fields,
        automationTitle: automationCard?.title,
      });
      return { value: 0, finalFormula: "" };
    }

    // Get formula from either NetImpactSimple or fallback to formula field
    let rawFormula =
      automationCard?.NetImpactSimple?.value || automationCard?.formula;

    // If no formula found, return early
    if (!rawFormula) {
      debugLog("No formula found:", {
        hasNetImpactSimple: !!automationCard?.NetImpactSimple,
        hasFormula: !!automationCard?.formula,
        automationTitle: automationCard?.title,
      });
      return { value: 0, finalFormula: "" };
    }

    // Strip away "Annual Net Impact = " or similar prefixes
    rawFormula = rawFormula.replace(/^.*?=\s*/, "");

    debugLog("Processed formula:", rawFormula); // Debug log

    try {
      // Extract variables by matching word characters (letters, numbers, underscores)
      const variables = [
        ...new Set(rawFormula.match(/[a-zA-Z_]\w*/g) || []),
      ].filter(
        // Filter out common math functions that might be used in formulas
        (word) =>
          !["Math", "abs", "ceil", "floor", "round", "min", "max"].includes(
            word
          )
      );

      // Create a copy of the formula for manipulation
      let calculatedFormula = rawFormula;

      // Handle SetupPrice and MonthlyPrice with defaults
      const setupPrice = automationCard?.SetupPrice ?? 500;
      const monthlyPrice = automationCard?.MonthlyPrice ?? 500;

      // Debug state before replacement
      const debugState = {
        originalFormula: rawFormula,
        strippedFormula: calculatedFormula,
        variables: variables,
        availableFields: Object.keys(companyData.fields),
        values: {
          SetupPrice: setupPrice,
          MonthlyPrice: monthlyPrice,
        },
      };

      // Replace all variables
      variables.forEach((variable) => {
        let value;

        // Check if the variable is SetupPrice or MonthlyPrice
        if (variable === "SetupPrice") {
          value = setupPrice;
        } else if (variable === "MonthlyPrice") {
          value = monthlyPrice;
        } else {
          value = companyData.fields[variable];
        }

        debugState.values[variable] = value;

        if (value === undefined || value === null) {
          console.warn(
            `Missing value for variable ${variable} in company data`
          );
          throw new Error(`Missing required variable: ${variable}`);
        }

        // Use word boundaries in regex to avoid partial word matches
        calculatedFormula = calculatedFormula.replace(
          new RegExp(`\\b${variable}\\b`, "g"),
          value
        );
      });

      // Log complete debug state
      debugLog("Calculation debug:", {
        ...debugState,
        finalFormula: calculatedFormula,
      });

      // Evaluate and return
      const result = evaluate(calculatedFormula);

      // Validate result
      if (isNaN(result) || !isFinite(result)) {
        throw new Error("Calculation resulted in invalid number");
      }

      const dampened = dampenAutomationValue(result, companyData.fields);

      return {
        value: Number.isFinite(dampened) ? dampened : 0,
        rawValue: result,
        finalFormula: calculatedFormula,
        processedFormula: rawFormula,
      };
    } catch (error) {
      console.error("Error calculating automation value:", {
        error: error.message,
        automation: automationCard.title,
        rawFormula: rawFormula,
        variables: automationCard?.variables,
      });
      return { value: 0, finalFormula: "" };
    }
  };

  // Add comparison function
  const compareAutomationsByValue = (cardA, cardB, companyData) => {
    // let valueA = calculateAutomationValue(cardA, companyData).value;
    // let valueB = calculateAutomationValue(cardB, companyData).value;

    let valueA = calculateNetImpactSimpleValue(cardA, companyData).value;
    let valueB = calculateNetImpactSimpleValue(cardB, companyData).value;

    if (valueA === undefined && valueB === undefined) return 0;
    else if (valueA === undefined) return 1;
    else if (valueB === undefined) return -1;

    return valueB - valueA;
  };

  const HeartButton = memo(
    ({ card, isHearted, onHeart, onAddToList, onRemoveFromList }) => {
      const [localHeartCount, setLocalHeartCount] = useState(0);
      const [heartedUserIds, setHeartedUserIds] = useState([]);
      const [isLoading, setIsLoading] = useState(false);
      const [showPopover, setShowPopover] = useState(false);
      const popoverTimeout = useRef();

      useEffect(() => {
        if (!card.heartedBy) return;
        try {
          const heartedBy =
            typeof card.heartedBy === "string"
              ? JSON.parse(card.heartedBy)
              : card.heartedBy || [];
          setHeartedUserIds(Array.isArray(heartedBy) ? heartedBy : []);
          setLocalHeartCount(Array.isArray(heartedBy) ? heartedBy.length : 0);
        } catch (e) {
          console.error("Error parsing heartedBy:", e);
        }
      }, [card.heartedBy]);

      useEffect(() => {
        return () => clearTimeout(popoverTimeout.current);
      }, []);

      const handlePopoverEnter = () => {
        clearTimeout(popoverTimeout.current);
        setShowPopover(true);
      };

      const handlePopoverLeave = () => {
        popoverTimeout.current = setTimeout(() => setShowPopover(false), 200);
      };

      const handleHeartClick = (e) => {
        e.stopPropagation();
        if (!isLoading) {
          onHeart(card);
        }
      };

      const handlePopoverClick = (e) => {
        e.stopPropagation();
      };

      return (
        <div className="absolute top-4 right-4 flex items-center gap-3 heart-button">
          <UserAvatarList userIds={heartedUserIds} maxDisplay={3} />
          <div className="flex items-center gap-1">
            <span
              className={`text-sm ${
                isHearted ? "text-red-500" : "text-gray-400"
              }`}
            >
              {localHeartCount}
            </span>
            <div className="relative">
              <button
                onClick={handleHeartClick}
                onMouseEnter={handlePopoverEnter}
                onMouseLeave={handlePopoverLeave}
                disabled={isLoading}
                className={`p-2 rounded-full transition-colors duration-200 ${
                  isHearted
                    ? "text-red-500 bg-red-50 hover:bg-red-100"
                    : "text-gray-400 bg-white hover:bg-gray-50"
                }`}
              >
                <HeartIcon 
                  className={`w-5 h-5 ${
                    isHearted 
                      ? "fill-red-500 text-red-500" 
                      : "fill-none text-gray-400"
                  }`} 
                />
              </button>
              {showPopover && (
                <div
                  className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
                  onMouseEnter={handlePopoverEnter}
                  onMouseLeave={handlePopoverLeave}
                  onClick={handlePopoverClick}
                >
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    <button
                      onClick={() => {
                        onAddToList(card.id, "start");
                        setShowPopover(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100"
                      role="menuitem"
                    >
                      <div className="flex items-center gap-2 text-gray-900 font-medium">
                        <PlusCircleIcon className="w-4 h-4" />
                        Add to Queue Start
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        onAddToList(card.id, "end");
                        setShowPopover(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100"
                      role="menuitem"
                    >
                      <div className="flex items-center gap-2 text-gray-900 font-medium">
                        <PlusCircleIcon className="w-4 h-4" />
                        Add to Queue End
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        onRemoveFromList(card.id);
                        setShowPopover(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-100"
                      role="menuitem"
                    >
                      <div className="flex items-center gap-2 text-red-600 font-medium">
                        <TrashIcon className="w-4 h-4" />
                        Remove from list
                      </div>
                    </button>
                    <div className="px-4 py-2 text-sm text-gray-900 font-medium border-t border-gray-100">
                      Move to position:
                    </div>
                    <div className="grid grid-cols-3 gap-1 px-4 py-2">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((pos) => (
                        <button
                          key={pos}
                          onClick={() => {
                            handleMoveToPosition(card.id, pos);
                            setShowPopover(false);
                          }}
                          className="px-2 py-1 text-sm text-gray-900 font-medium hover:bg-gray-100 rounded"
                        >
                          {pos}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
  );

  HeartButton.displayName = "HeartButton";

  // Function to generate icons from Tools field — always at least two for visual balance
  const generateIconsFromTools = (toolsString) => {
    if (!toolsString) return [...DEFAULT_FALLBACK_ICONS];
    let tools = [];
    if (Array.isArray(toolsString)) {
      tools = toolsString;
    } else {
      const str = String(toolsString).trim();
      if (!str) return [...DEFAULT_FALLBACK_ICONS];
      // Plain comma-separated text (e.g. "Webflow, Shopify") - don't JSON.parse
      if (str[0] !== '[' && str[0] !== '{') {
        tools = str.split(',').map(tool => tool.trim()).filter(Boolean);
      } else {
        try {
          const parsed = JSON.parse(str);
          tools = Array.isArray(parsed) ? parsed : [str];
        } catch {
          tools = str.split(',').map(tool => tool.trim()).filter(Boolean);
        }
      }
    }
    const icons = tools.map(tool => {
      const normalizedTool = String(tool).toLowerCase().replace(/\s+/g, '');
      return iconMap[normalizedTool] || `https://img.logo.dev/${normalizedTool}.com?token=pk_acnHOju_S3KawCcUE6Ek2Q`;
    });
    // Pad to at least two distinct-looking slots
    while (icons.length < 2) {
      const fallback = DEFAULT_FALLBACK_ICONS[icons.length] || iconMap.upgraded;
      if (!icons.includes(fallback)) icons.push(fallback);
      else icons.push(iconMap.upgraded);
    }
    return icons;
  };

  // Edit Automation Modal Component
  const EditAutomationModal = ({ isOpen, onClose, automation, onSave }) => {
    const [formData, setFormData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentImage, setCurrentImage] = useState(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [isRemovingImage, setIsRemovingImage] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
      if (automation) {
        // Initialize form data with automation fields
        const initialData = {
          Title: automation.Title || automation.title || '',
          Type: automation.Type || automation.type || 'automation',
          Stage: automation.Stage || automation.stage || 'coming_soon',
          Tools: typeof automation.Tools === 'string' ? automation.Tools : '',
          Benefits: typeof automation.Benefits === 'string' ? automation.Benefits : '',
          IncludedFeatures: typeof automation.IncludedFeatures === 'string' ? automation.IncludedFeatures : '',
          SetupPrice: automation.SetupPrice || automation.setupPrice || 0,
          MonthlyPrice: automation.MonthlyPrice || automation.monthlyPrice || 0,
          TurnaroundDays: automation.TurnaroundDays || automation.turnaroundDays || 3,
          StripeLink: automation.StripeLink || automation.stripeLink || '',
          ExampleOutputs: typeof automation.ExampleOutputs === 'string' ? automation.ExampleOutputs : '',
          // Read from "Content" fields if they exist, otherwise fall back to original AI-generated fields
          BestFor: automation.BestForContent || automation.BestFor || '',
          NotGoodFor: automation.NotGoodForContent || automation.NotGoodFor || '',
          WorkflowDetails: automation.WorkflowDetailsContent || automation.WorkflowDetails || automation.workflowDetails || '',
          AutomationRecipes: automation.AutomationRecipesContent || automation.AutomationRecipes || '',
          Deliverables: automation.DeliverablesContent || automation.Deliverables || '',
          SupportDetails: automation.SupportDetailsContent || automation.SupportDetails || automation.supportDetails || '',
          Integrations: automation.IntegrationsContent || automation.Integrations || '',
          ComplianceItems: automation.ComplianceItemsContent || automation.ComplianceItems || '',
          // Add more fields as needed
        };
        setFormData(initialData);
        
        // Initialize current image from automation Image field
        if (automation.Image && Array.isArray(automation.Image) && automation.Image.length > 0) {
          setCurrentImage(automation.Image[0]);
        } else {
          setCurrentImage(null);
        }
      }
    }, [automation]);

    const handleInputChange = (field, value) => {
      setFormData(prev => ({ ...prev, [field]: value }));
      // Clear error for this field
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: null }));
      }
    };

    const convertFileToBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = error => reject(error);
      });
    };

    const handleImageUpload = async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors({ image: 'Please select an image file.' });
        return;
      }

      // Validate file size (e.g., max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setErrors({ image: 'Image size must be less than 10MB.' });
        return;
      }

      setIsUploadingImage(true);
      setErrors({ image: null });

      try {
        // Convert file to base64
        const base64String = await convertFileToBase64(file);
        
        // Determine content type
        const contentType = file.type || 'image/jpeg';
        const filename = file.name || 'image.jpg';

        // Upload attachment to Airtable
        const uploadUrl = `https://content.airtable.com/v0/${baseId}/${automation.id}/Image/uploadAttachment`;
        
        const uploadResponse = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contentType: contentType,
            file: base64String,
            filename: filename,
          }),
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json().catch(() => ({}));
          throw new Error(errorData.error?.message || `Upload failed: ${uploadResponse.statusText}`);
        }

        const uploadData = await uploadResponse.json();
        debugLog('Upload response:', uploadData);
        
        // The upload endpoint returns a full Airtable record with the attachment in fields
        // The structure is: { id, createdTime, fields: { fieldId: [attachment, ...] } }
        // We need to find the Image field (could be by name "Image" or by field ID)
        let attachment = null;
        
        if (uploadData.fields) {
          // Try to find the Image field by name first
          if (uploadData.fields.Image && Array.isArray(uploadData.fields.Image) && uploadData.fields.Image.length > 0) {
            const uploadedAttachment = uploadData.fields.Image[0];
            attachment = {
              url: uploadedAttachment.url,
              filename: uploadedAttachment.filename || filename,
            };
          } else {
            // If not found by name, look for it in any field that has attachments
            // (The field ID might be dynamic like "fldsUYyCvuinNaWNV")
            for (const fieldValue of Object.values(uploadData.fields)) {
              if (Array.isArray(fieldValue) && fieldValue.length > 0 && fieldValue[0].url) {
                // Found an attachment field - use the first attachment
                const uploadedAttachment = fieldValue[0];
                attachment = {
                  url: uploadedAttachment.url,
                  filename: uploadedAttachment.filename || filename,
                };
                break;
              }
            }
          }
        }
        
        // Validate that we found an attachment
        if (!attachment || !attachment.url) {
          console.error('Upload response missing attachment URL:', uploadData);
          throw new Error('Upload failed: Could not extract attachment from upload response');
        }
        
        // The upload endpoint already updates the record, so we don't need to update again
        // However, we'll still update local state and call onSave to refresh the UI

        // Update local state
        setCurrentImage(attachment);
        
        // Call onSave to refresh data
        if (onSave) {
          onSave();
        }

        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        setErrors({ image: error.message || 'Failed to upload image. Please try again.' });
      } finally {
        setIsUploadingImage(false);
      }
    };

    const handleImageRemove = async () => {
      if (!currentImage) return;

      setIsRemovingImage(true);
      setErrors({ image: null });

      try {
        // Update Airtable record to remove the attachment (set to empty array)
        await base('automationsAIGrowthUpgrades').update([
          {
            id: automation.id,
            fields: {
              Image: []
            }
          }
        ]);

        // Update local state
        setCurrentImage(null);
        
        // Call onSave to refresh data
        if (onSave) {
          onSave();
        }
      } catch (error) {
        console.error('Error removing image:', error);
        setErrors({ image: error.message || 'Failed to remove image. Please try again.' });
      } finally {
        setIsRemovingImage(false);
      }
    };

    const handleSave = async () => {
      setIsLoading(true);
      setErrors({});

      try {
        // Prepare data for Airtable
        const airtableData = {
          Title: formData.Title,
          Type: formData.Type,
          Stage: formData.Stage,
          // Tools is a string field, so save as comma-separated string
          Tools: formData.Tools || '',
          // Benefits is a string field, so save as string
          Benefits: formData.Benefits || '',
          // IncludedFeatures is a string field, so save as string
          IncludedFeatures: formData.IncludedFeatures || '',
          SetupPrice: Number(formData.SetupPrice) || 0,
          MonthlyPrice: Number(formData.MonthlyPrice) || 0,
          TurnaroundDays: Number(formData.TurnaroundDays) || 3,
          StripeLink: formData.StripeLink,
          // ExampleOutputs is a string field, so save as string
          ExampleOutputs: formData.ExampleOutputs || '',
          // Save to new "Content" fields since original fields are AI-generated and read-only
          BestForContent: formData.BestFor.value || '',
          NotGoodFor: formData.NotGoodFor || '',
          WorkflowDetails: formData.WorkflowDetails || '',
          AutomationRecipes: formData.AutomationRecipes || '',
          Deliverables: formData.Deliverables || '',
          SupportDetails: formData.SupportDetails || '',
          Integrations: formData.Integrations  || '',
          ComplianceItems: formData.ComplianceItems || '',
        };

        // Update Airtable
        await base('automationsAIGrowthUpgrades').update([
          {
            id: automation.id,
            fields: airtableData
          }
        ]);

        // Call the onSave callback to refresh the data
        if (onSave) {
          onSave();
        }

        onClose();
      } catch (error) {
        console.error('Error saving automation:', error);
        setErrors({ general: 'Failed to save automation. Please try again.' });
      } finally {
        setIsLoading(false);
      }
    };

    if (!isOpen || !automation) return null;

    return (
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                  <div className="absolute right-0 top-0 pr-4 pt-4">
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900"
                    >
                      Edit Automation: {automation.title || automation.Title}
                    </Dialog.Title>

                    <div className="mt-6 space-y-6 text-left max-h-96 overflow-y-auto">
                      {/* Basic Information */}
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Title *
                          </label>
                          <input
                            type="text"
                            value={formData.Title || ''}
                            onChange={(e) => handleInputChange('Title', e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Type
                          </label>
                          <select
                            value={formData.Type || 'automation'}
                            onChange={(e) => handleInputChange('Type', e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          >
                            <option value="automation">Automation</option>
                            <option value="template">Template</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Stage
                          </label>
                          <select
                            value={formData.Stage || 'coming_soon'}
                            onChange={(e) => handleInputChange('Stage', e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          >
                            <option value="ready">Ready</option>
                            <option value="coming_soon">Coming Soon</option>
                            <option value="requested">Requested</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Turnaround Days
                          </label>
                          <input
                            type="number"
                            value={formData.TurnaroundDays || 3}
                            onChange={(e) => handleInputChange('TurnaroundDays', e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>
                      </div>

                      {/* Pricing */}
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Setup Price ($)
                          </label>
                          <input
                            type="number"
                            value={formData.SetupPrice || 0}
                            onChange={(e) => handleInputChange('SetupPrice', e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Monthly Price ($)
                          </label>
                          <input
                            type="number"
                            value={formData.MonthlyPrice || 0}
                            onChange={(e) => handleInputChange('MonthlyPrice', e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Stripe Link
                          </label>
                          <input
                            type="url"
                            value={formData.StripeLink || ''}
                            onChange={(e) => handleInputChange('StripeLink', e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>
                      </div>

                      {/* Content Fields */}
                      <div className="space-y-6">
                        {/* Image Upload Section */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Image Attachment
                          </label>
                          
                          {/* Current Image Preview */}
                          {currentImage && (
                            <div className="mb-3">
                              <div className="relative inline-block">
                                <img
                                  src={currentImage.url}
                                  alt={currentImage.filename || 'Automation image'}
                                  className="h-32 w-auto rounded-md border border-gray-300 object-cover"
                                />
                                <button
                                  type="button"
                                  onClick={handleImageRemove}
                                  disabled={isRemovingImage || isUploadingImage}
                                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Remove image"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                              {currentImage.filename && (
                                <p className="text-xs text-gray-500 mt-1">{currentImage.filename}</p>
                              )}
                            </div>
                          )}

                          {/* Upload Button */}
                          <div>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleImageUpload}
                              disabled={isUploadingImage || isRemovingImage}
                              className="hidden"
                              id="image-upload-input"
                            />
                            <label
                              htmlFor="image-upload-input"
                              className={`inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer ${
                                isUploadingImage || isRemovingImage ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                            >
                              <PhotoIcon className="h-5 w-5" />
                              {isUploadingImage ? 'Uploading...' : currentImage ? 'Replace Image' : 'Upload Image'}
                            </label>
                          </div>

                          {/* Error Message */}
                          {errors.image && (
                            <p className="mt-2 text-sm text-red-600">{errors.image}</p>
                          )}

                          {/* Help Text */}
                          <p className="mt-1 text-xs text-gray-500">
                            Supported formats: JPEG, PNG, GIF. Max size: 10MB.
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Tools (comma-separated)
                          </label>
                          <input
                            type="text"
                            value={formData.Tools || ''}
                            onChange={(e) => handleInputChange('Tools', e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Mixpanel, Sheets, Zapier"
                          />
                          {/* Show preview of generated icons */}
                          {formData.Tools && (
                            <div className="mt-2 flex items-center gap-2">
                              <span className="text-xs text-gray-500">Preview icons:</span>
                              {generateIconsFromTools(formData.Tools).map((icon, i) => (
                                <img
                                  key={i}
                                  src={icon}
                                  alt=""
                                  className="h-4 w-4 rounded"
                                />
                              ))}
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Benefits
                          </label>
                          <textarea
                            value={formData.Benefits || ''}
                            onChange={(e) => handleInputChange('Benefits', e.target.value)}
                            rows={4}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Enter benefits as plain text..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Included Features
                          </label>
                          <textarea
                            value={formData.IncludedFeatures || ''}
                            onChange={(e) => handleInputChange('IncludedFeatures', e.target.value)}
                            rows={4}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Enter included features as plain text..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Example Outputs
                          </label>
                          <textarea
                            value={formData.ExampleOutputs || ''}
                            onChange={(e) => handleInputChange('ExampleOutputs', e.target.value)}
                            rows={3}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Enter example outputs as plain text..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Best For (Editable Content)
                          </label>
                          <textarea
                            value={formData.BestFor || ''}
                            onChange={(e) => handleInputChange('BestFor', e.target.value)}
                            rows={3}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Enter best use cases as plain text..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Not Good For (Editable Content)
                          </label>
                          <textarea
                            value={formData.NotGoodFor || ''}
                            onChange={(e) => handleInputChange('NotGoodFor', e.target.value)}
                            rows={3}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Enter limitations as plain text..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Workflow Details (Editable Content)
                          </label>
                          <textarea
                            value={formData.WorkflowDetails || ''}
                            onChange={(e) => handleInputChange('WorkflowDetails', e.target.value)}
                            rows={4}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Enter workflow details as plain text..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Automation Recipes (Editable Content)
                          </label>
                          <textarea
                            value={formData.AutomationRecipes || ''}
                            onChange={(e) => handleInputChange('AutomationRecipes', e.target.value)}
                            rows={3}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Enter automation recipes as plain text..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Deliverables (Editable Content)
                          </label>
                          <textarea
                            value={formData.Deliverables || ''}
                            onChange={(e) => handleInputChange('Deliverables', e.target.value)}
                            rows={3}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Enter deliverables as plain text..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Support Details (Editable Content)
                          </label>
                          <textarea
                            value={formData.SupportDetails || ''}
                            onChange={(e) => handleInputChange('SupportDetails', e.target.value)}
                            rows={3}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Enter support details as plain text..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Integrations (Editable Content)
                          </label>
                          <textarea
                            value={formData.Integrations || ''}
                            onChange={(e) => handleInputChange('Integrations', e.target.value)}
                            rows={3}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Enter integrations as plain text..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Compliance Items (Editable Content)
                          </label>
                          <textarea
                            value={formData.ComplianceItems || ''}
                            onChange={(e) => handleInputChange('ComplianceItems', e.target.value)}
                            rows={3}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Enter compliance items as plain text..."
                          />
                        </div>
                      </div>

                      {errors.general && (
                        <div className="text-red-600 text-sm">{errors.general}</div>
                      )}
                    </div>

                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={isLoading}
                        className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2 disabled:opacity-50"
                      >
                        {isLoading ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                        onClick={onClose}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    );
  };

  const buySteps = [
    {
      id: "01",
      name: "Review Details",
      description: "Review benefits",
      href: "#",
      status: "complete",
    },
    {
      id: "02",
      name: "Buy Now",
      description: "Input info & buy",
      href: "#",
      status: "current",
    },
    {
      id: "03",
      name: "Requirements & QA",
      description: "Watch it run",
      href: "#",
      status: "upcoming",
    },
    {
      id: "04",
      name: "Watch it Run",
      description: "Watch it run",
      href: "#",
      status: "upcoming",
    },
  ];

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  // Add this console log to debug our cards
  // useEffect(() => {
  //   debugLog(
  //     "All cards:",
  //     allCards.map((card) => ({
  //       title: card.title,
  //       stage: card.stage,
  //     }))
  //   );
  // }, [allCards]);

  // useEffect(() => {
  //   debugLog("All cards:", allCards);
  // }, [allCards]);

  // TODO: Popular includes only the cards with an Image guide
  // TODO: Find the other popular function(s) to modify to only include cards with an Image guide
  // This useEffect is now handled by the unified filtering and sorting effect above

  // Add this useEffect to initialize Fuse when cards load
  useEffect(() => {
    if (allCards.length) {
      // Configure Fuse with our search options
      const fuse = new Fuse(allCards, {
        keys: ["title", "description", "tools", "type"],
        threshold: 0.4, // Increased from 0.3 to be more lenient
        distance: 200, // Increased search distance
        ignoreLocation: true, // Ignore where in the string the match occurs
        minMatchCharLength: 2,
        shouldSort: true,
        includeScore: true,
        findAllMatches: true,
      });
      setFuseInstance(fuse);

      if (debugAutomation) debugLog("Initialized Fuse with cards:", allCards.length);
    }
  }, [allCards]);

  // This useEffect is now handled by the unified filtering and sorting effect above

  const handleCardClick = async (e, card) => {
    if (!isSignedIn) {
      setShowAuthModal(true);
      return;
    }

    // Track guide view if the card has an image guide
    if (card.Image) {
      await trackGuideView(card.id);
    }

    // Keep existing CreatableSelect check
    if (
      e.target.closest(".react-select__control") ||
      e.target.closest(".react-select__menu")
    ) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    // Parse requirements before setting selected card
    const parsedCard = {
      ...card,
      requirements:
        typeof card.requirements === "string"
          ? JSON.parse(card.requirements)
          : card.requirements || {},
      fields: {
        ...card.fields,
        requirements:
          typeof card.fields?.requirements === "string"
            ? JSON.parse(card.fields.requirements)
            : card.fields?.requirements || {},
      },
    };

    // Update URL with card slug
    const slug = createSlug(card.title);
    const newUrl = new URL(window.location);
    newUrl.searchParams.set("automation", slug);
    window.history.pushState({}, "", newUrl);

    // Get email from Clerk user
    const userEmail = user?.primaryEmailAddress?.emailAddress;

    if (!isUsersLoading || !isSignedIn || !userEmail) {
      console.error("User not loaded or not signed in");
      return;
    }

    try {
      // Create record in Airtable
      await base("automationRequests").create([
        {
          fields: {
            RequesterEmail: userEmail,
            PartnerEmail:
              card.partnerEmail || "partneremailgoeshere@getupgraded.ca",
            AutomationDescription: card.title,
            Status: "Interested",
            AutomationCategory: card.tools.join(", "),
            Savings: card.benefits ? card.benefits[0] : "",
            Budget: card.pricing?.setup || 5000,
            ClientName: company?.fields?.Name || "",
            Date: new Date().toISOString().split("T")[0],
            Type: "Automation",
            LoomVideoLink: card.promptUrl || "",
            TechnologiesUsed: card.tools.join(", "),
            Requirements: JSON.stringify(card.requirements || {}),
            ...(companyData && {
              SimpleNetImpact: calculateNetImpactSimpleValue(card, companyData)
                .value,
              // SimpleNetImpactFormula: calculateNetImpactSimpleValue(card, companyData).processedFormula,
              // SimpleNetImpactFinalFormula: calculateNetImpactSimpleValue(card, companyData).finalFormula,
              CalculatedImpact: calculateAutomationValue(card, companyData)
                .value,
              // CalculatedImpactFinalFormula: calculateAutomationValue(card, companyData).finalFormula,
              ExpectedImpact: calculateNetImpactSimpleValue(card, companyData)
                .value,
            }),
          },
        },
      ]);

      debugLog("Created automation request record");
    } catch (error) {
      console.error("Error creating automation request:", error);
    }

    // Track guide view if the card has an image guide
    if (card.Image) {
      await trackGuideView(card.id);
    }

    try {
      // Fetch the latest data for the selected card
      const updatedCard = await base("automationsAIGrowthUpgrades").find(
        card.id
      );

      // Update the selectedCard state with the latest data
      setSelectedCard({
        ...parsedCard,
        GuideText: updatedCard.fields.GuideText,
      });
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error fetching card data:", error);
    }
  };

  const handleBuyNow = (card) => {
    // Generate a unique payment ID
    const paymentId = `payment_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    // Store the payment info
    localStorage.setItem(
      "pendingAutomation",
      JSON.stringify({
        id: paymentId,
        title: card.title,
        website: new URLSearchParams(window.location.search).get("website"),
        company: new URLSearchParams(window.location.search).get("company"),
        timestamp: Date.now(),
      })
    );

    // Use the Stripe link from Airtable if available, otherwise fall back to default
    const stripeLink =
      card.fields?.StripeLink || card.StripeLink || STRIPE_DEPOSIT_LINK;

    // Open Stripe payment in new tab with success_url
    window.open(
      `${stripeLink}?client_reference_id=${paymentId}&success_url=${window.location.origin}/automation-success`,
      "_blank"
    );
  };

  // Add this useEffect to poll for payment completion
  useEffect(() => {
    const handlePaymentMessage = (event) => {
      if (event.data.type === "PAYMENT_SUCCESS") {
        const card = automationCards.find(
          (c) => c.title === event.data.data.title
        );
        if (card) {
          setSelectedCard(card);
          setShowRequirementsForm(true);
        }
      }
    };

    window.addEventListener("message", handlePaymentMessage);
    return () => window.removeEventListener("message", handlePaymentMessage);
  }, []);

  // Keep the existing polling mechanism as a fallback
  useEffect(() => {
    const checkPaymentStatus = async () => {
      const pendingAutomation = localStorage.getItem("pendingAutomation");
      if (!pendingAutomation) return;

      const data = JSON.parse(pendingAutomation);
      const timestamp = data.timestamp;

      // If it's been more than 30 minutes, clear the pending automation
      if (Date.now() - timestamp > 30 * 60 * 1000) {
        localStorage.removeItem("pendingAutomation");
        return;
      }

      try {
        // Replace this URL with your API endpoint that checks Stripe payment status
        const response = await fetch("/api/check-payment-status");
        const { isComplete } = await response.json();

        if (isComplete) {
          const card = automationCards.find((c) => c.title === data.title);
          if (card) {
            setSelectedCard(card);
            setShowRequirementsForm(true);
          }
          localStorage.removeItem("pendingAutomation");
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
      }
    };

    const pollInterval = setInterval(checkPaymentStatus, 5000);
    return () => clearInterval(pollInterval);
  }, []);

  // One-click install automation function
  const handleOneClickInstall = async (card) => {
    try {
      setIsOneClickInstalling(true);

      // Show initial notification
      setNotification({
        showNotif: true,
        contents: {
          title: "One-Click Install",
          message: "Opening requirements form and auto-filling...",
          type: "info",
        },
      });

      // Step 1: Open requirements form
      setSelectedCard(card);
      setShowRequirementsForm(true);

      // Step 2: Wait for modal to open and then auto-fill
      setTimeout(async () => {
        if (
          card.requirements?.fromClient &&
          card.requirements.fromClient.length > 0
        ) {
          // Show auto-fill notification
          setNotification({
            showNotif: true,
            contents: {
              title: "One-Click Install",
              message: "Auto-filling requirements with AI...",
              type: "info",
            },
          });

          await handleAutoFillClientRequirements(card.requirements);

          // Step 3: Wait for auto-fill to complete, then submit
          setTimeout(() => {
            // Show submission notification
            setNotification({
              showNotif: true,
              contents: {
                title: "One-Click Install",
                message: "Submitting automation request...",
                type: "info",
              },
            });

            // Create a mock event and call handleFormSubmit directly
            const mockEvent = {
              preventDefault: () => {},
            };
            handleFormSubmit(mockEvent);
            setIsOneClickInstalling(false);
          }, 3000); // Wait 3 seconds for auto-fill to complete
        } else {
          // If no client requirements, just submit after a short delay
          setTimeout(() => {
            setNotification({
              showNotif: true,
              contents: {
                title: "One-Click Install",
                message: "Submitting automation request...",
                type: "info",
              },
            });

            const mockEvent = {
              preventDefault: () => {},
            };
            handleFormSubmit(mockEvent);
            setIsOneClickInstalling(false);
          }, 1000);
        }
      }, 500); // Wait 500ms for modal to open
    } catch (error) {
      console.error("Error in one-click install:", error);
      setIsOneClickInstalling(false);
      setNotification({
        showNotif: true,
        contents: {
          title: "Error",
          message:
            "Failed to complete one-click install. Please try the manual process.",
          type: "error",
        },
      });
    }
  };

  // Updated form submit handler (removing the duplicate)
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    try {
      // Create the record object with all fields // automationcard
      const recordData = {
        AutomationDescription: selectedCard.title,
        Budget: selectedCard.MonthlyPrice * 12, // Converting monthly to yearly
        CardURL:
          window.location.origin +
          "/automate-my-work?automation=" +
          createSlug(selectedCard.title),
        ExamplesToImitate: selectedCard.ExamplesToImitate || "",
        Requirements: JSON.stringify({
          fromClient:
            selectedCard.requirements?.fromClient?.map((req) => ({
              name: req.name,
              value: formData[`client-${req.name}`] || "",
            })) || [],
          technical:
            selectedCard.requirements?.technical?.map((req) => ({
              name: req.name,
              value: formData[`tech-${req.name}`] || "",
            })) || [],
          examples: formData.examples || "",
          affiliateLink: formData.affiliateLink || "",
          promotedWebsite: formData.promotedWebsite || "",
        }),
        Status: "Submitted",
        RequesterEmail: user?.primaryEmailAddress?.emailAddress,
        Type: selectedCard.category || "General",
        // Adding back missing fields
        PartnerEmail:
          generalAutomationEmails && generalAutomationEmails.length > 0
            ? generalAutomationEmails.join(", ")
            : selectedCard.partnerEmail ||
              "partneremailgoeshere@getupgraded.ca",
        AutomationCategory: selectedCard.tools?.join(", ") || "",
        Savings: selectedCard.benefits?.[0] || "",
        ClientName: company?.fields?.Name || "",
        Date: new Date().toISOString().split("T")[0],
        LoomVideoLink: selectedCard.promptUrl || "",
        TechnologiesUsed: selectedCard.tools?.join(", ") || "",
        UserResponses: JSON.stringify(formData), // Store raw form data as backup
        ...(companyData && {
          SimpleNetImpact: calculateNetImpactSimpleValue(
            selectedCard,
            companyData
          ).processedFormula,
          // SimpleNetImpactFormula:
          // SimpleNetImpactFinalFormula: calculateNetImpactSimpleValue(selectedCard, companyData).finalFormula,
          CalculatedImpact: calculateAutomationValue(selectedCard, companyData)
            .finalFormula,
          // CalculatedImpactFinalFormula: calculateAutomationValue(selectedCard, companyData).finalFormula,
          ExpectedImpact: calculateNetImpactSimpleValue(
            selectedCard,
            companyData
          ).value,
        }),
      };

      // Create the record in Airtable
      await base("automationRequests").create([
        {
          fields: recordData,
        },
      ]);

      // Reset form and close modal
      setFormData({});
      setShowRequirementsForm(false);

      // Show success notification
      setNotification({
        showNotif: true,
        contents: {
          title: "Success!",
          message: "Your automation request has been submitted.",
          type: "success",
        },
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      // Show error notification
      setNotification({
        showNotif: true,
        contents: {
          title: "Error",
          message: "Failed to submit automation request. Please try again.",
          type: "error",
        },
      });
    }
  };

  const handleModalClose = () => {
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete("automation");
    window.history.pushState({}, "", newUrl);
    setIsGuideVisible(false);
    setIsEditingGuideText(false);
    setIsModalOpen(false);
  };

  const getShareableLink = (card) => {
    const url = new URL(window.location);
    url.searchParams.set("automation", createSlug(card.title));
    return url.toString();
  };

  const MAX_HEARTS = 3000; // Set a safety limit

  const handleHeartCard = async (card) => {
    // Debug logging
    debugLog("Current user:", user);
    debugLog("User metadata:", {
      unsafe: user?.unsafeMetadata,
      public: user?.publicMetadata,
    });

    const userId = user?.unsafeMetadata?.airtableId;

    if (!userId) {
      console.error("No Airtable ID found in user metadata:", {
        unsafeMetadata: user?.unsafeMetadata,
        userId: user?.id,
      });
      return;
    }

    try {
      // Get current hearted users array
      const record = await base("automationsAIGrowthUpgrades").find(card.id);
      let heartedBy = [];

      try {
        heartedBy = JSON.parse(record.fields.HeartedBy || "[]");
        debugLog("Current heartedBy array:", heartedBy);
      } catch (e) {
        console.error("Error parsing HeartedBy:", e);
        heartedBy = [];
      }

      // Check if user already hearted
      const isHearted = heartedBy.includes(userId);
      debugLog("Is card hearted by user?", isHearted);

      // Update array
      const newHeartedBy = isHearted
        ? heartedBy.filter((id) => id !== userId)
        : [...heartedBy, userId];

      debugLog("New heartedBy array:", newHeartedBy);

      // Update Airtable
      await base("automationsAIGrowthUpgrades").update([
        {
          id: card.id,
          fields: {
            HeartedBy: JSON.stringify(newHeartedBy),
          },
        },
      ]);

      // Update local state
      if (setAllCards) {
        setAllCards((prev) =>
          prev.map((c) =>
            c.id === card.id
              ? {
                  ...c,
                  heartedBy: JSON.stringify(newHeartedBy),
                }
              : c
          )
        );
      }

      setHeartedCards((prev) =>
        isHearted ? prev.filter((id) => id !== card.id) : [...prev, card.id]
      );

      localStorage.setItem("heartedCards", JSON.stringify(heartedCards));

      debugLog("Successfully updated heart status");
    } catch (error) {
      console.error("Error updating hearts:", error);
    }
  };

  const handleSubscriptionToggle = async (card) => {
    if (!userRecord) {
      console.error("No user record found");
      return;
    }

    try {
      const isSubscribed = subscribedAutomations.includes(card.id);
      const newSubscriptions = isSubscribed
        ? subscribedAutomations.filter((id) => id !== card.id)
        : [...subscribedAutomations, card.id];

      // Update Airtable user record
      await base(usersTable).update([
        {
          id: userRecord.id,
          fields: {
            UpgraderAutomations: JSON.stringify(newSubscriptions),
          },
        },
      ]);

      // Update local state
      setSubscribedAutomations(newSubscriptions);
      setUserRecord((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          UpgraderAutomations: JSON.stringify(newSubscriptions),
        },
      }));

      debugLog("Successfully updated subscription status");
    } catch (error) {
      console.error("Error updating subscription:", error);
    }
  };

  // // Add this useEffect to monitor allCards changes
  // useEffect(() => {
  //   debugLog(
  //     "allCards updated:",
  //     allCards.map((card) => ({
  //       id: card.id,
  //       title: card.title,
  //       heartedBy: card.heartedBy,
  //       image: card.Image,
  //     }))
  //   );
  // }, [allCards]);

  // Add useEffect to handle sorting when stage changes
  useEffect(() => {
    // Only handle non-prioritization stages here
    // Exclude automation status stages (ready, coming_soon, requested) as they're now filters
    if (!['value_menu', 'value_rank', 'start_grow', 'ready', 'coming_soon', 'requested'].includes(selectedStage)) {
      if (selectedStage === "popular") {
        const filteredCards = allCards.filter(
          (card) => card.Image && card.Image.length > 0
        );
        const sortedByFavoritesAndPopularity =
          sortCardsByFavoritesAndPopularity(filteredCards);
        setFilteredCards(sortedByFavoritesAndPopularity);
      } else {
        const filtered = allCards.filter((card) => {
          if (selectedStage === "all") return true;
          return card.stage === selectedStage;
        });
        setFilteredCards(filtered);
      }
    }
  }, [selectedStage, allCards]);

  // Add this near the top of the component where showRequirementsForm is used
  useEffect(() => {
    if (debugAutomation) debugLog("showRequirementsForm state:", showRequirementsForm);
  }, [showRequirementsForm]);

  // Add this where selectedCard is set
  // useEffect(() => {
  //   debugLog("Selected card data:", selectedCard);
  //   debugLog(
  //     "Selected card requirements:",
  //     selectedCard?.requirements?.fromClient
  //   );
  //   debugLog(
  //     "Selected card fields requirements:",
  //     selectedCard?.fields?.requirements?.fromClient
  //   );
  // }, [selectedCard]);

  // Add this state to store the form requirements JSON
  const [formRequirements, setFormRequirements] = useState({
    fromClient: [
      {
        name: "User ID",
        example: "user123",
      },
      {
        name: "User Input Data",
        example: "text example",
      },
    ],
  });

  // Add this function to update the requirements
  const updateFormRequirements = (newRequirements) => {
    try {
      // If it's a string, parse it, otherwise use the object directly
      const parsedReqs =
        typeof newRequirements === "string"
          ? JSON.parse(newRequirements)
          : newRequirements;

      setFormRequirements(parsedReqs);
    } catch (e) {
      console.error("Failed to parse requirements:", e);
    }
  };

  // Simple search handler
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (!query.trim()) {
      setExactResults([]);
      setFuzzyResults([]);
      setFilteredCards(allCards);
      return;
    }

    // Exact matches
    const exact = allCards.filter((card) => {
      const lowercaseQuery = query.toLowerCase().trim();
      return (
        card.title?.toLowerCase().includes(lowercaseQuery) ||
        card.description?.toLowerCase().includes(lowercaseQuery) ||
        card.tools?.some((tool) =>
          tool.toLowerCase().includes(lowercaseQuery)
        ) ||
        card.type?.toLowerCase().includes(lowercaseQuery)
      );
    });
    setExactResults(exact);

    // Get exact match IDs for filtering
    const exactIds = new Set(exact.map((card) => card.id));

    // Fuzzy matches
    let fuzzy = [];
    if (fuseInstance) {
      const fuseResults = fuseInstance.search(query);
      fuzzy = fuseResults
        .map((result) => ({
          ...result.item,
          // Ensure heartedBy is properly formatted for fuzzy results
          heartedBy: result.item.heartedBy || "[]", // Default to empty array if undefined
          // Preserve any existing hearts count
          heartsCount: result.item.heartsCount || 0,
        }))
        // Only include items that aren't in exact matches
        .filter((card) => !exactIds.has(card.id));
    }
    setFuzzyResults(fuzzy);

    // Update debug state
    setDebug({
      totalCards: allCards.length,
      filteredCards: exact.length + fuzzy.length,
      searchQuery: query,
      exactMatches: exact.length,
      fuzzyMatches: fuzzy.length,
    });

    debugLog("Exact matches:", exact.length);
    debugLog("Fuzzy matches:", fuzzy.length);
  };

  // Add this function to fetch notes
  const fetchNotes = async (automationId) => {
    try {
      const records = await base("automation_notes")
        .select({
          filterByFormula: `{automation_id} = '${automationId}'`,
          sort: [{ field: "helpful_count", direction: "desc" }],
          view: "Grid view",
        })
        .all();

      const formattedNotes = records.map((record) => ({
        id: record.id,
        content: record.fields.content,
        userId: record.fields.user_id,
        userName: record.fields.user_name,
        helpfulCount: record.fields.helpful_count || 0,
        notHelpfulCount: record.fields.not_helpful_count || 0,
        createdAt: record.fields.created_at,
        status: record.fields.status,
      }));

      setNotes(formattedNotes);
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  // Add this function to handle note submission
  const handleSubmitNote = async (noteContent) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    try {
      const response = await base("automation_notes").create([
        {
          fields: {
            automation_id: selectedCard.id,
            user_id: user.id,
            user_email: user.primaryEmailAddress.emailAddress,
            user_name: user.fullName || user.username,
            content: noteContent,
            helpful_count: 0,
            not_helpful_count: 0,
            status: "pending",
            created_at: new Date().toISOString(),
          },
        },
      ]);

      // Notes: [
      //   ...(selectedCard.Notes || []),
      //   {
      //     content: noteContent,
      //     userId: user.id,
      //     userName: user.fullName || user.username,
      //     createdAt: new Date().toISOString(),
      //     helpfulCount: 0,
      //     notHelpfulCount: 0,
      //   },
      // ],

      // Add new note to state
      setNotes((prev) => [
        ...prev,
        {
          id: selectedCard.id,
          content: noteContent,
          userId: user.id,
          userName: user.fullName || user.username,
          helpfulCount: 0,
          notHelpfulCount: 0,
          createdAt: new Date().toISOString(),
          status: "pending",
        },
      ]);

      setNoteContent("");
      setShowAddNoteForm(false);

      setNotification({
        showNotif: true,
        contents: {
          type: "success",
          heading: "Success",
          message: "Your note has been added successfully.",
        },
      });
    } catch (error) {
      console.error("Error submitting note:", error);
      setNotification({
        showNotif: true,
        contents: {
          type: "error",
          heading: "Error",
          message: "Failed to add note. Please try again.",
        },
      });
    } finally {
      setIsSubmittingNote(false);
    }
  };

  // Add this function to handle rating
  const handleNoteRating = async (noteId, isHelpful) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    try {
      // Check if user has already rated
      const existingRatings = await base("note_ratings")
        .select({
          filterByFormula: `AND({note_id} = '${noteId}', {user_id} = '${user.id}')`,
        })
        .all();

      if (existingRatings.length > 0) {
        setNotification({
          showNotif: true,
          contents: {
            type: "info",
            heading: "Already Rated",
            message: "You have already rated this note",
          },
        });
        return;
      }

      // Create rating record
      await base("note_ratings").create([
        {
          fields: {
            note_id: String(noteId),
            user_id: String(user.id),
            is_helpful: String(isHelpful),
            created_at: String(new Date().toISOString()),
          },
        },
      ]);

      // Update note counts
      const noteRecord = await base("automation_notes").find(noteId);
      await base("automation_notes").update([
        {
          id: noteId,
          fields: {
            helpful_count: isHelpful
              ? (noteRecord.fields.helpful_count || 0) + 1
              : noteRecord.fields.helpful_count,
            not_helpful_count: !isHelpful
              ? (noteRecord.fields.not_helpful_count || 0) + 1
              : noteRecord.fields.not_helpful_count,
          },
        },
      ]);

      // Update local state
      setNotes((prev) =>
        prev.map((note) => {
          if (note.id === noteId) {
            return {
              ...note,
              helpfulCount: isHelpful
                ? note.helpfulCount + 1
                : note.helpfulCount,
              notHelpfulCount: !isHelpful
                ? note.notHelpfulCount + 1
                : note.notHelpfulCount,
            };
          }
          return note;
        })
      );
    } catch (error) {
      console.error("Error rating note:", error);
      setNotification({
        showNotif: true,
        contents: {
          type: "error",
          heading: "Error",
          message: "Failed to rate note. Please try again.",
        },
      });
    }
  };

  // Add this useEffect to monitor filteredCards changes
  // useEffect(() => {
  //   debugLog("filteredCards updated:", filteredCards?.length);
  // }, [filteredCards]);

  // Add this near your other state declarations
  const [debug, setDebug] = useState({
    totalCards: 0,
    filteredCards: 0,
    searchQuery: "",
    stage: "",
    category: "",
  });

  // Unified function to get filtered and sorted cards
  const getFilteredAndSortedCards = (sortType = 'favorites_and_popularity') => {
    const filtered = filterCards(allCards, {
      searchQuery,
      selectedStage,
      selectedCategory,
      showPrioritizedList,
      prioritizedIds,
      user
    });


    return sortCards(filtered, sortType);
  };

  // Legacy function for backward compatibility
  const getFilteredCards = () => {
    return getFilteredAndSortedCards('favorites_and_popularity');
  };

  const steps = [
    {
      target: ".search-bar",
      content:
        "Describe what you want to automate in plain English, or search through our existing automations",
      placement: "bottom",
    },
    {
      target: ".category-selector",
      content: "Filter automations by category to find exactly what you need",
      placement: "bottom",
    },
    {
      target: ".filters-section",
      content:
        "Quick filters to show popular, ready-to-use, or upcoming automations",
      placement: "bottom",
    },
    {
      target: ".automation-type",
      content: "See the type of automation and which tools it uses",
      placement: "top",
    },
    {
      target: ".heart-button",
      content: "Heart your favorite automations to save them to your list",
      placement: "left",
    },
    {
      target: ".pricing-info",
      content: "View the expected monthly benefit and cost of the automation",
      placement: "top",
    },
    {
      target: ".status-badge",
      content:
        "Check if the automation is ready to use, coming soon, or has been requested by others",
      placement: "left",
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) && setRunTour) {
      setRunTour(false);
    }
  };

  const handleSync = async () => {
    try {
      const result = await syncAutomationsToAirtable(automationCards);
      if (result.success) {
        alert(`Sync successful! ${result.message}`);
      } else {
        alert(`Sync failed: ${result.error}`);
      }
    } catch (error) {
      console.error("Error during sync:", error);
      alert("Failed to sync automations to Airtable");
    }
  };

  const location = useLocation();
  const isAutomatePage =
    location.pathname === "/automate-my-work" ||
    location.pathname === "/ai-automations";

  // Add this function to handle saving all edits
  const handleSaveEdits = async () => {
    let imageUrls = [];

    try {
      // Add the current URL being edited, if any
      if (editedCard.ExampleOutputs) {
        // Handle different possible formats
        if (typeof editedCard.ExampleOutputs === "string") {
          // If it's a URL, use it directly
          if (editedCard.ExampleOutputs.startsWith("http")) {
            imageUrls = [editedCard.ExampleOutputs];
          } else {
            // Try to parse JSON string, fallback to empty array if it fails
            try {
              const parsed = JSON.parse(editedCard.ExampleOutputs);
              imageUrls = Array.isArray(parsed) ? parsed : [parsed];
            } catch (e) {
              console.warn("Failed to parse ExampleOutputs:", e);
              imageUrls = [];
            }
          }
        } else if (Array.isArray(editedCard.ExampleOutputs)) {
          imageUrls = editedCard.ExampleOutputs;
        }
      }

      // Clean up the URLs - keep it simple
      imageUrls = imageUrls
        .map((url) => {
          if (typeof url === "string" && url.startsWith("http")) {
            return url.trim();
          }
          return null;
        })
        .filter(Boolean); // Remove null values

      debugLog("Clean URLs to save:", imageUrls);

      const updateFields = {
        Title: editedCard.title,
        Benefits: JSON.stringify(editedCard.benefits),
        NotGoodFor: JSON.stringify(editedCard.notGoodFor),
        Deliverables: JSON.stringify(editedCard.deliverables),
        SetupPrice: editedCard.SetupPrice,
        MonthlyPrice: editedCard.MonthlyPrice,
        TurnaroundDays: editedCard.turnaroundDays,
      };

      // Only add ExampleOutputs if we have valid URLs
      if (imageUrls.length > 0) {
        updateFields.ExampleOutputs = JSON.stringify(imageUrls);
      }

      await base("automationsAIGrowthUpgrades").update([
        {
          id: editedCard.id,
          fields: updateFields,
        },
      ]);

      // Update local state with the clean array
      const updatedCard = {
        ...editedCard,
        ExampleOutputs: imageUrls,
      };

      if (setAllCards) {
        setAllCards((prev) =>
          prev.map((card) => (card.id === editedCard.id ? updatedCard : card))
        );
      }
      setSelectedCard(updatedCard);
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving edits:", {
        error,
        cardId: editedCard.id,
        originalExampleOutputs: editedCard.ExampleOutputs,
        cleanedUrls: imageUrls,
      });
      alert("Failed to save changes");
    }
  };

  // Add this function to handle field updates
  const handleFieldUpdate = (field, value, index = null, subfield = null) => {
    setEditedCard((prev) => {
      const newCard = { ...prev };

      if (index !== null && Array.isArray(newCard[field])) {
        // Handling array updates (like bestFor, benefits, etc.)
        const newArray = [...newCard[field]];
        if (subfield) {
          newArray[index] = { ...newArray[index], [subfield]: value };
        } else {
          newArray[index] = value;
        }
        newCard[field] = newArray;
      } else {
        // Handling direct field updates
        newCard[field] = value;
      }

      return newCard;
    });
  };

  // Add this function to handle adding new items to arrays
  const handleAddItem = (field) => {
    setEditedCard((prev) => ({
      ...prev,
      [field]: [...(prev[field] || []), ""],
    }));
  };

  // Add this function to handle removing items from arrays
  const handleRemoveItem = (field, index) => {
    setEditedCard((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  // Add these debug states near your other state declarations
  const [debugInfo, setDebugInfo] = useState({
    lastError: null,
    lastAttempt: null,
    selectedFields: [],
    completedFields: [],
  });

  // Add these debug states near your other state declarations
  const [newDebugInfo, setNewDebugInfo] = useState({});

  const handleAutoComplete = async () => {
    if (!selectedCard) {
      console.error("No card selected for auto-completion");
      return;
    }

    setIsLoading(true);
    debugLog("Starting auto-completion for card:", selectedCard.title);

    // Define all possible fields to complete
    const allFields = [
      "ExampleOutputs", // Move ExampleOutputs to the start to prioritize it
      "Type",
      "Stage",
      "Tools",
      "BestFor",
      "NotGoodFor",
      "TurnaroundDays",
      "SetupPrice",
      "MonthlyPrice",
      "Benefits",
      "IncludedFeatures",
      "BestPractices",
      "requirements",
      "WorkflowDetails",
      "AutomationRecipes",
      "Deliverables",
      "SupportDetails",
      "Integrations",
      "ComplianceItems",
    ];

    // Track completion status for each field
    const completionStatus = {
      completed: [],
      failed: [],
      skipped: [],
    };

    try {
      // Process each field one at a time
      for (const fieldName of allFields) {
        debugLog(`\nChecking field: ${fieldName}`);

        // Check if field is empty or needs updating
        const currentValue =
          selectedCard[fieldName] || selectedCard.fields?.[fieldName];

        if (currentValue && !Array.isArray(currentValue)) {
          try {
            const parsed = JSON.parse(currentValue);
            if (parsed && parsed.length > 0) {
              debugLog(
                `Skipping ${fieldName} - already has content:`,
                parsed
              );
              completionStatus.skipped.push(fieldName);
              continue;
            }
          } catch (e) {
            // If it's not JSON, check if the string has content
            if (currentValue && currentValue.trim()) {
              debugLog(
                `Skipping ${fieldName} - already has content:`,
                currentValue
              );
              completionStatus.skipped.push(fieldName);
              continue;
            }
          }
        }

        debugLog(`Generating content for ${fieldName}...`);

        try {
          let parsedContent;

          // Special handling for ExampleOutputs
          if (fieldName === "ExampleOutputs") {
            // Get example images based on the title
            const imageSet = generateExampleOutputsPrompt(selectedCard.title);
            parsedContent = imageSet;

            // Validate the URLs
            const validation = validateField(fieldName, parsedContent);
            if (!validation.valid) {
              throw new Error(
                `Invalid image URLs: ${validation.errors.join(", ")}`
              );
            }

            debugLog("Generated example outputs:", parsedContent);
          } else {
            // Handle other fields as before
            const prompt = generateFieldPrompt(fieldName, selectedCard.title);
            debugLog(`Generated prompt for ${fieldName}:`, prompt);

            // Call Next.js API route (server-side) instead of OpenAI directly
            const response = await fetch('/api/generateAutomationField', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                prompt,
                fieldName,
                model: 'gpt-4o',
                maxTokens: 200,
                temperature: 0.4
              })
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(errorData.details || errorData.error || `HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            
            if (!result.success || !result.content) {
              throw new Error(result.error || 'No response from automation field generation');
            }

            const content = result.content;
            debugLog(`Raw response for ${fieldName}:`, content);

            // Parse the response based on field type
            if (automationSchema.fields[fieldName]?.type === "number") {
              parsedContent = Number(content.trim());
              if (isNaN(parsedContent)) {
                throw new Error(`Invalid number format for ${fieldName}`);
              }
            } else if (automationSchema.fields[fieldName]?.type === "array") {
              parsedContent = content
                .split(/[\n•-]/)
                .map((item) => item.trim())
                .filter(
                  (item) =>
                    item.length > 0 &&
                    !item.startsWith("•") &&
                    !item.startsWith("-")
                );
            } else {
              parsedContent = content.trim();
            }
          }

          debugLog(`Parsed content for ${fieldName}:`, parsedContent);

          // Validate the content
          const validation = validateField(fieldName, parsedContent);
          if (!validation.valid) {
            throw new Error(
              `Validation failed: ${validation.errors.join(", ")}`
            );
          }

          // Format for Airtable
          const formattedValue = formatForAirtable(fieldName, parsedContent);

          // Update Airtable
          await base("automationsAIGrowthUpgrades").update([
            {
              id: selectedCard.id,
              fields: {
                [fieldName]: formattedValue,
              },
            },
          ]);

          // Update local state
          setSelectedCard((prev) => ({
            ...prev,
            [fieldName]: formattedValue,
          }));

          setEditedCard((prev) => ({
            ...prev,
            [fieldName]: formattedValue,
          }));

          completionStatus.completed.push(fieldName);
          debugLog(`Successfully completed ${fieldName}`);
        } catch (error) {
          console.error(`Error completing ${fieldName}:`, error);
          completionStatus.failed.push({
            field: fieldName,
            error: error.message,
          });
          // Continue with next field despite error
          continue;
        }
      }

      // Final completion report
      debugLog("\nAutocompletion Summary:");
      debugLog("Completed:", completionStatus.completed);
      debugLog("Failed:", completionStatus.failed);
      debugLog("Skipped:", completionStatus.skipped);

      // Show user-friendly alert
      alert(`Autocomplete finished!\n
Completed: ${completionStatus.completed.length} fields
Failed: ${completionStatus.failed.length} fields
Skipped: ${completionStatus.skipped.length} fields`);
    } catch (error) {
      console.error("Fatal error in auto-completion:", error);
      alert(`Error during auto-completion: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageScrape = async () => {
    if (!selectedCard) {
      debugLog("No card selected, returning early");
      return;
    }

    setIsLoading(true);
    debugLog("Starting image scrape for card:", selectedCard.title);

    try {
      // Log the URL we're about to fetch
      const apiUrl = `http://localhost:3001/api/scrape-images?title=${encodeURIComponent(
        selectedCard.title
      )}`;
      debugLog("Fetching from:", apiUrl);

      // Add explicit headers to ensure we get JSON back
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      // Log the response status and headers
      debugLog("Response status:", response.status);
      debugLog("Response headers:", [...response.headers.entries()]);

      // Get the response text first to debug
      const text = await response.text();
      debugLog("Raw response:", text);

      // Try to parse as JSON
      const data = JSON.parse(text);

      if (!data.success) {
        throw new Error(data.error || "Failed to fetch images");
      }

      // Format the outputs
      const formattedOutputs = data.images.map((url, i) => ({
        type:
          i === 0 ? "social_post" : i === 1 ? "analytics" : "content_calendar",
        description: `${selectedCard.title} ${
          i === 0 ? "Dashboard" : i === 1 ? "Interface" : "Workflow"
        }`,
        imageUrl: url,
        specs: {
          format: "Dashboard view",
          metrics: ["Performance", "Efficiency", "ROI"],
          timeRange: "Real-time",
        },
      }));

      // Update Airtable record
      await base("automationsAIGrowthUpgrades").update([
        {
          id: selectedCard.id,
          fields: {
            ExampleOutputs: JSON.stringify(formattedOutputs),
          },
        },
      ]);

      // Update local state
      setEditedCard((prev) => ({
        ...prev,
        exampleOutputs: formattedOutputs,
      }));

      debugLog("Successfully updated images:", formattedOutputs);
      alert("Successfully updated images!");
    } catch (error) {
      debugLog(`Error scraping images: ${error.message}`);
      console.error("Error scraping images:", error);
      debugLog(`Error scraping images: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Add this useEffect to monitor showGuideImage state changes
  // useEffect(() => {
  //   debugLog("showGuideImage state changed:", showGuideImage);
  // }, [showGuideImage]);

  useEffect(() => {
    if (isModalOpen) {
      // Add a small delay to ensure the modal is rendered
      setTimeout(() => {
        // Try to find and scroll the modal content specifically
        const modalContent = document.querySelector('[role="dialog"]');
        if (modalContent) {
          debugLog("Found modal content, scrolling to bottom then top");
          // First scroll to bottom
          modalContent.scrollTop = modalContent.scrollHeight;

          // Then immediately scroll to top
          setTimeout(() => {
            modalContent.scrollTop = 0;
          }, 450);
        }
      }, 600);

      // Log for debugging
      debugLog("Modal opened, attempting modal scroll");
    }
  }, [isModalOpen]);

  const tourSteps = [
    {
      target: ".search-section",
      content: "Search for automations by keyword or browse by category",
      disableBeacon: true,
    },
    {
      target: ".filters-section",
      content: "Filter automations by category",
    },
    {
      target: ".stage-filters",
      content:
        "Quick filters to help you prioritize: popular, ready-to-use, beta, value menu, value rank, or start & grow",
      placement: "bottom",
    },
    {
      target: ".automation-card",
      content: "Browse through our collection of ready-to-use automations",
    },
    {
      target: ".automation-type",
      content: "See the type of upgrade and which tools are recommended",
    },
    {
      target: ".heart-button",
      content:
        "Heart your favorite automations to save them to your list for later and see who's interested in what.",
    },
    {
      target: ".status-badge",
      content:
        "Check if the automation is 'Ready to Use', 'In Beta', or 'Requested' by others",
    },
    {
      target: ".earnings-section",
      content:
        "View the expected monthly net benefit of the automation - estimated time and cost savings or improved earnings per month",
    },
  ];

  const handleUnlimitedCheckout = () => {
    // You can add analytics tracking here
    debugLog("User clicked unlimited automations checkout");
    window.open("https://buy.stripe.com/6oE17O6NA1ukdag9AC", "_blank");
  };

  // Add these helper functions near the top of the AutomationDirectory component
  const setContents = (content, setterFunction) => {
    setterFunction(content);
  };

  const setShowNotif = (show, setterFunction) => {
    setterFunction(show);
    if (show) {
      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        setterFunction(false);
      }, 50000);
    }
  };

  const handleOfferSubmit = async () => {
    const userEmail = user?.primaryEmailAddress?.emailAddress;

    if (!isUsersLoading || !isSignedIn || !userEmail) {
      setNotification({
        showNotif: true,
        contents: {
          type: "error",
          heading: "Error",
          message: "Please sign in to submit an offer",
        },
      });
      return;
    }

    try {
      await base("automationRequests").create([
        {
          fields: {
            RequesterEmail: userEmail,
            PartnerEmail: "partneremailgoeshere@getupgraded.ca",
            AutomationDescription: selectedCard?.title || "Unknown Automation",
            Status: "Offer Received",
            AutomationCategory: selectedCard?.tools?.join(", ") || "",
            Savings: selectedCard?.benefits?.[0] || "",
            Budget: parseFloat(offerAmount),
            ClientName: company?.fields?.Name || "",
            Date: new Date().toISOString().split("T")[0],
            Type: "Custom Offer",
            LoomVideoLink: selectedCard?.promptUrl || "",
            TechnologiesUsed: selectedCard?.tools?.join(", ") || "",
            AutomationSelected: selectedCard?.title || "",
            ...(companyData && {
              SimpleNetImpact: calculateNetImpactSimpleValue(
                selectedCard,
                companyData
              ).value,
              SimpleNetImpactFormula: calculateNetImpactSimpleValue(
                selectedCard,
                companyData
              ).processedFormula,
              SimpleNetImpactFinalFormula: calculateNetImpactSimpleValue(
                selectedCard,
                companyData
              ).finalFormula,
              CalculatedImpact: calculateAutomationValue(
                selectedCard,
                companyData
              ).value,
              CalculatedImpactFinalFormula: calculateAutomationValue(
                selectedCard,
                companyData
              ).finalFormula,
              ExpectedImpact: calculateNetImpactSimpleValue(
                selectedCard,
                companyData
              ).value,
            }),
          },
        },
      ]);

      // Do any offer submission logic here (e.g., API call)
      // For demo purposes, simulate a successful async operation:
      await new Promise((resolve) => setTimeout(resolve, 500));

      alert("Offer submitted successfully!");
      // Now trigger the notification by updating the state.
      // Ensure that setOfferNotification is declared in your component state.

      // Close the modal
      handleModalClose();

      // Show success notification

      setNotification({
        showNotif: true,
        contents: {
          type: "success",
          heading: "Offer Submitted!",
          message: "We'll get back to you within 48 hours!",
        },
      });

      // Clear the offer amount
      setOfferAmount("");
    } catch (error) {
      console.error("Error submitting offer:", error);
      setNotification({
        showNotif: true,
        contents: {
          type: "error",
          heading: "Error",
          message: "Failed to submit offer. Please try again.",
        },
      });
    }
  };

  const fuseOptions = {
    keys: ["title", "description", "tools", "type"],
    threshold: 1,
    includeScore: true,
    shouldSort: true,
    minMatchCharLength: 2,
    // Additional options you might want to consider:
    distance: 100, // How far to search for matches
    location: 0, // Where to start searching for matches
    ignoreLocation: true, // Ignore location and distance
    findAllMatches: true, // Continue searching after first match
  };

  // First, extract the card rendering into a reusable function
  const renderAutomationCard = (card, type, userTier, isAdmin) => (
    <div
      key={`${type}-${card.id}`}
      onClick={(e) => handleCardClick(e, card)}
      className="group relative bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer border border-gray-100 min-h-[220px]"
    >
      {/* Video Overlay - covers the whole card when icon is hovered */}
      {hoveredVideoCardId === card.id && (
        <div
          className="absolute top-0 left-0 w-full h-full z-50 flex items-center justify-center"
          style={{
            borderRadius: "1rem",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
            background: "black",
            border: "2px solid #e5e7eb", // Tailwind gray-200
          }}
          onMouseLeave={(e) => {
            e.stopPropagation();
            setHoveredVideoCardId(null);
          }}
        >
          <iframe
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${card.YoutubeID.value}?autoplay=1&controls=0&modestbranding=1&rel=0&showinfo=0`}
            title="YouTube video preview"
            frameBorder="0"
            allow="autoplay; encrypted-media"
            allowFullScreen
            style={{
              width: "100%",
              height: "100%",
              borderRadius: "1rem",
              pointerEvents: "none",
            }}
          />
          {/* Overlay to block pointer events */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 10,
              cursor: "default",
              background: "transparent",
            }}
          />
        </div>
      )}
      {/* Debug indicator */}
      {/* <div className="absolute top-2 left-2 text-xs text-gray-400">
        {type === 'exact' ? 'Exact' : 'Fuzzy'}
      </div> */}

      {/* Card Header */}
      <div className="flex items-start mb-4">
        <div className="flex items-center gap-2 automation-type">
          {/* Use generated icons from Tools field, fallback to card.icons */}
          {(generateIconsFromTools(card.Tools) || card.icons || DEFAULT_FALLBACK_ICONS).map((icon, i) => (
            <img
              key={i}
              src={icon}
              alt=""
              className="h-6 w-6 rounded-md shadow-sm"
            />
          ))}
          <AutomationTypeBadge type={card.type} />
        </div>
      </div>

      {/* AutomationCard Content - We need to make video display in each one of the cards */}
      <h3 className="text-lg font-semibold text-gray-900 mb-2 whitespace-pre-line">
        {formatTitle(card.title)}
      </h3>

      {/* Pricing Info - Updated with better error handling */}
      <div className="absolute bottom-6 left-6 flex items-center">
        <div className="flex flex-col gap-1 earnings-section group">
          {/* <span className="text-[10px] text-gray-400">
            Help us improve dynamic estimates
          </span> */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-x-1">
              {companyData ? (
                <span className="text-sm font-semibold text-gray-800">
                  <span className="group-hover:hidden">
                    +
                    {currencyFormat(
                      safeImpactAmount(
                        calculateNetImpactSimpleValue(card, companyData).value,
                        { min: 1000, fallback: 1000 }
                      )
                    )}
                    /yr
                  </span>
                  <span className="hidden group-hover:inline">
                    {card.MonthlyPrice ? `$${Math.round(card.MonthlyPrice)}/mo` : "$99"}
                  </span>
                  <span className="text-xs text-gray-400 relative left-1 group-hover:hidden">
                    est earnings
                  </span>
                  <span className="text-xs text-gray-400 relative left-1 hidden group-hover:inline">
                    investment
                  </span>
                </span>
              ) : (
                <span
                  className="text-sm font-medium text-blue-600 cursor-pointer flex items-center gap-x-1 relative"
                  onClick={() => setShowAuthModal(true)}
                >
                  Join waitlist for est. $ value
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {/* <span className="text-[10px] px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded-full font-medium">
                BETA
              </span> */}
              <Popover className="relative">
                <Popover.Button className="flex items-center text-gray-400 hover:text-gray-500">
                  <InformationCircleIcon
                    className="h-4 w-4"
                    aria-hidden="true"
                  />
                </Popover.Button>
                <Popover.Panel className="absolute z-50 mb-2 w-80 -translate-x-1/2 transform px-4 sm:px-0 left-1/2 bottom-[120%]">
                  <div className="relative overflow-visible rounded-lg shadow-lg ring-1 ring-black/5">
                    <div className="relative bg-white p-6">
                      <div className="prose prose-sm max-w-none">
                        <div className="mt-20">
                          <hr className="my-4" />
                          <h3 className="text-md text-gray-900 mb-2 font-bold">
                            New Net Impact
                          </h3>
                          <h4 className="text-md text-gray-900 mb-2 font-bold">
                            Simple Net Impact
                          </h4>
                          <code className="block bg-gray-100 p-5 my-2 rounded-lg font-mono text-sm">
                            {card.NetImpactSimple?.value}
                          </code>
                          <h4 className="text-md text-gray-900 mb-2 font-bold">
                            Simple Net Impact Final Formula
                          </h4>
                          <code className="block bg-gray-100 p-5 my-2 rounded-lg font-mono text-sm">
                            {currencyFormat(
                              safeImpactAmount(
                                calculateNetImpactSimpleValue(card, companyData)
                                  .value,
                                { min: 0, fallback: 0 }
                              )
                            )}{" "}
                            ={" "}
                            {
                              calculateNetImpactSimpleValue(card, companyData)
                                .finalFormula
                            }
                          </code>
                          <h4 className="text-md text-gray-900 mb-2 font-bold">
                            Super Accurate Net Impact
                          </h4>
                          <code className="block bg-gray-100 p-5 my-2 rounded-lg font-mono text-sm">
                            {card.NetImpact?.value}
                          </code>
                          <h4 className="text-md text-gray-900 mb-2 font-bold">
                            Super Accurate Net Impact Explanation
                          </h4>
                          {card.ExampleNetImpactValues?.value && (
                            <div className="mt-4">
                              <pre className="whitespace-pre-wrap font-sans text-sm text-gray-600 bg-gray-50 rounded-lg p-4">
                                {card.ExampleNetImpactValues.value}
                              </pre>
                            </div>
                          )}
                          {/* Steps to Winning
                        - 1. Get the formula
                        - 2. Plug in the numbers
                        - 3. Calculate the impact
                        - 4. Profit
                        */}
                        </div>
                      </div>
                    </div>
                  </div>
                </Popover.Panel>
              </Popover>
            </div>
          </div>
        </div>
      </div>

      {/* Status Badge */}
      <div className="absolute bottom-4 right-4 status-badge flex items-center gap-2">
        {/* <span
          className={`
            px-2.5 py-1 rounded-full text-xs font-medium
            ${
              card.stage === "ready"
                ? "bg-green-50 text-green-700"
                : "bg-gray-100 text-gray-600"
            }
          `}
        >
          {card.stage === "coming_soon" ? "Beta" : "Ready to Use"}
        </span> */}
        {/* Install Button */}
        <span className="isolate inline-flex rounded-md shadow-lg">
          <button
            type="button"
            onClick={() => setShowRequirementsForm(true)}
            className="relative inline-flex items-center gap-x-1.5 rounded-l-md bg-zinc-900 px-2 py-2 text-xs font-bold text-white ring-inset hover:bg-zinc-700 focus:z-10 dark:bg-zinc-600 dark:hover:bg-zinc-700 transition-colors duration-150"
          >
            Try
          </button>
          <button
            type="button"
            onClick={() => handleOneClickInstall(card)}
            disabled={isOneClickInstalling}
            title="One-click install: Auto-fill requirements and submit automation request"
            className="relative -ml-px inline-flex items-center rounded-r-md bg-zinc-900 px-2 py-2 text-xs font-semibold text-white ring-inset hover:bg-zinc-700 focus:z-10 dark:bg-zinc-600 dark:hover:bg-zinc-700 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isOneClickInstalling ? (
              <svg
                className="animate-spin h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              <svg
                className="h-4 w-4"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>
        </span>
        <div
          className="relative flex items-center"
          onMouseEnter={(e) => {
            e.stopPropagation();
            setHoveredVideoCardId(card.id);
          }}
          // onMouseLeave removed from here, handled by overlay
        >
          {/* <CloudArrowUpIcon className="h-5 w-5 text-blue-600" /> */}
          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 rounded-full bg-gray-400/10 animate-subtle-pulse" />
            <img
              src="/images/getupgraded_logo.jpeg"
              alt="Scroll to top"
              className="h-6 w-6 group-hover:scale-110 transition-transform duration-200 z-10 relative rounded-full ring-1 ring-gray-400/10"
            />
          </div>
        </div>
      </div>

      {/* Heart Button */}
      <HeartButton
        card={card}
        isHearted={heartedCards.includes(card.id)}
        onHeart={handleHeartCard}
        onAddToList={handleAddToList}
        onRemoveFromList={handleRemoveFromList}
      />

      {/* Admin Edit Button */}
      {isAdmin && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setEditingAutomation(card);
            setIsEditModalOpen(true);
          }}
          className="absolute top-0 -left-12 z-50 p-2 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors duration-200 opacity-0 group-hover:opacity-100"
          title="Edit Automation"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
      )}

      {/* Subscription Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleSubscriptionToggle(card);
        }}
        className={`absolute top-10 -left-12 z-50 p-2 rounded-full shadow-lg transition-colors duration-200 opacity-0 group-hover:opacity-100 ${
          subscribedAutomations.includes(card.id)
            ? "bg-green-600 text-white hover:bg-green-700"
            : "bg-gray-400 text-white hover:bg-gray-500"
        }`}
        title={subscribedAutomations.includes(card.id) ? "Unsubscribe" : "Subscribe to updates"}
      >
        <BookmarkIcon 
          className={`h-4 w-4 ${
            subscribedAutomations.includes(card.id)
              ? "fill-white text-white"
              : "fill-none text-white"
          }`} 
        />
      </button>
    </div>
  );

  // List view component - Beautiful, Apple-like, and compact
  const renderAutomationListItem = (card, type, userTier, isAdmin, index = 0, animatingCardIndex = 0, isAnimating = false, completedIndices = new Set()) => {
    const isTopCard = index === animatingCardIndex;
    const shouldAnimate = isTopCard && isAnimating;
    const isCompleted = completedIndices.has(index);
    const isBelowAnimating = isAnimating && index > animatingCardIndex;
    // Card height: p-4 (32px padding) + content (~56px) = ~88px, plus space-y-2 gap (8px) = 96px total
    // Using measured height if available, otherwise fallback
    const CARD_HEIGHT_WITH_GAP = measuredCardHeight;
    
    // Hide completed cards completely
    if (isCompleted && !shouldAnimate) {
      return null;
    }
    
    return (
    <div
      ref={index === 0 ? cardHeightRef : null}
      key={`${type}-${card.id}-${index === animatingCardIndex ? animatingCardIndex : 'static'}`}
      onClick={(e) => handleCardClick(e, card)}
      className={`group relative bg-white rounded-xl p-4 border border-gray-200 hover:border-gray-300 hover:shadow-md shadow-sm cursor-pointer ${
        shouldAnimate ? 'animate-todo-complete overflow-hidden' : 'overflow-visible'
      }`}
      style={{
        ...(shouldAnimate ? {
          animation: 'todoComplete 1.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards',
          zIndex: 50,
          pointerEvents: 'none',
        } : {
          // Natural reflow with smooth transition - cards will smoothly move up as space collapses
          transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          opacity: 1,
        }),
      }}
    >
      {/* Checkmark overlay - appears during animation */}
      {shouldAnimate && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100/90 z-10 animate-checkmark-appear rounded-xl">
          <div className="w-14 h-14 rounded-full bg-gray-900 flex items-center justify-center shadow-lg animate-checkmark-bounce">
            <CheckIcon className="h-8 w-8 text-white" strokeWidth={3} />
          </div>
        </div>
      )}
      <div className={`flex items-center gap-4 relative transition-opacity duration-500 ${shouldAnimate ? 'opacity-60' : 'opacity-100'}`}>
        {/* Icons */}
        <div className="flex-shrink-0 flex items-center gap-1.5">
          {(generateIconsFromTools(card.Tools) || card.icons || DEFAULT_FALLBACK_ICONS).slice(0, 2).map((icon, i) => (
            <img
              key={i}
              src={icon}
              alt=""
              className="h-9 w-9 rounded-lg shadow-md p-0.5"
            />
          ))}
        </div>

        {/* Title and Type */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900 truncate">
              {formatTitle(card.title)}
            </h3>
            <AutomationTypeBadge type={card.type} />
          </div>
        </div>

        {/* Earnings */}
        <div className="flex-shrink-0 text-right min-w-[120px]">
          {companyData ? (
            <div className="flex flex-col items-end">
              <span className="text-sm font-semibold text-gray-900">
                +
                {currencyFormat(
                  safeImpactAmount(
                    calculateNetImpactSimpleValue(card, companyData).value,
                    { min: 1000, fallback: 1000 }
                  )
                )}
                /yr
              </span>
              <span className="text-xs text-gray-500 mt-0.5">
                {card.MonthlyPrice ? `$${Math.round(card.MonthlyPrice)}/mo` : "$99/mo"}
              </span>
            </div>
          ) : (
            <span
              className="text-sm font-medium text-blue-600 cursor-pointer hover:text-blue-700"
              onClick={(e) => {
                e.stopPropagation();
                setShowAuthModal(true);
              }}
            >
              Join waitlist
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex-shrink-0 flex items-center gap-2">
          <span className="isolate inline-flex rounded-lg shadow-lg">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowRequirementsForm(true);
              }}
              className="relative inline-flex items-center gap-x-1 rounded-l-lg bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-white ring-zinc-300 ring-inset hover:bg-zinc-800 transition-colors duration-150"
            >
              {/* Install */}
              Try
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleOneClickInstall(card);
              }}
              disabled={isOneClickInstalling}
              className="relative -ml-px inline-flex items-center rounded-r-lg bg-zinc-900 px-2 py-1.5 text-xs font-semibold text-white ring-inset hover:bg-zinc-800 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isOneClickInstalling ? (
                <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          </span>
          
          {/* Heart Button - Compact inline version */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleHeartCard(card);
            }}
            className={`p-1.5 rounded-lg transition-colors duration-200 ${
              heartedCards.includes(card.id)
                ? "text-red-500 bg-red-50 hover:bg-red-100"
                : "text-gray-400 bg-gray-50 hover:bg-gray-100"
            }`}
          >
            <HeartIcon 
              className={`w-4 h-4 ${
                heartedCards.includes(card.id) 
                  ? "fill-red-500 text-red-500" 
                  : "fill-none text-gray-400"
              }`} 
            />
          </button>
        </div>
      </div>
    </div>
    );
  };

  // Create a memoized version of the metadata update function
  const testMetadataUpdate = async (user, company) => {
    // Early return if metadata is already set
    if (user?.unsafeMetadata?.airtableId) {
      debugLog("Airtable ID already set in metadata, skipping update");
      return;
    }

    try {
      const userEmail = user?.primaryEmailAddress?.emailAddress;
      const companyDomain = company?.fields?.Domain;

      if (!userEmail || !companyDomain) {
        console.warn("Missing required user email or company domain");
        return;
      }

      // Find existing user record in Airtable
      const response = await fetch(
        `https://api.airtable.com/v0/${baseId}/${usersTable}?filterByFormula={Email}='${encodeURIComponent(
          userEmail
        )}'`,
        {
          headers: {
            Authorization: `Bearer ${apiKey}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      const existingUser = data.records[0];

      if (!existingUser) {
        throw new Error("User record not found in Airtable");
      }

      // Update existing user record with new domain
      const updateResponse = await fetch(
        `https://api.airtable.com/v0/${baseId}/${usersTable}/${existingUser.id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${apiKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fields: {
              Email: userEmail,
              Domain: companyDomain,
            },
          }),
        }
      );

      if (!updateResponse.ok) {
        throw new Error(`HTTP error! Status: ${updateResponse.status}`);
      }

      const updatedData = await updateResponse.json();
      debugLog("Airtable update successful:", updatedData);

      // Update Clerk metadata with Airtable ID
      await user.update({
        unsafeMetadata: {
          airtableId: existingUser.id,
        },
      });
      debugLog("Clerk metadata update successful:", user.unsafeMetadata);
    } catch (error) {
      console.error("Error updating data:", error);
    }
  };

  // Add this useEffect hook in your component
  useEffect(() => {
    // Only run if we have the required data and haven't set the metadata yet
    if (user && company && !user?.unsafeMetadata?.airtableId) {
      testMetadataUpdate(user, company);
    }
  }, [user, company]); // Dependencies array ensures this only runs when user or company changes

  // Add this helper function near other handler functions
  const handleInterestSubmission = async (referralEmail) => {
    if (!user?.primaryEmailAddress?.emailAddress) {
      setNotification({
        showNotif: true,
        contents: {
          type: "error",
          heading: "Error",
          message: "Please sign in to refer an upgrader",
        },
      });
      return;
    }

    if (!referralEmail) {
      setNotification({
        showNotif: true,
        contents: {
          type: "error",
          heading: "Error",
          message: "Please enter an email address",
        },
      });
      return;
    }

    setIsSubmittingInterest(true);
    try {
      const currentEmails = selectedCard?.Upgrader || "";
      const emailList = currentEmails.split(",").map((email) => email.trim());

      // Only add email if it's not already in the list
      if (!emailList.includes(referralEmail)) {
        emailList.push(referralEmail);

        // Update Airtable
        await base("automationsAIGrowthUpgrades").update([
          {
            id: selectedCard.id,
            fields: {
              Upgrader: emailList.join(", "),
            },
          },
        ]);

        setNotification({
          showNotif: true,
          contents: {
            type: "success",
            heading: "Success!",
            message:
              "Successfully referred a new upgrader for this automation.",
          },
        });
      } else {
        setNotification({
          showNotif: true,
          contents: {
            type: "info",
            heading: "Already Registered",
            message: "This email is already on the list for this automation.",
          },
        });
      }
    } catch (error) {
      console.error("Error submitting referral:", error);
      setNotification({
        showNotif: true,
        contents: {
          type: "error",
          heading: "Error",
          message: "Failed to submit referral. Please try again.",
        },
      });
    } finally {
      setIsSubmittingInterest(false);
    }
  };

  // Add this effect to monitor company data changes
  useEffect(() => {
    if (companyData) {
      debugLog("Company data updated:", {
        hasFields: !!companyData.fields,
        availableFields: Object.keys(companyData.fields || {}),
        timestamp: new Date().toISOString(),
      });
    }
  }, [companyData]);

  const handleUpdateField = async (fieldName, value) => {
    if (!companyData?.id) {
      console.error("No company data available");
      setNotification({
        showNotif: true,
        contents: {
          title: "Error",
          message: "Company information not available",
          type: "error",
        },
      });
      return;
    }

    try {
      // Validate value based on field type
      const fieldType = FIELD_TYPES[fieldName];
      let processedValue = value;

      // Handle number fields - process both string and number types
      if (fieldType === "number") {
        // Convert string to number if needed
        if (typeof value === "string") {
          processedValue = Number(value.replace(/[^0-9.]/g, ""));
        } else {
          // Already a number, ensure it's valid
          processedValue = Number(value);
        }
        
        if (isNaN(processedValue)) {
          throw new Error("Invalid number format");
        }
        
        // For integer fields like ListSize, FollowerCount, convert to integer
        // (ListSize, FollowerCount, IncreasedAnnualTraffic should be integers)
        if (fieldName === "ListSize" || 
            fieldName === "FollowerCount" || 
            fieldName === "IncreasedAnnualTraffic") {
          processedValue = Math.round(processedValue);
        }
        
        // Ensure value is not negative for these fields
        if ((fieldName === "ListSize" || 
             fieldName === "FollowerCount" || 
             fieldName === "IncreasedAnnualTraffic") && 
            processedValue < 0) {
          processedValue = 0;
        }
      }

      debugLog(`Updating ${fieldName} to:`, processedValue);

      // Update the company record in Airtable
      await base("companies").update([
        {
          id: companyData.id,
          fields: {
            [fieldName]: processedValue,
          },
        },
      ]);

      // Update local state
      setCompanyData((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          [fieldName]: processedValue,
        },
      }));

      setNotification({
        showNotif: true,
        contents: {
          title: "Success",
          message: "Company information updated successfully",
          type: "success",
        },
      });
    } catch (error) {
      console.error("Error updating company field:", error);
      console.error("Field name:", fieldName, "Value:", value, "Type:", typeof value);
      setNotification({
        showNotif: true,
        contents: {
          title: "Error",
          message:
            error.message === "Invalid number format"
              ? "Please enter a valid number"
              : `Failed to update company information: ${error.message || error}`,
          type: "error",
        },
      });
    }
  };

  // Create a separate memoized component for the note form
  const AddNoteForm = memo(({ onSubmit, onClose }) => {
    const [localNoteContent, setLocalNoteContent] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault(); // Now we have the event object to prevent default
      if (!localNoteContent.trim()) return;

      setIsSubmitting(true);
      try {
        await onSubmit(localNoteContent);
        setLocalNoteContent("");
        onClose();
      } catch (error) {
        console.error("Error submitting note:", error);
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <Dialog.Panel className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl w-full max-w-lg">
        <div className="mb-4">
          <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
            Add Community Note
          </Dialog.Title>
          <p className="mt-2 text-sm text-gray-500">
            Share your experience or insights about this automation to help
            others.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {" "}
          {/* Wrap in a form element */}
          <textarea
            value={localNoteContent}
            onChange={(e) => setLocalNoteContent(e.target.value)}
            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
            rows={4}
            placeholder="Share your thoughts..."
          />
          <div className="mt-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !localNoteContent.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 
                       rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Submitting..." : "Submit Note"}
            </button>
          </div>
        </form>
      </Dialog.Panel>
    );
  });

  AddNoteForm.displayName = "AddNoteForm";

  // Add this new useEffect to load viewed guides from Airtable when component mounts

  useEffect(() => {
    const loadViewedGuides = async () => {
      if (!user?.primaryEmailAddress?.emailAddress) return;

      try {
        const response = await base("guideViews")
          .select({
            filterByFormula: `{userEmail} = '${user.primaryEmailAddress.emailAddress}'`,
          })
          .firstPage();

        if (response && response[0]) {
          // Parse viewedGuides from the response, handling both array and string formats
          let viewedGuideIds = response[0].fields.viewedGuides;

          // If it's a string, split it into an array
          if (typeof viewedGuideIds === "string") {
            viewedGuideIds = viewedGuideIds.split(",").map((id) => id.trim());
          }

          // Ensure we have an array, even if empty
          viewedGuideIds = Array.isArray(viewedGuideIds) ? viewedGuideIds : [];

          // Set the state with the parsed array
          setViewedGuides(viewedGuideIds);
          setRemainingGuides(
            Math.max(0, MAX_FREE_GUIDES - viewedGuideIds.length)
          );

          // Debug logging
          debugLog("Loaded viewed guides:", {
            raw: response[0].fields.viewedGuides,
            parsed: viewedGuideIds,
            remaining: Math.max(0, MAX_FREE_GUIDES - viewedGuideIds.length),
          });
        }
      } catch (error) {
        console.error("Error loading viewed guides:", error);
      }
    };

    loadViewedGuides();
  }, [user]);

  // Add this function to track when a guide is viewed
  const trackGuideView = async (cardId) => {
    if (!user?.primaryEmailAddress?.emailAddress) return;

    debugLog("viewedGuides", viewedGuides);

    // Parse viewedGuides string into array, handling empty string case
    const currentGuides = Array.isArray(viewedGuides)
      ? viewedGuides
      : viewedGuides
        ? viewedGuides.split(",").map((g) => g.trim())
        : [];

    // If user has already viewed this guide, don't count it again
    if (currentGuides.includes(cardId)) return;

    try {
      // Get existing record or create new one
      const response = await base("guideViews")
        .select({
          filterByFormula: `{userEmail} = '${user.primaryEmailAddress.emailAddress}'`,
        })
        .firstPage();

      const updatedGuides = [...currentGuides, cardId];
      const updatedGuidesString = updatedGuides.join(",");

      if (response && response[0]) {
        // Update existing record
        await base("guideViews").update([
          {
            id: response[0].id,
            fields: {
              userEmail: user.primaryEmailAddress.emailAddress,
              viewedGuides: updatedGuidesString,
            },
          },
        ]);
      } else {
        // Create new record
        await base("guideViews").create([
          {
            fields: {
              userEmail: user.primaryEmailAddress.emailAddress,
              viewedGuides: updatedGuidesString,
            },
          },
        ]);
      }

      setViewedGuides(updatedGuidesString);
      setRemainingGuides(Math.max(0, MAX_FREE_GUIDES - updatedGuides.length));
    } catch (error) {
      console.error("Error tracking guide view:", error);
    }

    // After updating viewedGuides and remainingGuides in state
    if (companyRecord) {
      try {
        // Update the RemainingGuides field in Airtable
        await base("companies").update(companyRecord.id, {
          RemainingGuides: remainingGuides,
        });
      } catch (error) {
        console.error("Error updating remaining guides in Airtable:", error);
      }
    }
  };

  // Add this useEffect to fetch the user record from Airtable
  useEffect(() => {
    const fetchUserRecord = async () => {
      if (!isLoaded || !isSignedIn || !user) {
        setIsLoadingUserRecord(false);
        return;
      }

      try {
        setIsLoadingUserRecord(true);
        const userEmail = user.primaryEmailAddress.emailAddress;

        // Use your existing Airtable base
        const records = await base(usersTable)
          .select({
            filterByFormula: `{Email} = '${userEmail}'`,
            maxRecords: 1,
          })
          .all();

        if (records && records.length > 0) {
          const record = records[0];
          setUserRecord(record);
          setUserPaidStatus(record.fields.Paid || "unpaid");

          // If you want to store remainingGuides in Airtable, get it from the record
          if (record.fields.RemainingGuides !== undefined) {
            setRemainingGuides(record.fields.RemainingGuides);
          }

          // Load subscribed automations from UpgraderAutomations field
          if (record.fields.UpgraderAutomations) {
            try {
              const subscriptions = JSON.parse(record.fields.UpgraderAutomations);
              setSubscribedAutomations(Array.isArray(subscriptions) ? subscriptions : []);
            } catch (e) {
              console.error("Error parsing UpgraderAutomations:", e);
              setSubscribedAutomations([]);
            }
          } else {
            setSubscribedAutomations([]);
          }
        }
      } catch (error) {
        console.error("Error fetching user record:", error);
      } finally {
        setIsLoadingUserRecord(false);
      }
    };

    fetchUserRecord();
  }, [isLoaded, isSignedIn, user]);

  // Only load cards when on dedicated automation pages (not when embedded in home App)
  const isAutomationPage = location.pathname === '/ai-automations' || location.pathname === '/automate-my-work';

  useEffect(() => {
    if (!isAutomationPage) return;

    const loadAllCards = async () => {
      try {
        const cards = await getAutomationCards();
        if (cards && cards.length > 0) {
          const parsedCards = cards.map((card) => ({
            ...card,
            requirements:
              typeof card.requirements === "string"
                ? JSON.parse(card.requirements)
                : card.requirements || {},
            fields: {
              ...card.fields,
              requirements:
                typeof card.fields?.requirements === "string"
                  ? JSON.parse(card.fields.requirements)
                  : card.fields?.requirements || {},
            },
          }));

          const sortedByFavoritesAndPopularity =
            sortCardsByFavoritesAndPopularity(parsedCards);

          if (setAllCards) {
            setAllCards(sortedByFavoritesAndPopularity);
          }
          setFilteredCards(sortedByFavoritesAndPopularity);
          if (debugAutomation) debugLog(
            `Loaded ${sortedByFavoritesAndPopularity.length} cards directly`
          );
        }
      } catch (error) {
        console.error("Error directly loading cards:", error);
      }
    };

    loadAllCards();
  }, [isAutomationPage]);

  const { loading, hasMore } = useInfiniteScroll(loadMoreCards);

  // Add this component near your other card components
  const VideoCard = () => (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 hover:shadow-md transition-shadow duration-200">
      <div className="p-4">
        <h3 className="text-lg font-medium text-gray-900">
          See Automations in Action
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Watch how these automations can transform your workflow
        </p>
      </div>
      <div className="aspect-video w-full">
        <iframe
          className="w-full h-full"
          src="https://www.loom.com/embed/9c9c062c1ca54f97899b69266bf700cf?sid=13e0dde0-22cc-4459-bda6-7db7afb62626"
          frameBorder="0"
          allowFullScreen
        ></iframe>
      </div>
    </div>
  );

  // Helper function to generate intelligent fallback values based on company data
  const generateFallbackValue = (fieldName, companyData) => {
    const companyName = companyData.fields?.Name || "Company";
    const industry = companyData.fields?.Industry || "Business";
    const revenue = companyData.fields?.AnnualRevenue || 0;
    const employees = companyData.fields?.EmployeeCount || 0;
    const domain = companyData.fields?.Domain || "company.com";
    const description = companyData.fields?.Description || "";

    // Generate intelligent fallbacks based on field name and company data
    const fieldNameLower = fieldName.toLowerCase();

    // For Upgraded company specifically (based on debug data)
    if (companyName === "Upgraded" && industry === "Technology") {
      if (
        fieldNameLower.includes("content") &&
        fieldNameLower.includes("theme")
      ) {
        return "Tax credit opportunities, Grant applications, Technology savings, Startup funding, R&D incentives";
      }

      if (
        fieldNameLower.includes("brand") &&
        fieldNameLower.includes("voice")
      ) {
        return "Professional yet approachable, Expert guidance, Trustworthy advisor, Clear and concise, Results-focused";
      }

      if (
        fieldNameLower.includes("posting") &&
        fieldNameLower.includes("schedule")
      ) {
        return "Monday, Wednesday, Friday at 9 AM EST - Tax tips and grant updates";
      }

      if (
        fieldNameLower.includes("competitor") &&
        fieldNameLower.includes("account")
      ) {
        return "Deloitte Tax, PwC Tax Services, EY Tax, KPMG Tax, Grant Thornton";
      }

      if (
        fieldNameLower.includes("social") &&
        fieldNameLower.includes("credential")
      ) {
        return "Admin access to LinkedIn, Twitter, and Facebook accounts for Upgraded";
      }

      if (
        fieldNameLower.includes("buffer") ||
        fieldNameLower.includes("access")
      ) {
        return "Buffer account access for scheduling posts across platforms";
      }
    }

    // Generic fallbacks for other companies
    if (
      fieldNameLower.includes("email") ||
      fieldNameLower.includes("contact")
    ) {
      return `contact@${domain}`;
    }

    if (fieldNameLower.includes("name") || fieldNameLower.includes("title")) {
      return `${companyName} ${fieldName}`;
    }

    if (
      fieldNameLower.includes("url") ||
      fieldNameLower.includes("website") ||
      fieldNameLower.includes("link")
    ) {
      return `https://${domain}`;
    }

    if (fieldNameLower.includes("api") || fieldNameLower.includes("key")) {
      return `${companyName.toLowerCase().replace(/\s+/g, "_")}_api_key`;
    }

    if (
      fieldNameLower.includes("list") ||
      fieldNameLower.includes("items") ||
      fieldNameLower.includes("products")
    ) {
      return `${industry} Product 1, ${industry} Product 2, ${industry} Service`;
    }

    if (
      fieldNameLower.includes("number") ||
      fieldNameLower.includes("count") ||
      fieldNameLower.includes("amount")
    ) {
      if (
        fieldNameLower.includes("revenue") ||
        fieldNameLower.includes("budget")
      ) {
        return revenue > 0 ? revenue.toString() : "50000";
      }
      if (
        fieldNameLower.includes("employee") ||
        fieldNameLower.includes("staff")
      ) {
        return employees > 0 ? employees.toString() : "10";
      }
      return "100";
    }

    if (
      fieldNameLower.includes("date") ||
      fieldNameLower.includes("deadline")
    ) {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 30);
      return futureDate.toISOString().split("T")[0];
    }

    if (
      fieldNameLower.includes("description") ||
      fieldNameLower.includes("notes")
    ) {
      return `${companyName} ${fieldName} for ${industry} automation`;
    }

    // Default fallback
    return `${companyName} ${fieldName}`;
  };

  const handleAutoFillClientRequirements = async (requirements) => {
    if (!companyData) {
      alert("Company information is required for auto-fill.");
      return;
    }

    setIsSubmitting(true);
    const newFormData = { ...formData };
    const newDebugInfo = {};

    for (const req of requirements.fromClient) {
      let value = "";
      let responseText = "";

      // Create a more detailed prompt with company context
      const companyContext = `
Company Name: ${companyData.fields?.Name || "Unknown"}
Industry: ${companyData.fields?.Industry || "Unknown"}
Annual Revenue: ${companyData.fields?.AnnualRevenue ? `$${companyData.fields.AnnualRevenue.toLocaleString()}` : "Unknown"}
Employee Count: ${companyData.fields?.EmployeeCount || "Unknown"}
Domain: ${companyData.fields?.Domain || "Unknown"}
Description: ${companyData.fields?.Description || "No description available"}
      `.trim();

      // Create a more specific prompt based on the actual field information
      const fieldQuestion = req.example || req.description || req.name;
      const fieldType = req.example?.toLowerCase().includes("list")
        ? "list"
        : req.example?.toLowerCase().includes("email")
          ? "email"
          : req.example?.toLowerCase().includes("url")
            ? "url"
            : req.example?.toLowerCase().includes("schedule")
              ? "schedule"
              : req.example?.toLowerCase().includes("account")
                ? "account"
                : req.example?.toLowerCase().includes("credential")
                  ? "credential"
                  : "text";

      const prompt = `
You are a business analyst helping to fill out automation requirements for a company.

Company Information:
${companyContext}

Field Question: "${fieldQuestion}"
Field Type: ${fieldType}
Field Name: "${req.name}"

CRITICAL INSTRUCTIONS:
1. Analyze the company data above
2. Generate a realistic, company-specific answer to: "${fieldQuestion}"
3. Base your answer on the company's industry (${companyData.fields?.Industry || "Technology"}), size, and business model
4. Do NOT repeat, paraphrase, or reference the example text
5. Do NOT use generic words like "Upgraded", "Enhanced", "Improved", "Better", "Advanced"
6. Do NOT use placeholder text or template language
7. Provide a concrete, actionable value that makes sense for this specific company
8. If this is asking for a list, provide 3-5 relevant items specific to this company
9. If this is asking for a number, provide a realistic estimate based on company size
10. If this is asking for text, be specific and relevant to the company's actual business

FORBIDDEN WORDS: "Upgraded", "Enhanced", "Improved", "Better", "Advanced", "Example", "Sample", "Template", "Placeholder"

Return only the value, no explanations or quotes.
      `.trim();

      try {
        // Call Next.js API route (server-side) instead of OpenAI directly
        const response = await fetch('/api/generateAutomationField', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prompt,
            fieldName: req.name,
            model: 'gpt-4o',
            maxTokens: 150,
            temperature: 0.4
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.details || errorData.error || `HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        if (!result.success || !result.content) {
          throw new Error(result.error || 'No response from automation field generation');
        }

        responseText = result.content;

        // Validate the response
        const forbiddenWords = [
          "upgraded",
          "enhanced",
          "improved",
          "better",
          "advanced",
          "example",
          "sample",
          "template",
          "placeholder",
        ];
        const hasForbiddenWords = forbiddenWords.some((word) =>
          responseText.toLowerCase().includes(word)
        );

        const isValidResponse =
          responseText &&
          responseText.length > 2 &&
          !responseText.toLowerCase().includes(req.example.toLowerCase()) &&
          !responseText.toLowerCase().includes("example") &&
          !responseText.toLowerCase().includes("cannot") &&
          !responseText.toLowerCase().includes("unable") &&
          !responseText.toLowerCase().includes("i don't") &&
          !responseText.toLowerCase().includes("i do not") &&
          !hasForbiddenWords;

        if (!isValidResponse) {
          // Try with a more aggressive prompt
          const aggressivePrompt = `
Company: ${companyData.fields?.Name || "Unknown"} (${companyData.fields?.Industry || "Unknown"})
Revenue: ${companyData.fields?.AnnualRevenue ? `$${companyData.fields.AnnualRevenue.toLocaleString()}` : "Unknown"}
Employees: ${companyData.fields?.EmployeeCount || "Unknown"}

Generate a specific answer to: "${fieldQuestion}"

CRITICAL REQUIREMENTS:
- Must be specific to this company
- Must be actionable and realistic
- Must NOT be the example: "${req.example}"
- Must NOT be generic or placeholder text
- Must NOT use words like "Upgraded", "Enhanced", "Improved", "Better", "Advanced"
- Must be based on the company's actual data and industry
- Must provide concrete, specific information

FORBIDDEN: "Upgraded", "Enhanced", "Improved", "Better", "Advanced", "Example", "Sample", "Template", "Placeholder"

If this is asking for a list, provide 3-5 specific items relevant to this company.
If this is asking for a number, provide a realistic estimate based on company size.
If this is asking for text, provide specific, relevant information about this company's business.

Answer with ONLY the value, no explanations.
          `.trim();

          // Retry with aggressive prompt via API route
          const retryResponse = await fetch('/api/generateAutomationField', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              prompt: aggressivePrompt,
              fieldName: req.name,
              model: 'gpt-4o',
              maxTokens: 200,
              temperature: 0.5
            })
          });

          if (retryResponse.ok) {
            const retryResult = await retryResponse.json();
            if (retryResult.success && retryResult.content) {
              responseText = retryResult.content;
            }
          }
        }

        // Final validation - if still not good, generate a basic but specific answer
        if (
          !responseText ||
          responseText.toLowerCase().includes(req.example.toLowerCase()) ||
          responseText.toLowerCase().includes("example") ||
          responseText.length < 3
        ) {
          // Generate a fallback based on company data
          const fallbackValue = generateFallbackValue(req.name, companyData);
          value = fallbackValue;
        } else {
          value = responseText;
        }

        newFormData[`client-${req.name}`] = value;
        newDebugInfo[req.name] = {
          prompt,
          response: responseText,
          value,
          companyContext,
        };
      } catch (err) {
        console.error("OpenAI error for", req.name, err);
        // Generate fallback value instead of using example
        const fallbackValue = generateFallbackValue(req.name, companyData);
        newFormData[`client-${req.name}`] = fallbackValue;
        newDebugInfo[req.name] = {
          prompt,
          error: err.message,
          value: fallbackValue,
        };
      }
    }

    setFormData(newFormData);
    setNewDebugInfo(newDebugInfo);
    setIsSubmitting(false);
  };

  // Ensure prioritizedIds is set from company record
  useEffect(() => {
    if (companyRecord && companyRecord.fields?.PrioritizedAutomationIds) {
      let ids = companyRecord.fields.PrioritizedAutomationIds;
      if (typeof ids === "string") {
        try {
          ids = JSON.parse(ids);
        } catch {
          ids = [];
        }
      }
      if (setPrioritizedIds) {
        setPrioritizedIds(Array.isArray(ids) ? ids : []);
      }
    }
  }, [companyRecord]);

  // debugLog("Prioritized IDs:", prioritizedIds);
  // debugLog("My List Cards:", getFilteredCards());

  const myListCards = prioritizedIds
    ? prioritizedIds
        .map((id) => allCards.find((card) => card.id === id))
        .filter(Boolean)
    : [];

  // Removed console.log to prevent infinite re-renders

  const handleMoveToPosition = async (cardId, newPosition) => {
    const currentIndex = prioritizedIds.indexOf(cardId);
    if (currentIndex === -1) return;

    const newPrioritizedIds = [...prioritizedIds];
    newPrioritizedIds.splice(currentIndex, 1); // Remove from current position
    newPrioritizedIds.splice(newPosition - 1, 0, cardId); // Insert at new position

    if (setPrioritizedIds) {
      setPrioritizedIds(newPrioritizedIds);
    }
    if (companyRecord) {
      await updateCompanyPrioritizedIds(companyRecord.id, newPrioritizedIds);
    }
  };

  // Refresh automation data after edit
  const handleRefreshAutomations = async () => {
    try {
      const cards = await getAutomationCards();
      const parsedCards = cards.map((card) => ({
        ...card,
        slug: createSlug(card.title),
        requirements:
          typeof card.requirements === "string"
            ? JSON.parse(card.requirements)
            : card.requirements || {},
        fields: {
          ...card.fields,
          requirements:
            typeof card.fields?.requirements === "string"
              ? JSON.parse(card.fields.requirements)
              : card.fields?.requirements || {},
        },
      }));

      const sortedByFavoritesAndPopularity = sortCardsByFavoritesAndPopularity(parsedCards);
      if (setAllCards) {
        setAllCards(sortedByFavoritesAndPopularity);
      }
      setFilteredCards(sortedByFavoritesAndPopularity);
    } catch (error) {
      console.error("Error refreshing automations:", error);
    }
  };

  // Add this with the other useState hooks near the top of AutomationDirectory:
  const [hoveredVideoCardId, setHoveredVideoCardId] = useState(null);

  return (
    <UsersProvider>
      <div className="min-h-screen py-4 relative">
        {/* Hero Section - New Design */}
        <div className="mx-auto text-center mb-2">
          <h1
            className={`text-[56px] font-extrabold tracking-tight mb-6 group transition-colors duration-300 text-center w-full ${
              isDarkMode
                ? "text-white"
                : "text-[#0A0A0B]"
            }`}
            style={{ fontFamily: "Recoleta, 'Crimson Pro', serif" }}
          >
            <div className="inline-block" style={{ whiteSpace: "nowrap", position: "relative" }}>
              <span className="group-hover:hidden">
                <span style={{ display: "inline-block", marginRight: "8px" }}>
                  {filters?.industries?.[0]
                    ? (
                        <>
                          AI Your Entire {filters.industries[0]} Business
                          {", "}
                        </>
                      )
                    : (
                        <>
                          AI Your Entire Business{", "}
                        </>
                      )
                  }
                </span>
                <span style={{ 
                  display: "inline-block", 
                  width: "300px", 
                  textAlign: "left",
                  verticalAlign: "baseline"
                }}>
                  <Typed
                    strings={[
                      "Done-For-You",
                      "Guaranteed",
                      "Whitelabelable",
                      "Referralable",
                    ]}
                    typeSpeed={40}
                    backSpeed={50}
                    loop={true}
                    backDelay={1000}
                    showCursor={true}
                    cursorChar="|"
                    smartBackspace={true}
                  />
                </span>
              </span>
              <span className="hidden group-hover:inline-block">
                {filters?.industries?.[0]
                  ? `${filters.industries[0]} AI App Store for Work`
                  : "AI App Store for Work"}
              </span>
            </div>
          </h1>

          <p
            className={`text-xl max-w-7xl mx-auto mb-10 transition-colors duration-300 md:text-2xl ${isDarkMode ? "text-white drop-shadow-lg" : "text-gray-900"}`}
            style={{ fontFamily: "Recoleta, 'Crimson Pro', serif" }}
          >
            <strong>Done-for-you, money back guarantee, whitelabelable</strong>{" "}
            AI automations that save time, reduce costs, sell & earn
            more.
          </p>

          {/* Search Bar - New Design */}
          <div 
            className="max-w-4xl mx-auto relative mb-10 flex items-center gap-4"
            onMouseEnter={() => setIsHoveringSearchArea(true)}
            onMouseLeave={() => setIsHoveringSearchArea(false)}
          >
            <div className="flex-1 p-[3px] rounded-2xl bg-gradient-to-r from-[#FFB82C] via-[#FF6B2C] via-[#CD4899] to-[#8B5CF6] shadow-lg">
              <div className="relative flex items-center bg-white rounded-2xl h-[72px]">
                <button
                  className={`group absolute top-4 left-3 z-10 p-2 -ml-2 rounded-lg transition-all cursor-pointer ${
                    viewMode === 'list' 
                      ? 'hover:scale-110' 
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <div 
                    className={`inline-block mt-1 sparkles-gradient-wrapper ${
                      viewMode === 'list' && shouldRunAnimation
                        ? 'animate-thinking'
                        : ''
                    }`}
                  >
                    <SparklesIcon className="font-light h-5 w-5 transition-all" />
                  </div>
                  {viewMode === 'list' && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                      Hover over search to pause animation
                      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  )}
                </button>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full h-full pl-12 pr-32 text-sm text-gray-400 placeholder-gray-400 bg-transparent border-0 focus:ring-0 focus:outline-none search-section"
                  placeholder="What would you like to automate?"
                />
                <span className="absolute right-6 text-sm text-gray-400 select-none">
                  {filteredCards.length} AI automations & upgrades
                </span>
              </div>
            </div>

            {/* Combined Toggles */}
            <div className="flex-shrink-0 flex items-center gap-3">
              {/* Golden Layout Toggle */}
              <div className="flex flex-col items-center gap-1">
                <div className="p-[3px] rounded-2xl bg-gradient-to-r from-[#FFB82C] via-[#FF6B2C] via-[#CD4899] to-[#8B5CF6] shadow-lg">
                  <div className="bg-white rounded-2xl px-4 py-3 flex items-center">
                    <button
                      type="button"
                      onClick={() => {
                        setIsFocusedLayout((prev) => !prev);
                        // Clear list click-through animation so a new value order is visible immediately
                        resetAnimation();
                      }}
                      className="flex items-center justify-center transition-all duration-200 hover:scale-105"
                    >
                      <div
                        className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                          isFocusedLayout
                            ? "bg-gradient-to-r from-[#FFB82C] to-[#FF6B2C]"
                            : "bg-gray-200"
                        }`}
                      >
                        <div
                          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200 ${
                            isFocusedLayout ? "translate-x-6" : "translate-x-0"
                          }`}
                        />
                      </div>
                    </button>
                  </div>
                </div>
                <span
                  className={`text-xs font-semibold transition-colors duration-200 ${
                    isFocusedLayout ? "text-[#FFB82C]" : "text-gray-900"
                  }`}
                >
                  {isFocusedLayout ? "Focused" : "Relaxed"}
                </span>
              </div>

              {/* View Mode Toggle */}
              <div 
                className="flex flex-col items-center gap-1"
                onMouseEnter={() => setIsHoveringViewToggle(true)}
                onMouseLeave={() => setIsHoveringViewToggle(false)}
              >
                <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#FFB82C] via-[#FF6B2C] via-[#CD4899] to-[#8B5CF6] shadow-md">
                  <div className="bg-gray-100 rounded-xl px-1 py-2 flex items-center relative">
                    {/* Sliding Background Pill */}
                    <div
                      className={`absolute top-1 bottom-1 left-1 right-1 rounded-lg bg-white border border-gray-200 shadow-sm transition-transform duration-200 ${
                        viewMode === "card" ? "translate-x-0" : "translate-x-full"
                      }`}
                      style={{
                        width: "calc(50% - 4px)",
                      }}
                    />

                    {/* Card View Option */}
                    <button
                      onClick={() => setViewMode("card")}
                      className={`relative z-10 flex items-center justify-center px-3 py-1.5 rounded-lg transition-colors duration-200 flex-1 ${
                        viewMode === "card"
                          ? "text-gray-900"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <Squares2X2Icon className="h-5 w-5" />
                    </button>

                    {/* List View Option */}
                    <button
                      onClick={() => setViewMode("list")}
                      className={`relative z-10 flex items-center justify-center px-3 py-1.5 rounded-lg transition-colors duration-200 flex-1 ${
                        viewMode === "list"
                          ? "text-gray-900"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <Bars3Icon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <span className="text-xs font-medium text-gray-900">
                  {viewMode === "card" ? "Card" : "List"}
                </span>
              </div>
            </div>
          </div>

          {/* Move the stage filters to a conditional render */}
        </div>
        {/* Add a debug panel */}
        {debug.searchQuery && (
          <div className="fixed bottom-4 right-4 bg-white p-4 rounded-lg shadow-lg text-sm z-50">
            <p>Total Cards: {debug.totalCards}</p>
            <p>Search Query: "{debug.searchQuery}"</p>
            <p>Exact Matches: {debug.exactMatches}</p>
            <p>Fuzzy Matches: {debug.fuzzyMatches}</p>
            <p>Displayed Results: {filteredCards.length}</p>
          </div>
        )}
        {/* Automation Cards Grid or List - New Design */}
        {viewMode === "card" ? (
          <div
            className={`grid gap-6 ${
              isFocusedLayout
                ? "grid-cols-1 max-w-md mx-auto"
                : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
            }`}
          >
            {!searchQuery.trim() ? (
              // When no search query, calculate cards to show based on current state
              (() => {
                if (showPrioritizedList) {
                  const prioritizedCards = getPrioritizedCards(prioritizedIds, allCards);
                  if (prioritizedCards.length === 0) {
                    // Show a message when no cards are in the queue
                    return (
                      <div className="col-span-full text-center py-12">
                        <div className="max-w-md mx-auto">
                          <div className="text-6xl mb-4">📋</div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            Your Queue is Empty
                          </h3>
                          <p className="text-gray-600 mb-4">
                            Add automations to your queue by clicking the "Add to Queue" button on any automation card.
                          </p>
                          <button
                            onClick={() => setShowPrioritizedList(false)}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            Browse All Automations
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return prioritizedCards.map((card) =>
                    renderAutomationCard(
                      card,
                      "exact",
                      user?.unsafeMetadata?.tier || "Basic",
                      isAdmin
                    )
                  );
                } else {
                  // Use displayedCards which is properly sorted by the main sorting effect
                  // displayedCards already contains the correctly sorted and filtered cards
                  return displayedCards.map((card) =>
                    renderAutomationCard(
                      card,
                      "exact",
                      user?.unsafeMetadata?.tier || "Basic",
                      isAdmin
                    )
                  );
                }
              })()
            ) : (
              <>
                {exactResults.length > 0 && (
                  <>
                    <div className="col-span-full text-sm text-gray-500 mb-2">
                      Exact Matches ({exactResults.length})
                    </div>
                    {exactResults.map((card) =>
                      renderAutomationCard(
                        card,
                        "exact",
                        user?.unsafeMetadata?.tier || "Basic",
                        isAdmin
                      )
                    )}
                  </>
                )}

                {fuzzyResults.length > 0 && (
                  <>
                    <div className="col-span-full text-sm text-gray-500 mb-2 mt-6">
                      Similar Matches ({fuzzyResults.length})
                    </div>
                    {fuzzyResults.map((card) =>
                      renderAutomationCard(
                        card,
                        "fuzzy",
                        user?.unsafeMetadata?.tier || "Basic",
                        isAdmin
                      )
                    )}
                  </>
                )}

                {exactResults.length === 0 && fuzzyResults.length === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    No results found for "{searchQuery}"
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="max-w-4xl mx-auto space-y-2 relative" style={{ minHeight: '100px' }}>
            {!searchQuery.trim() ? (
              // When no search query, calculate cards to show based on current state
              (() => {
                if (showPrioritizedList) {
                  const prioritizedCards = getPrioritizedCards(prioritizedIds, allCards);
                  if (prioritizedCards.length === 0) {
                    // Show a message when no cards are in the queue
                    return (
                      <div className="text-center py-12">
                        <div className="max-w-md mx-auto">
                          <div className="text-6xl mb-4">📋</div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            Your Queue is Empty
                          </h3>
                          <p className="text-gray-600 mb-4">
                            Add automations to your queue by clicking the "Add to Queue" button on any automation card.
                          </p>
                          <button
                            onClick={() => setShowPrioritizedList(false)}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            Browse All Automations
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return prioritizedCards.map((card, index) =>
                    renderAutomationListItem(
                      card,
                      "exact",
                      user?.unsafeMetadata?.tier || "Basic",
                      isAdmin,
                      index,
                      animatingCardIndex,
                      isAnimating && shouldRunAnimation,
                      completedCardIndices
                    )
                  );
                } else {
                  // Use displayedCards which is properly sorted by the main sorting effect
                  return displayedCards.map((card, index) =>
                    renderAutomationListItem(
                      card,
                      "exact",
                      user?.unsafeMetadata?.tier || "Basic",
                      isAdmin,
                      index,
                      animatingCardIndex,
                      isAnimating && shouldRunAnimation,
                      completedCardIndices
                    )
                  );
                }
              })()
            ) : (
              <>
                {exactResults.length > 0 && (
                  <>
                    <div className="text-sm text-gray-500 mb-3 px-2">
                      Exact Matches ({exactResults.length})
                    </div>
                    {exactResults.map((card, index) =>
                      renderAutomationListItem(
                        card,
                        "exact",
                        user?.unsafeMetadata?.tier || "Basic",
                        isAdmin,
                        index,
                        animatingCardIndex,
                        isAnimating && shouldRunAnimation,
                        completedCardIndices
                      )
                    )}
                  </>
                )}

                {fuzzyResults.length > 0 && (
                  <>
                    <div className="text-sm text-gray-500 mb-3 mt-6 px-2">
                      Similar Matches ({fuzzyResults.length})
                    </div>
                    {fuzzyResults.map((card, index) =>
                      renderAutomationListItem(
                        card,
                        "fuzzy",
                        user?.unsafeMetadata?.tier || "Basic",
                        isAdmin,
                        exactResults.length + index,
                        animatingCardIndex,
                        isAnimating && shouldRunAnimation,
                        completedCardIndices
                      )
                    )}
                  </>
                )}

                {exactResults.length === 0 && fuzzyResults.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No results found for "{searchQuery}"
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Loading indicator for infinite scroll - only show when not searching and not in prioritized list */}
        {!searchQuery.trim() && !showPrioritizedList && isLoadingMore && (
          <div className="col-span-full flex justify-center my-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* "Load More" button as fallback - only show when not searching and not in prioritized list */}
        {!searchQuery.trim() && !showPrioritizedList && !isLoadingMore && hasMoreCards && (
          <div className="col-span-full flex justify-center my-8">
            <button
              onClick={loadMoreCards}
              disabled={isLoadingMore}
              className="mt-8 max-w-xs w-full py-3 px-4 rounded-lg bg-gray-900 text-white font-medium transition-all duration-200 shadow-[0_4px_12px_rgba(0,0,0,0.2)] hover:shadow-[0_6px_16px_rgba(0,0,0,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingMore ? "Loading..." : "Load More"}
            </button>
          </div>
        )}
        {/* Modal for detailed information - buy now modal */}
        <Transition.Root show={isModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={handleModalClose}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity big-modal" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 overflow-y-auto big-modal">
              <div
                className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0 big-modal"
                ref={(node) => {
                  // When the modal opens, scroll to the top
                  if (node) {
                    node.scrollTop = 0;
                  }
                }}
              >
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel
                    className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-4xl"
                    ref={(node) => {
                      // When the panel mounts, scroll to the top
                      if (node) {
                        node.scrollTop = 0;
                      }
                    }}
                  >
                    {/* Header Section with Large Title and Gradient Accent */}
                    <div className="relative">
                      {/* Subtle gradient accent bar */}
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-500 to-purple-600" />

                      <div className="px-8 pt-12 pb-2">
                        {/* Centered Title - Apple-style */}
                        <Dialog.Title as="div" className="text-center mb-6">
                          <h3 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 whitespace-pre-line">
                            {formatTitle(selectedCard?.title)}
                          </h3>
                        </Dialog.Title>
                      </div>
                    </div>
                    {/* Main Content */}
                    <div className="px-8">
                      {/* Move the Guide Image section here, right at the top of the content */}
                      {showGuideImage && selectedCard?.Image && (
                        <div className="mb-5">
                          {/* Enhanced guide counter banner */}
                          <div className="mb-4 p-6 bg-gradient-to-r from-purple-50 to-orange-50 rounded-xl border border-purple-100 shadow-md">
                            <div className="flex justify-between items-center">
                              {/* Left side with guide counter */}
                              <div>
                                <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-orange-500 bg-clip-text text-transparent">
                                  {remainingGuides} of {MAX_FREE_GUIDES} Free
                                  Guides Left
                                </span>
                                <p className="text-gray-600 mt-1">
                                  Explore our AI, automation & marketing guides
                                </p>
                              </div>

                              {/* Right side with CTA */}
                              <div className="flex items-center gap-4">
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    Get access to +350 guides
                                  </h3>
                                  <p className="text-gray-600">
                                    $99/yr - Limited Time Offer
                                  </p>
                                </div>
                                <button
                                  onClick={() =>
                                    window.open(
                                      "https://buy.stripe.com/dR6g2I5Jw2yo3zGeVm",
                                      "_blank"
                                    )
                                  }
                                  className="flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg bg-gray-800 text-white hover:bg-gray-900 transition-colors shadow-lg"
                                >
                                  Get Access Now
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Add the accordion/dropdown button and conditionally render the guide */}
                          <div className="mb-4">
                            {/* Accordion Button */}
                            <button
                              onClick={() => setIsGuideVisible((prev) => !prev)}
                              className="flex items-center justify-center w-full px-6 py-2 text-sm font-medium text-gray-900 bg-gray-100 rounded-lg hover:bg-gray-200"
                              style={{ border: "none", marginBottom: "1rem" }} // Removes black border and adds space below the button
                            >
                              <svg
                                className={`w-4 h-4 transform transition-transform ${
                                  isGuideVisible ? "rotate-180" : ""
                                }`}
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>

                            {/* Conditionally render either blurred or clear image */}
                            {isGuideVisible && selectedCard && (
                              <>
                                {/* Edit GuideText Button */}
                                {isAdmin && ( // Can only edit if user is admin
                                  <button
                                    onClick={() => {
                                      setIsEditingGuideText(true);
                                      setEditedGuideText(
                                        selectedCard.GuideText || ""
                                      );
                                    }}
                                    className="mt-2 mb-4 px-3 py-1 text-xs font-medium text-black bg-gray-200 rounded-md hover:bg-gray-300"
                                  >
                                    Edit GuideText
                                  </button>
                                )}

                                {debugLog("User record:", userRecord)}
                                {debugLog(
                                  "User paid status:",
                                  userPaidStatus
                                )}

                                {isEditingGuideText ? (
                                  <div className="mt-4">
                                    <textarea
                                      value={editedGuideText}
                                      onChange={(e) =>
                                        setEditedGuideText(e.target.value)
                                      }
                                      className="w-full h-40 p-2 border border-gray-300 rounded-lg"
                                    />
                                    <div className="flex justify-end mt-2 space-x-2">
                                      <button
                                        onClick={() =>
                                          setIsEditingGuideText(false)
                                        }
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={async () => {
                                          try {
                                            // Save to Airtable
                                            await base(
                                              "automationsAIGrowthUpgrades"
                                            ).update([
                                              {
                                                id: selectedCard.id,
                                                fields: {
                                                  GuideText: editedGuideText,
                                                },
                                              },
                                            ]);

                                            // Update local state
                                            setSelectedCard((prev) => ({
                                              ...prev,
                                              GuideText: editedGuideText,
                                            }));
                                            setIsEditingGuideText(false);
                                          } catch (error) {
                                            console.error(
                                              "Error saving GuideText:",
                                              error
                                            );
                                            alert(
                                              "Failed to save GuideText. Please try again."
                                            );
                                          }
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                                      >
                                        Save
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    {selectedCard.GuideText && (
                                      // Render GuideText if it exists
                                      <div
                                        className={`relative ${
                                          remainingGuides === 0 &&
                                          !(
                                            user?.unsafeMetadata?.tier ===
                                              "Guide" ||
                                            user?.unsafeMetadata?.tier ===
                                              "Premium" ||
                                            userPaidStatus === "tech" ||
                                            userPaidStatus === "basic" ||
                                            isAdmin
                                          )
                                            ? "blur-sm"
                                            : ""
                                        } rounded-xl overflow-hidden bg-gray-100 mb-4`}
                                      >
                                        <div
                                          dangerouslySetInnerHTML={{
                                            __html: selectedCard.GuideText,
                                          }}
                                          className="p-4"
                                        />
                                        {remainingGuides === 0 &&
                                          !(
                                            user?.unsafeMetadata?.tier ===
                                              "Guide" ||
                                            user?.unsafeMetadata?.tier ===
                                              "Premium" ||
                                            userPaidStatus === "tech" ||
                                            userPaidStatus === "basic" ||
                                            isAdmin
                                          ) && (
                                            <div className="absolute inset-0 flex items-center justify-center">
                                              <div className="bg-white/95 px-8 py-6 rounded-xl shadow-lg max-w-2xl mx-4">
                                                <div className="flex justify-between items-center">
                                                  <div>
                                                    <h3 className="text-xl font-semibold text-gray-900">
                                                      Premium Guide Content
                                                    </h3>
                                                    <p className="text-gray-600 max-w-[320px]">
                                                      Upgrade to Guide tier to
                                                      unlock this automation
                                                      guide and hundreds more
                                                    </p>
                                                  </div>
                                                  <button
                                                    onClick={() =>
                                                      window.open(
                                                        "https://buy.stripe.com/dR6g2I5Jw2yo3zGeVm",
                                                        "_blank"
                                                      )
                                                    }
                                                    className="flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg bg-gray-800 text-white hover:bg-gray-900 transition-colors shadow-lg"
                                                  >
                                                    Get Access Now
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                    )}
                                    {selectedCard.Image && (
                                      <div>
                                        <img
                                          src={selectedCard.Image[0].url}
                                          alt="Guide Image"
                                          className="w-full h-full object-contain rounded-xl"
                                        />
                                      </div>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Visual Examples/Screenshots */}
                      {(selectedCard?.ExampleOutputs ||
                        selectedCard?.ExampleOutputImages) && (
                        <div className="mb-12">
                          <div className="relative">
                            {/* Add Edit Button */}
                            {isEditing && (
                              <div className="absolute -top-4 right-0 z-10">
                                <button
                                  onClick={() => {
                                    // Get current images
                                    let currentImages = [];
                                    try {
                                      currentImages = selectedCard
                                        .ExampleOutputImages?.value
                                        ? JSON.parse(
                                            selectedCard.ExampleOutputImages
                                              .value
                                          )
                                        : [];
                                    } catch (e) {
                                      currentImages = Array.isArray(
                                        selectedCard.ExampleOutputImages
                                      )
                                        ? selectedCard.ExampleOutputImages
                                        : [];
                                    }

                                    // Prompt for new image URL
                                    const newImageUrl = window.prompt(
                                      "Enter image URL:",
                                      ""
                                    );
                                    if (newImageUrl) {
                                      // Add new image to array
                                      const updatedImages = [
                                        ...currentImages,
                                        newImageUrl,
                                      ];
                                      // Update the field
                                      handleFieldUpdate(
                                        "exampleOutputImages",
                                        JSON.stringify(updatedImages)
                                      );
                                    }
                                  }}
                                  className="px-3 py-1 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 shadow-sm"
                                >
                                  Add Image
                                </button>
                              </div>
                            )}

                            {/* Image Grid - 2 columns */}
                            <div className="grid grid-cols-2 gap-6">
                              {(() => {
                                try {
                                  // Combine both image sources into a single array
                                  let allImages = [];

                                  // Add ExampleOutputs
                                  if (selectedCard.ExampleOutputs) {
                                    const exampleOutputs =
                                      typeof selectedCard.ExampleOutputs ===
                                      "string"
                                        ? JSON.parse(
                                            selectedCard.ExampleOutputs
                                          )
                                        : selectedCard.ExampleOutputs;
                                    allImages = [
                                      ...allImages,
                                      ...exampleOutputs,
                                    ];
                                  }

                                  // Add exampleOutputImages
                                  if (selectedCard.exampleOutputImages) {
                                    let additionalImages = [];
                                    try {
                                      additionalImages =
                                        selectedCard.exampleOutputImages.value;
                                    } catch (e) {
                                      additionalImages =
                                        selectedCard.exampleOutputImages;
                                    }

                                    if (typeof additionalImages === "string") {
                                      try {
                                        additionalImages =
                                          JSON.parse(additionalImages);
                                      } catch (e) {
                                        additionalImages = [additionalImages];
                                      }
                                    }

                                    const validUrls = (
                                      Array.isArray(additionalImages)
                                        ? additionalImages
                                        : [additionalImages]
                                    ).filter((url) => {
                                      try {
                                        new URL(url);
                                        return true;
                                      } catch {
                                        return false;
                                      }
                                    });

                                    allImages = [...allImages, ...validUrls];
                                  }

                                  // Get visible subset based on currentImageSet
                                  const startIdx = currentImageSet * 2;
                                  const visibleImages = allImages.slice(
                                    startIdx,
                                    startIdx + 2
                                  );

                                  return visibleImages.map((imageUrl, i) => (
                                    <div
                                      key={i}
                                      className="relative aspect-[16/10] rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer bg-gray-50 group"
                                    >
                                      {/* Add delete button when editing */}
                                      {isEditing && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (
                                              window.confirm(
                                                "Delete this image?"
                                              )
                                            ) {
                                              // Remove image from array
                                              const updatedImages =
                                                allImages.filter(
                                                  (_, index) =>
                                                    index !== startIdx + i
                                                );
                                              // Update the field
                                              handleFieldUpdate(
                                                "exampleOutputImages",
                                                JSON.stringify(updatedImages)
                                              );
                                            }
                                          }}
                                          className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                        >
                                          <XMarkIcon className="h-4 w-4" />
                                        </button>
                                      )}

                                      <img
                                        src={
                                          typeof imageUrl === "string"
                                            ? imageUrl
                                            : imageUrl.imageUrl
                                        }
                                        alt={`${selectedCard.title} example ${
                                          startIdx + i + 1
                                        }`}
                                        className="object-cover w-full h-full"
                                        onError={(e) => {
                                          console.error(
                                            `Failed to load image: ${imageUrl}`
                                          );
                                          e.target.parentElement.style.display =
                                            "none";
                                        }}
                                        loading="lazy"
                                        onClick={() =>
                                          setExpandedImage({
                                            imageUrl:
                                              typeof imageUrl === "string"
                                                ? imageUrl
                                                : imageUrl.imageUrl,
                                            description: `${
                                              selectedCard.title
                                            } example ${startIdx + i + 1}`,
                                          })
                                        }
                                      />
                                    </div>
                                  ));
                                } catch (error) {
                                  console.error(
                                    "Error rendering images:",
                                    error
                                  );
                                  return null;
                                }
                              })()}
                            </div>

                            {/* Navigation Arrows */}
                            {(() => {
                              try {
                                const allImages = selectedCard.ExampleOutputs
                                  ? typeof selectedCard.ExampleOutputs ===
                                    "string"
                                    ? JSON.parse(selectedCard.ExampleOutputs)
                                    : selectedCard.ExampleOutputs
                                  : [];

                                const totalImages = Array.isArray(allImages)
                                  ? allImages.length
                                  : 0;
                                const totalSets = Math.ceil(totalImages / 2);

                                if (totalImages > 2) {
                                  return (
                                    <>
                                      <button
                                        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 p-2 rounded-full bg-white shadow-lg text-gray-600 hover:text-gray-900 transition-colors z-20"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Calculate previous set, wrapping around to the last set if at the beginning
                                          const newSet =
                                            (currentImageSet - 1 + totalSets) %
                                            totalSets;
                                          debugLog(
                                            "Navigating to previous set:",
                                            newSet,
                                            "of",
                                            totalSets
                                          );
                                          setCurrentImageSet(newSet);
                                        }}
                                      >
                                        <svg
                                          className="w-5 h-5"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 19l-7-7 7-7"
                                          />
                                        </svg>
                                      </button>

                                      <button
                                        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 p-2 rounded-full bg-white shadow-lg text-gray-600 hover:text-gray-900 transition-colors z-20"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Calculate next set, wrapping around to the first set if at the end
                                          const newSet =
                                            (currentImageSet + 1) % totalSets;
                                          debugLog(
                                            "Navigating to next set:",
                                            newSet,
                                            "of",
                                            totalSets
                                          );
                                          setCurrentImageSet(newSet);
                                        }}
                                      >
                                        <svg
                                          className="w-5 h-5"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 5l7 7-7 7"
                                          />
                                        </svg>
                                      </button>
                                    </>
                                  );
                                }
                              } catch (error) {
                                console.error("Error in navigation:", error);
                              }
                              return null;
                            })()}

                            {/* Image Counter/Dots */}
                            {(() => {
                              try {
                                const allImages = selectedCard.ExampleOutputs
                                  ? typeof selectedCard.ExampleOutputs ===
                                    "string"
                                    ? JSON.parse(selectedCard.ExampleOutputs)
                                    : selectedCard.ExampleOutputs
                                  : [];

                                const totalImages = Array.isArray(allImages)
                                  ? allImages.length
                                  : 0;
                                const totalSets = Math.ceil(totalImages / 2);

                                if (totalImages > 2) {
                                  return (
                                    <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                                      {[...Array(totalSets)].map((_, i) => (
                                        <button
                                          key={i}
                                          className={`w-2 h-2 rounded-full transition-colors ${
                                            i === currentImageSet
                                              ? "bg-gray-800"
                                              : "bg-gray-300"
                                          }`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setCurrentImageSet(i);
                                          }}
                                        />
                                      ))}
                                    </div>
                                  );
                                }
                              } catch (error) {
                                console.error(
                                  "Error rendering image counter:",
                                  error
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </div>
                      )}
                      {/* Progress Steps */}
                      <div className="mb-8">
                        <nav aria-label="Progress">
                          <ol role="list" className="flex space-x-8">
                            {buySteps.map((step) => (
                              <li key={step.name} className="flex-1">
                                <div
                                  className={`
                                  group border-t-4 pt-4 pb-0
                                  ${
                                    step.status === "complete"
                                      ? "border-gray-600"
                                      : step.status === "current"
                                        ? "border-gray-600"
                                        : "border-gray-200"
                                  }
                                `}
                                >
                                  <span className="text-sm font-medium text-gray-500">
                                    {step.id}
                                  </span>
                                  <span className="block text-sm font-medium">
                                    {step.name}
                                  </span>
                                </div>
                              </li>
                            ))}
                          </ol>
                        </nav>
                      </div>
                      {/* Core Benefits Section */}
                      <div className="mb-6 bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                        <h3 className="text-xl font-semibold text-gray-900 mb-8">
                          Our Promise
                        </h3>
                        <div className="grid grid-cols-3 gap-8">
                          {[
                            {
                              title: "We Set Up Quickly",
                              description:
                                "2-14 day implementation with dedicated support",
                              icon: <ClockIcon className="h-8 w-8" />,
                            },
                            {
                              title: "30 Risk Free Trial",
                              description: "30-day money back guarantee",
                              icon: <ShieldCheckIcon className="h-8 w-8" />,
                            },
                            {
                              title: "Full Integration",
                              description: "Works with your existing tools",
                              icon: <ArrowPathIcon className="h-8 w-8" />,
                            },
                          ].map((benefit) => (
                            <div key={benefit.title} className="relative">
                              <div className="flex items-start gap-4 mb-2">
                                <div className="text-gray-900 flex-shrink-0 pt-1">
                                  {benefit.icon}
                                </div>
                                <div>
                                  <h4 className="text-base font-semibold text-gray-900 mb-1">
                                    {benefit.title}
                                  </h4>
                                  <p className="text-sm text-gray-500 leading-relaxed">
                                    {benefit.description}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* Call to Action - Thinner version */}
                      <div className="bg-gray-900 rounded-2xl p-5 mb-4 relative overflow-hidden">
                        {/* Subtle gradient overlay */}
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 via-indigo-500/5 to-gray-500/5" />

                        <div className="flex items-center justify-between relative z-10">
                          <div>
                            <h3 className="text-lg font-semibold text-white">
                              Ready to transform your company?
                            </h3>
                            <p className="mt-1 text-sm text-gray-300">
                              Get started today and see the difference.
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            {/* Referral Button */}
                            <button
                              onClick={() => {
                                setShowReferralModal(true);
                                setReferralEmail(
                                  user?.primaryEmailAddress?.emailAddress || ""
                                );
                              }}
                              className="rounded-lg px-6 py-2.5 text-sm font-medium bg-white/10 text-white 
                               border border-white/20 hover:bg-white/15 
                                transition-all duration-200 flex items-center gap-2"
                            >
                              <span>Refer an Upgrader</span>
                            </button>

                            {/* Get Started Button */}
                            <button
                              onClick={() => handleBuyNow(selectedCard)}
                              className="rounded-lg px-6 py-2.5 text-sm font-medium
                                bg-white text-gray-900 hover:bg-gray-100
                                transition-all duration-200 shadow-lg hover:shadow-xl"
                            >
                              Get Started
                            </button>
                          </div>
                        </div>
                      </div>
                      {/* Who This Works Well For Section */}
                      <div className="mb-12 bg-gradient-to-br from-purple-50 to-orange-50 rounded-2xl p-8">
                        <h3 className="text-xl font-semibold text-gray-900 mb-6">
                          Who This Works Well For
                        </h3>
                        {/* Debug output */}
                        {/* <pre className="mb-4 p-2 bg-gray-100 rounded text-xs">
                          {JSON.stringify(selectedCard?.BestFor, null, 2)}
                        </pre> */}
                        {/* <ul className="grid grid-cols-2 gap-6">
                          {selectedCard?.BestFor && JSON.parse(selectedCard.BestFor.value).map((item, i) => (
                            <div key={i} className="flex items-start space-x-4">
                              <div className="flex-shrink-0 p-2 bg-white rounded-lg shadow-sm">
                                {item.icon}
                              </div>
                              <div>
                                <h4 className="text-base font-semibold text-gray-900 mb-1">
                                  {item.title}
                                </h4>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                  {item.description}
                                </p>
                              </div>
                            </div>
                          ))}
                        </ul> */}
                        <ul className="grid grid-cols-2 gap-6">
                          {(() => {
                            try {
                              // Get the BestFor data and handle different possible formats
                              const bestForData =
                                selectedCard?.BestFor?.value ||
                                selectedCard?.BestFor;
                              let parsedData = [];

                              if (typeof bestForData === "string") {
                                const trimmed = bestForData.trim();
                                // Plain comma-separated text (e.g. "Webflow, Shopify") - don't JSON.parse
                                if (trimmed.length > 0 && trimmed[0] !== "[" && trimmed[0] !== "{") {
                                  parsedData = trimmed
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter(Boolean)
                                    .map((title) => ({ title, description: "", icon: "QuestionMarkCircleIcon" }));
                                } else {
                                  const cleanedString = bestForData
                                    .replace(/\n\s*/g, "")
                                    .replace(/([{,])\s*(\w+):/g, '$1"$2":')
                                    .replace(/'/g, '"');
                                  try {
                                    parsedData = JSON.parse(cleanedString);
                                  } catch (parseError) {
                                    try {
                                      if (
                                        cleanedString.trim().startsWith("[") &&
                                        cleanedString.trim().endsWith("]")
                                      ) {
                                        parsedData = eval(`(${cleanedString})`);
                                      }
                                    } catch {
                                      parsedData = [];
                                    }
                                  }
                                }
                              } else if (Array.isArray(bestForData)) {
                                parsedData = bestForData;
                              }

                              return parsedData.map((item, i) => {
                                // Convert icon string to actual component
                                // Example: "WorldIcon" -> HeroIcons["GlobeAltIcon"]
                                const IconComponent =
                                  HeroIcons[item.icon] ||
                                  HeroIcons.QuestionMarkCircleIcon;

                                return (
                                  <div
                                    key={i}
                                    className="flex items-start space-x-4"
                                  >
                                    <div className="flex-shrink-0 p-2 bg-white rounded-lg shadow-sm">
                                      <IconComponent className="h-6 w-6 text-gray-600" />
                                    </div>
                                    <div>
                                      <h4 className="text-base font-semibold text-gray-900 mb-1">
                                        {item.title}
                                      </h4>
                                      <p className="text-sm text-gray-600 leading-relaxed">
                                        {item.description}
                                      </p>
                                    </div>
                                  </div>
                                );
                              });
                            } catch (error) {
                              console.error(
                                "Error parsing BestFor data:",
                                error
                              );
                              debugLog("Raw data:", selectedCard?.BestFor);
                              return (
                                <div className="text-red-500">
                                  Error loading data
                                </div>
                              );
                            }
                          })()}
                        </ul>
                      </div>
                      {/* What's Included + Impact & Results Section */}
                      <div className="grid gap-12">
                        {/* Impact & Results Section */}
                        {selectedCard?.BenefitMetrics.value && (
                          <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm w-full">
                            <h3 className="text-lg font-semibold text-gray-900 mb-8">
                              Impact & Results
                            </h3>
                            <div className="grid grid-cols-2 gap-8">
                              {(() => {
                                try {
                                  // Parse the BenefitMetrics.value if it's a string
                                  const metrics =
                                    typeof selectedCard.BenefitMetrics.value ===
                                    "string"
                                      ? JSON.parse(
                                          selectedCard.BenefitMetrics.value
                                        )
                                      : selectedCard.BenefitMetrics.value;

                                  return metrics.map((benefit, i) => {
                                    // Clean up the benefit string
                                    const cleanBenefit = benefit
                                      .trim()
                                      .replace(/\s+/g, " ");

                                    // Extract metrics using regex patterns
                                    const patterns = [
                                      /\$[\d,]+-\$[\d,]+/, // Matches "$750-$1500"
                                      /\$[\d,]+/, // Matches single dollar amounts
                                      /\d+-\d+(?:\s*hours?|\s*%)?/i, // Matches "2-5 hours" or "20-30%"
                                      /\d+(?:\s*%|\s*hours?)/i, // Matches "30%" or "5 hours"
                                      /\+\d+x/i, // Matches "+2x"
                                    ];

                                    // Find the first matching pattern
                                    let metric = null;
                                    for (const pattern of patterns) {
                                      const match = cleanBenefit.match(pattern);
                                      if (match) {
                                        metric = match[0];
                                        break;
                                      }
                                    }

                                    // If no metric found, try to extract any number
                                    if (!metric) {
                                      const numberMatch =
                                        cleanBenefit.match(/\d+/);
                                      if (numberMatch) {
                                        metric = numberMatch[0];
                                      }
                                    }

                                    return (
                                      <div
                                        key={i}
                                        className="group hover:bg-gray-50 p-4 rounded-xl transition-colors"
                                      >
                                        {metric && (
                                          <div className="text-4xl font-bold text-gray-900 mb-3 tracking-tight">
                                            {metric}
                                          </div>
                                        )}
                                        <div className="text-base text-gray-900 leading-relaxed">
                                          {cleanBenefit}
                                        </div>
                                      </div>
                                    );
                                  });
                                } catch (error) {
                                  console.error(
                                    "Error parsing BenefitMetrics:",
                                    error
                                  );
                                  return null;
                                }
                              })()}
                            </div>
                          </div>
                        )}
                        {/* What's Included Section - Updated styling */}
                        <div className="bg-gradient-to-br from-purple-50 to-orange-50 rounded-2xl p-8 w-full">
                          <h3 className="text-xl font-semibold text-gray-900 mb-6">
                            What's Included
                          </h3>
                          <div className="grid grid-cols-2 gap-6">
                            {isEditing ? (
                              <>
                                {(editedCard?.ProfitableDeliverables.value
                                  ? JSON.parse(
                                      editedCard.ProfitableDeliverables.value
                                    )
                                  : []
                                ).map((feature, i) => (
                                  <li
                                    key={i}
                                    className="flex items-start gap-3 group list-none"
                                  >
                                    <div className="flex-1 flex items-start gap-2">
                                      <CheckIcon className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                                      <input
                                        type="text"
                                        value={feature}
                                        onChange={(e) => {
                                          const newFeatures = JSON.parse(
                                            editedCard.ProfitableDeliverables
                                              .value
                                          );
                                          newFeatures[i] = e.target.value;
                                          handleFieldUpdate(
                                            "ProfitableDeliverables",
                                            JSON.stringify(newFeatures)
                                          );
                                        }}
                                        className="flex-1 bg-transparent border-b border-purple-200 focus:border-purple-500 outline-none text-gray-600 placeholder-gray-400"
                                        placeholder="Add a feature..."
                                      />
                                    </div>
                                    <button
                                      onClick={() => {
                                        const newFeatures = JSON.parse(
                                          editedCard.ProfitableDeliverables
                                            .value
                                        ).filter((_, index) => index !== i);
                                        handleFieldUpdate(
                                          "ProfitableDeliverables.value",
                                          JSON.stringify(newFeatures)
                                        );
                                      }}
                                      className="opacity-0 group-hover:opacity-100 text-purple-600 hover:text-purple-700"
                                    >
                                      <XMarkIcon className="h-5 w-5" />
                                    </button>
                                  </li>
                                ))}
                                <li className="col-span-2 list-none">
                                  <button
                                    onClick={() => {
                                      const currentFeatures = editedCard
                                        .ProfitableDeliverables.value
                                        ? JSON.parse(
                                            editedCard.ProfitableDeliverables
                                              .value
                                          )
                                        : [];
                                      handleFieldUpdate(
                                        "ProfitableDeliverables",
                                        JSON.stringify([...currentFeatures, ""])
                                      );
                                    }}
                                    className="flex items-center gap-2 text-purple-600 hover:text-purple-700"
                                  >
                                    <PlusCircleIcon className="w-4 h-4 text-gray-600" />
                                    <span>Add feature</span>
                                  </button>
                                </li>
                              </>
                            ) : (
                              (() => {
                                const features =
                                  selectedCard?.ProfitableDeliverables?.value
                                    ?.replace(/[\[\]"]/g, "")
                                    ?.split(/[,\n]/)
                                    ?.map((feature) => feature.trim())
                                    ?.filter(
                                      (feature) => feature && feature.length > 0
                                    ) || [];

                                return features.map((feature, i) => (
                                  <div
                                    key={i}
                                    className="flex items-start gap-4 group"
                                  >
                                    <div className="flex-shrink-0 p-2 bg-white rounded-lg shadow-sm">
                                      <CheckIcon className="h-6 w-6 text-gray-600" />
                                    </div>
                                    <div>
                                      <p className="text-base text-gray-900">
                                        {feature}
                                      </p>
                                    </div>
                                  </div>
                                ));
                              })()
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Case Studies Section */}
                      <div className="mt-4 mb-4 bg-gradient-to-br from-purple-50 to-orange-50 rounded-2xl p-8">
                        <h3 className="text-xl font-semibold text-gray-900 mb-6">
                          Success Stories
                        </h3>
                        <div className="grid grid-cols-2 gap-8">
                          {/* Case Study 1 - Millie */}
                          <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-start gap-4 mb-4">
                              <img
                                src="https://logo.clearbit.com/brilliant.com"
                                alt="Company logo"
                                className="w-12 h-12 rounded-lg bg-gray-100 object-contain"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src =
                                    "https://ui-avatars.com/api/?name=B&background=random";
                                }}
                              />
                              <div>
                                <h4 className="font-semibold text-gray-900">
                                  Millie T. @ Brilliant
                                </h4>
                                <p className="text-sm text-gray-500">
                                  Branded Product, E-commerce
                                </p>
                              </div>
                            </div>
                            <div className="space-y-4">
                              <div className="flex items-baseline justify-between border-b border-gray-100 pb-2">
                                <span className="text-sm text-gray-500">
                                  Additional Revenue
                                </span>
                                <span className="font-semibold text-gray-900">
                                  $1,386,590
                                </span>
                              </div>
                              <div className="flex items-baseline justify-between border-b border-gray-100 pb-2">
                                <span className="text-sm text-gray-500">
                                  Time Saved Per Week
                                </span>
                                <span className="font-semibold text-gray-900">
                                  3-20 hours
                                </span>
                              </div>
                              <blockquote className="text-sm text-gray-600 italic">
                                "The AI automation has completely transformed
                                how we handle our sales pipeline."
                              </blockquote>
                              <div className="flex items-center gap-2">
                                <img
                                  src="data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2064%2064%22%20fill%3D%22none%22%20shape-rendering%3D%22auto%22%3E%3Cmetadata%20xmlns%3Ardf%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2F02%2F22-rdf-syntax-ns%23%22%20xmlns%3Axsi%3D%22http%3A%2F%2Fwww.w3.org%2F2001%2FXMLSchema-instance%22%20xmlns%3Adc%3D%22http%3A%2F%2Fpurl.org%2Fdc%2Felements%2F1.1%2F%22%20xmlns%3Adcterms%3D%22http%3A%2F%2Fpurl.org%2Fdc%2Fterms%2F%22%3E%3Crdf%3ARDF%3E%3Crdf%3ADescription%3E%3Cdc%3Atitle%3EPersonas%20by%20Draftbit%3C%2Fdc%3Atitle%3E%3Cdc%3Acreator%3EDraftbit%20-%20draftbit.com%3C%2Fdc%3Acreator%3E%3Cdc%3Asource%20xsi%3Atype%3D%22dcterms%3AURI%22%3Ehttps%3A%2F%2Fpersonas.draftbit.com%2F%3C%2Fdc%3Asource%3E%3Cdcterms%3Alicense%20xsi%3Atype%3D%22dcterms%3AURI%22%3Ehttps%3A%2F%2Fcreativecommons.org%2Flicenses%2Fby%2F4.0%2F%3C%2Fdcterms%3Alicense%3E%3Cdc%3Arights%3ERemix%20of%20%E2%80%9EPersonas%20by%20Draftbit%E2%80%9D%20(https%3A%2F%2Fpersonas.draftbit.com%2F)%20by%20%E2%80%9EDraftbit%20-%20draftbit.com%E2%80%9D%2C%20licensed%20under%20%E2%80%9ECC%20BY%204.0%E2%80%9D%20(https%3A%2F%2Fcreativecommons.org%2Flicenses%2Fby%2F4.0%2F)%3C%2Fdc%3Arights%3E%3C%2Frdf%3ADescription%3E%3C%2Frdf%3ARDF%3E%3C%2Fmetadata%3E%3Cmask%20id%3D%22viewboxMask%22%3E%3Crect%20width%3D%2264%22%20height%3D%2264%22%20rx%3D%220%22%20ry%3D%220%22%20x%3D%220%22%20y%3D%220%22%20fill%3D%22%23fff%22%20%2F%3E%3C%2Fmask%3E%3Cg%20mask%3D%22url(%23viewboxMask)%22%3E%3Cpath%20d%3D%22M37%2046.08V52a5%205%200%200%201-10%200v-5.92A14.04%2014.04%200%200%201%2018.58%2037h-.08a4.5%204.5%200%200%201-.5-8.97V27a14%2014%200%201%201%2028%200v1.03a4.5%204.5%200%200%201-.58%208.97A14.04%2014.04%200%200%201%2037%2046.08Z%22%20fill%3D%22%23eeb4a4%22%2F%3E%3Cmask%20id%3D%22personas-a%22%20style%3D%22mask-type%3Aluminance%22%20maskUnits%3D%22userSpaceOnUse%22%20x%3D%2214%22%20y%3D%2213%22%20width%3D%2236%22%20height%3D%2244%22%3E%3Cpath%20d%3D%22M37%2046.08V52a5%205%200%200%201-10%200v-5.92A14.04%2014.04%200%200%201%2018.58%2037h-.08a4.5%204.5%200%200%201-.5-8.97V27a14%2014%200%201%201%2028%200v1.03a4.5%204.5%200%200%201-.58%208.97A14.04%2014.04%200%200%201%2037%2046.08Z%22%20fill%3D%22%23fff%22%2F%3E%3C%2Fmask%3E%3Cg%20mask%3D%22url(%23personas-a)%22%3E%3Cpath%20d%3D%22M32%2013a14%2014%200%200%201%2014%2014v6a14%2014%200%201%201-28%200v-6a14%2014%200%200%201%2014-14Z%22%20fill%3D%22%23fff%22%20style%3D%22mix-blend-mode%3Aoverlay%22%20opacity%3D%22.36%22%2F%3E%3C%2Fg%3E%3Cg%20transform%3D%22translate(20%2024)%22%3E%3Cpath%20d%3D%22M4%204.5a1.5%201.5%200%201%200%203%200%201.5%201.5%200%200%200-3%200ZM21.24%203.9a.75.75%200%200%200-.83-.64l-4%20.5a.75.75%200%200%200%20.18%201.48l4-.5c.41-.05.7-.42.65-.83Z%22%20fill%3D%22%231B0640%22%2F%3E%3C%2Fg%3E%3Cg%20transform%3D%22translate(2%202)%22%3E%3Cpath%20d%3D%22m15.79%2025.56-.71-6.6A9%209%200%200%201%2024.03%209H41c0%201.28-.26%202.51-.74%203.63a7.93%207.93%200%200%201%204.6%208.35L44%2027v4.22a14%2014%200%200%201-.48%203.42l-2.52.54A1%201%200%200%201%2039.8%2034l1.9-9.12c-1.57-2.09-2.68-5.24-3.31-9.44a9.24%209.24%200%200%201-5.7%202.77c-1.82.2-3.39.29-4.7.29-3%200-5.67-.5-8-1.5%200%202.6-.67%205.1-2%207.52L19.98%2034a1%201%200%200%201-1.2%201.18l-2.51-.54a14%2014%200%200%201-.48-3.42v-5.66Z%22%20fill%3D%22%23362c47%22%2F%3E%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M19.98%2034%2018%2024.52c1.33-2.42%202-4.93%202-7.52%202.33%201%205%201.5%208%201.5%201.31%200%202.88-.1%204.7-.29A9.24%209.24%200%200%200%2041%209H24.03a9%209%200%200%200-8.95%209.97l.7%206.59v5.66a14%2014%200%200%200%20.49%203.42l2.52.54A1%201%200%200%200%2019.98%2034ZM44%2031.22V27l.86-6.02.02-.14a7.91%207.91%200%200%200-2.42-6.73%209.29%209.29%200%200%201-3.45%204.5%2017.22%2017.22%200%200%200%202.7%206.27L39.8%2034A1%201%200%200%200%2041%2035.18l2.52-.54a14%2014%200%200%200%20.48-3.42Z%22%20fill%3D%22%23fff%22%20style%3D%22mix-blend-mode%3Aoverlay%22%20opacity%3D%22.26%22%2F%3E%3C%2Fg%3E%3Cg%20transform%3D%22translate(11%2044)%22%3E%3Cpath%20d%3D%22M16%207v.47a5%205%200%201%200%2010%200V7c7.06%201.52%2012.93%206.74%2016%2013H0C3.07%2013.74%208.94%208.52%2016%207Z%22%20fill%3D%22%23e24553%22%2F%3E%3C%2Fg%3E%3Cg%20transform%3D%22translate(23%2036)%22%3E%3Cellipse%20cx%3D%229%22%20cy%3D%225%22%20rx%3D%222%22%20ry%3D%222.5%22%20fill%3D%22%231B0640%22%2F%3E%3C%2Fg%3E%3Cg%20transform%3D%22translate(24%2028)%22%3E%3Cpath%20d%3D%22M5.29%206.24a.75.75%200%201%201%201.42-.48c.23.7.62%201%201.3%201%20.66%200%201.05-.3%201.28-1a.75.75%200%201%201%201.42.48c-.42%201.3-1.37%202.01-2.7%202.01S5.72%207.54%205.3%206.24Z%22%20fill%3D%22%23000%22%20style%3D%22mix-blend-mode%3Aoverlay%22%20opacity%3D%22.36%22%2F%3E%3C%2Fg%3E%3Cg%20transform%3D%22translate(14%2026)%22%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E"
                                  alt="Millie T."
                                  className="w-8 h-8 rounded-full"
                                />
                                <div className="text-sm">
                                  <p className="font-medium text-gray-900">
                                    Millie T.
                                  </p>
                                  <p className="text-gray-500">Founder</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Case Study 2 - Scott */}
                          <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-start gap-4 mb-4">
                              <img
                                src="https://logo.clearbit.com/fundsquire.com"
                                alt="Company logo"
                                className="w-12 h-12 rounded-lg bg-gray-100 object-contain"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src =
                                    "https://ui-avatars.com/api/?name=F&background=random";
                                }}
                              />
                              <div>
                                <h4 className="font-semibold text-gray-900">
                                  Scott S. @ Fundsquire
                                </h4>
                                <p className="text-sm text-gray-500">
                                  Financial Services
                                </p>
                              </div>
                            </div>
                            <div className="space-y-4">
                              <div className="flex items-baseline justify-between border-b border-gray-100 pb-2">
                                <span className="text-sm text-gray-500">
                                  Additional Revenue
                                </span>
                                <span className="font-semibold text-gray-900">
                                  $1,595,620
                                </span>
                              </div>
                              <div className="flex items-baseline justify-between border-b border-gray-100 pb-2">
                                <span className="text-sm text-gray-500">
                                  Time Saved Per Week
                                </span>
                                <span className="font-semibold text-gray-900">
                                  8-16 hours
                                </span>
                              </div>
                              <blockquote className="text-sm text-gray-600 italic">
                                "The ROI was immediate. Our team now focuses on
                                strategy while AI handles the repetitive work."
                              </blockquote>
                              <div className="flex items-center gap-2">
                                <img
                                  src="data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2064%2064%22%20fill%3D%22none%22%20shape-rendering%3D%22auto%22%3E%3Cmetadata%20xmlns%3Ardf%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2F02%2F22-rdf-syntax-ns%23%22%20xmlns%3Axsi%3D%22http%3A%2F%2Fwww.w3.org%2F2001%2FXMLSchema-instance%22%20xmlns%3Adc%3D%22http%3A%2F%2Fpurl.org%2Fdc%2Felements%2F1.1%2F%22%20xmlns%3Adcterms%3D%22http%3A%2F%2Fpurl.org%2Fdc%2Fterms%2F%22%3E%3Crdf%3ARDF%3E%3Crdf%3ADescription%3E%3Cdc%3Atitle%3EPersonas%20by%20Draftbit%3C%2Fdc%3Atitle%3E%3Cdc%3Acreator%3EDraftbit%20-%20draftbit.com%3C%2Fdc%3Acreator%3E%3Cdc%3Asource%20xsi%3Atype%3D%22dcterms%3AURI%22%3Ehttps%3A%2F%2Fpersonas.draftbit.com%2F%3C%2Fdc%3Asource%3E%3Cdcterms%3Alicense%20xsi%3Atype%3D%22dcterms%3AURI%22%3Ehttps%3A%2F%2Fcreativecommons.org%2Flicenses%2Fby%2F4.0%2F%3C%2Fdcterms%3Alicense%3E%3Cdc%3Arights%3ERemix%20of%20%E2%80%9EPersonas%20by%20Draftbit%E2%80%9D%20(https%3A%2F%2Fpersonas.draftbit.com%2F)%20by%20%E2%80%9EDraftbit%20-%20draftbit.com%E2%80%9D%2C%20licensed%20under%20%E2%80%9ECC%20BY%204.0%E2%80%9D%20(https%3A%2F%2Fcreativecommons.org%2Flicenses%2Fby%2F4.0%2F)%3C%2Fdc%3Arights%3E%3C%2Frdf%3ADescription%3E%3C%2Frdf%3ARDF%3E%3C%2Fmetadata%3E%3Cmask%20id%3D%22viewboxMask%22%3E%3Crect%20width%3D%2264%22%20height%3D%2264%22%20rx%3D%220%22%20ry%3D%220%22%20x%3D%220%22%20y%3D%220%22%20fill%3D%22%23fff%22%20%2F%3E%3C%2Fmask%3E%3Cg%20mask%3D%22url(%23viewboxMask)%22%3E%3Cpath%20d%3D%22M37%2046.08V52a5%205%200%200%201-10%200v-5.92A14.04%2014.04%200%200%201%2018.58%2037h-.08a4.5%204.5%200%200%201-.5-8.97V27a14%2014%200%201%201%2028%200v1.03a4.5%204.5%200%200%201-.58%208.97A14.04%2014.04%200%200%201%2037%2046.08Z%22%20fill%3D%22%23eeb4a4%22%2F%3E%3Cmask%20id%3D%22personas-a%22%20style%3D%22mask-type%3Aluminance%22%20maskUnits%3D%22userSpaceOnUse%22%20x%3D%2214%22%20y%3D%2213%22%20width%3D%2236%22%20height%3D%2244%22%3E%3Cpath%20d%3D%22M37%2046.08V52a5%205%200%200%201-10%200v-5.92A14.04%2014.04%200%200%201%2018.58%2037h-.08a4.5%204.5%200%200%201-.5-8.97V27a14%2014%200%201%201%2028%200v1.03a4.5%204.5%200%200%201-.58%208.97A14.04%2014.04%200%200%201%2037%2046.08Z%22%20fill%3D%22%23fff%22%2F%3E%3C%2Fmask%3E%3Cg%20mask%3D%22url(%23personas-a)%22%3E%3Cpath%20d%3D%22M32%2013a14%2014%200%200%201%2014%2014v6a14%2014%200%201%201-28%200v-6a14%2014%200%200%201%2014-14Z%22%20fill%3D%22%23fff%22%20style%3D%22mix-blend-mode%3Aoverlay%22%20opacity%3D%22.36%22%2F%3E%3C%2Fg%3E%3Cg%20transform%3D%22translate(20%2024)%22%3E%3Cpath%20d%3D%22M4%204.5a1.5%201.5%200%201%200%203%200%201.5%201.5%200%200%200-3%200ZM21.24%203.9a.75.75%200%200%200-.83-.64l-4%20.5a.75.75%200%200%200%20.18%201.48l4-.5c.41-.05.7-.42.65-.83Z%22%20fill%3D%22%231B0640%22%2F%3E%3C%2Fg%3E%3Cg%20transform%3D%22translate(2%202)%22%3E%3Cpath%20d%3D%22m15.79%2025.56-.71-6.6A9%209%200%200%201%2024.03%209H41c0%201.28-.26%202.51-.74%203.63a7.93%207.93%200%200%201%204.6%208.35L44%2027v4.22a14%2014%200%200%201-.48%203.42l-2.52.54A1%201%200%200%201%2039.8%2034l1.9-9.12c-1.57-2.09-2.68-5.24-3.31-9.44a9.24%209.24%200%200%201-5.7%202.77c-1.82.2-3.39.29-4.7.29-3%200-5.67-.5-8-1.5%200%202.6-.67%205.1-2%207.52L19.98%2034a1%201%200%200%201-1.2%201.18l-2.51-.54a14%2014%200%200%201-.48-3.42v-5.66Z%22%20fill%3D%22%23362c47%22%2F%3E%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M19.98%2034%2018%2024.52c1.33-2.42%202-4.93%202-7.52%202.33%201%205%201.5%208%201.5%201.31%200%202.88-.1%204.7-.29A9.24%209.24%200%200%200%2041%209H24.03a9%209%200%200%200-8.95%209.97l.7%206.59v5.66a14%2014%200%200%200%20.49%203.42l2.52.54A1%201%200%200%200%2019.98%2034ZM44%2031.22V27l.86-6.02.02-.14a7.91%207.91%200%200%200-2.42-6.73%209.29%209.29%200%200%201-3.45%204.5%2017.22%2017.22%200%200%200%202.7%206.27L39.8%2034A1%201%200%200%200%2041%2035.18l2.52-.54a14%2014%200%200%200%20.48-3.42Z%22%20fill%3D%22%23fff%22%20style%3D%22mix-blend-mode%3Aoverlay%22%20opacity%3D%22.26%22%2F%3E%3C%2Fg%3E%3Cg%20transform%3D%22translate(11%2044)%22%3E%3Cpath%20d%3D%22M16%207v.47a5%205%200%201%200%2010%200V7c7.06%201.52%2012.93%206.74%2016%2013H0C3.07%2013.74%208.94%208.52%2016%207Z%22%20fill%3D%22%23e24553%22%2F%3E%3C%2Fg%3E%3Cg%20transform%3D%22translate(23%2036)%22%3E%3Cellipse%20cx%3D%229%22%20cy%3D%225%22%20rx%3D%222%22%20ry%3D%222.5%22%20fill%3D%22%231B0640%22%2F%3E%3C%2Fg%3E%3Cg%20transform%3D%22translate(24%2028)%22%3E%3Cpath%20d%3D%22M5.29%206.24a.75.75%200%201%201%201.42-.48c.23.7.62%201%201.3%201%20.66%200%201.05-.3%201.28-1a.75.75%200%201%201%201.42.48c-.42%201.3-1.37%202.01-2.7%202.01S5.72%207.54%205.3%206.24Z%22%20fill%3D%22%23000%22%20style%3D%22mix-blend-mode%3Aoverlay%22%20opacity%3D%22.36%22%2F%3E%3C%2Fg%3E%3Cg%20transform%3D%22translate(14%2026)%22%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E"
                                  alt="Scott S."
                                  className="w-8 h-8 rounded-full"
                                />
                                <div className="text-sm">
                                  <p className="font-medium text-gray-900">
                                    Scott S.
                                  </p>
                                  <p className="text-gray-500">CEO</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Expected Impact Formula Section */}
                      <div className="mt-6 border-gray-200 pt-6 mb-8">
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                          <div className="flex flex-col gap-2 mb-6">
                            <div className="flex items-center gap-2">
                              <h3 className="text-2xl font-semibold text-gray-900">
                                Expected Impact:
                              </h3>
                              <span className="text-2xl font-semibold text-gray-900">
                                +
                                {currencyFormat(
                                  safeImpactAmount(
                                    calculateNetImpactSimpleValue(
                                      selectedCard,
                                      companyData
                                    ).value,
                                    { min: 0, fallback: 0 }
                                  )
                                )}
                                /yr
                              </span>
                              <span className="text-[10px] px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded-full font-medium">
                                BETA
                              </span>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">
                                Simple Net Impact
                              </h4>
                              <code className="block bg-gray-50 p-3 rounded-lg font-mono text-sm text-gray-800 border border-gray-100">
                                {selectedCard?.NetImpactSimple?.value}
                              </code>
                            </div>

                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">
                                Calculated Impact
                              </h4>
                              <code className="block bg-gray-50 p-3 rounded-lg font-mono text-sm text-gray-800 border border-gray-100">
                                {currencyFormat(
                                  safeImpactAmount(
                                    calculateNetImpactSimpleValue(
                                      selectedCard,
                                      companyData
                                    ).value,
                                    { min: 0, fallback: 0 }
                                  )
                                )}{" "}
                                ={" "}
                                {
                                  calculateNetImpactSimpleValue(
                                    selectedCard,
                                    companyData
                                  ).finalFormula
                                }
                              </code>
                            </div>

                            {selectedCard?.NetImpact?.value && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 mb-2">
                                  Detailed Impact Analysis
                                </h4>
                                <code className="block bg-gray-50 p-3 rounded-lg font-mono text-sm text-gray-800 border border-gray-100">
                                  {selectedCard.NetImpact.value}
                                </code>
                              </div>
                            )}

                            {selectedCard?.ExampleNetImpactValues?.value && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 mb-2">
                                  Example Values & Explanation
                                </h4>
                                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-600 bg-gray-50 rounded-lg p-3 border border-gray-100">
                                  {selectedCard.ExampleNetImpactValues.value}
                                </pre>
                              </div>
                            )}

                            <div className="flex items-center gap-2 mt-6 pt-4 border-t border-gray-100">
                              <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                              <p className="text-xs text-gray-500">
                                Help us improve estimates by providing feedback
                                on the calculations and assumptions in the
                                community notes below.
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2 mt-6">
                            <div className="flex items-center gap-2">
                              <h3 className="text-2xl font-semibold text-gray-900">
                                Expected Impact:
                              </h3>
                              <span className="text-2xl font-semibold text-gray-900">
                                +
                                {currencyFormat(
                                  safeImpactAmount(
                                    calculateNetImpactSimpleValue(
                                      selectedCard,
                                      companyData
                                    ).value,
                                    { min: 0, fallback: 0 }
                                  )
                                )}
                                /yr
                              </span>
                              <span className="text-[10px] px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded-full font-medium">
                                BETA
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Estimated Time Spent Section - Updated Design */}
                      {selectedCard?.EstTimeSpent?.value && (
                        <div className="bg-gradient-to-br from-purple-50 to-orange-50 rounded-2xl p-8 mb-8">
                          <h3 className="text-xl font-semibold text-gray-900 mb-6">
                            Time is Money: What Could You Do Instead?
                          </h3>
                          <div className="grid grid-cols-2 gap-8">
                            {(() => {
                              try {
                                const estTimeStr =
                                  selectedCard.EstTimeSpent.value;

                                // Parse setup time
                                const setupMatch = estTimeStr.match(
                                  /Setup Time: (\d+-\d+) hours @ \$(\d+)\/hr \$(\d+-\$?\d+) to set up/
                                );

                                // Parse maintenance time
                                const maintenanceMatch = estTimeStr.match(
                                  /Maintenance Time: (\d+-\d+) hours \/ month @ \$(\d+)\/hr \$(\d+-\$?\d+) \/ month/
                                );

                                return (
                                  <>
                                    {/* Setup Time Block */}
                                    <div className="bg-white rounded-xl p-6 shadow-sm">
                                      <div className="flex flex-col">
                                        <dt className="text-sm text-gray-500 mb-1">
                                          Setup Time
                                        </dt>
                                        <dd className="text-3xl font-bold text-gray-900 mb-2">
                                          {setupMatch
                                            ? `${setupMatch[1]} hours`
                                            : "N/A"}
                                        </dd>
                                        <dd className="text-base text-gray-800">
                                          {setupMatch
                                            ? `${setupMatch[3]} to set up @ $${setupMatch[2]}/hr`
                                            : "N/A"}
                                        </dd>
                                      </div>
                                    </div>

                                    {/* Maintenance Time Block */}
                                    <div className="bg-white rounded-xl p-6 shadow-sm">
                                      <div className="flex flex-col">
                                        <dt className="text-sm text-gray-500 mb-1">
                                          Monthly Maintenance
                                        </dt>
                                        <dd className="text-3xl font-bold text-gray-900 mb-2">
                                          {maintenanceMatch
                                            ? `${maintenanceMatch[1]} hours`
                                            : "N/A"}
                                        </dd>
                                        <dd className="text-base text-gray-800">
                                          {maintenanceMatch
                                            ? `${maintenanceMatch[3]}/month @ $${maintenanceMatch[2]}/hr`
                                            : "N/A"}
                                        </dd>
                                      </div>
                                    </div>
                                  </>
                                );
                              } catch (error) {
                                console.error(
                                  "Error parsing EstTimeSpent:",
                                  error
                                );
                                return null;
                              }
                            })()}
                          </div>
                        </div>
                      )}
                      {/* Key Stats Section - Updated Design */}
                      <div className="mb-12 bg-gradient-to-br from-purple-50 to-orange-50 rounded-2xl p-8">
                        <h3 className="text-xl font-semibold text-gray-900 mb-8 text-center">
                          Your Investment & Timeline
                        </h3>
                        <div className="grid grid-cols-3 gap-8">
                          {[
                            {
                              label: "Setup Cost",
                              stat: (
                                <div className="flex flex-col items-center">
                                  <span className="line-through text-3xl font-semibold text-gray-400">
                                    {selectedCard?.SetupPrice
                                      ? `$${Math.round(selectedCard.SetupPrice)}`
                                      : "$448"}
                                  </span>
                                  <span className="mt-1 text-xs italic text-gray-400 text-center max-w-[120px]">
                                    Starting Summer 2025 (act soon)
                                  </span>
                                </div>
                              ),
                            },
                            {
                              label: "Average Setup Time",
                              stat: selectedCard?.turnaroundDays
                                ? `${selectedCard.turnaroundDays} Days`
                                : "7 Days",
                            },
                            {
                              label: "Monthly Investment",
                              stat: selectedCard?.MonthlyPrice
                                ? `$${Math.round(selectedCard.MonthlyPrice)}`
                                : "$300",
                            },
                          ].map((item) => (
                            <div key={item.label} className="flex flex-col">
                              <dt className="text-sm text-gray-500 mb-1 text-center">
                                {item.label}
                              </dt>
                              <dd className="text-[32px] font-semibold text-gray-900 text-center">
                                {item.stat}
                              </dd>
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* Engagement Options */}
                      <div className="grid grid-cols-3 gap-6 mb-8">
                        {/* Make an Offer Option */}
                        <div className="bg-white rounded-xl p-6 border border-gray-200 hover:border-purple-200 transition-colors flex flex-col h-full relative">
                          <div className="text-center">
                            <h4 className="font-semibold text-gray-900 mb-2">
                              Lowball us, we dare you.
                            </h4>
                            <p className="text-sm text-gray-500 mb-4">
                              Name your price and let's talk.
                            </p>
                            <div className="relative mt-2 mb-4">
                              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                <span className="text-gray-500 sm:text-sm">
                                  $
                                </span>
                              </div>
                              <input
                                type="number"
                                value={offerAmount}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value);
                                  if (value >= 150 || e.target.value === "") {
                                    setOfferAmount(e.target.value);
                                  }
                                }}
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value);
                                  if (value < 150) {
                                    setOfferAmount("150");
                                  }
                                }}
                                className="block w-full rounded-md border-0 py-2 pl-7 pr-20 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                                placeholder="150"
                                min="150"
                                step="10"
                                aria-describedby="price-currency"
                              />
                              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                <span
                                  className="text-gray-500 sm:text-sm"
                                  id="price-currency"
                                >
                                  usd/mo
                                </span>
                              </div>
                            </div>
                          </div>
                          <ul className="space-y-2 mb-4 text-sm text-gray-600">
                            <li className="flex items-center justify-center gap-2">
                              <CheckIcon className="h-4 w-4 text-purple-600" />
                              Custom pricing flexibility
                            </li>
                            <li className="flex items-center justify-center gap-2">
                              <CheckIcon className="h-4 w-4 text-purple-600" />
                              48-hour response guarantee
                            </li>
                          </ul>
                          <div className="text-xs text-gray-500 mb-4 text-center">
                            <span className="text-purple-600 font-medium">
                              324 hours
                            </span>{" "}
                            saved this month
                          </div>
                          <button
                            onClick={handleOfferSubmit}
                            className="w-full rounded-lg px-4 py-2 text-sm font-medium bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors mt-auto"
                          >
                            Make an Offer
                          </button>
                        </div>

                        {/* DIY Guide Option */}
                        <div className="bg-white rounded-xl p-6 border border-gray-200 hover:border-orange-200 transition-colors flex flex-col h-full relative">
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-medium">
                            Most Popular
                          </div>
                          <div className="text-center">
                            <h4 className="font-semibold text-gray-900 mb-2">
                              Not Sure Yet?
                            </h4>
                            <p className="text-[13px] text-gray-500 mb-4">
                              Want to Do This Yourself? Access to our
                              step-by-step guides.
                            </p>
                            <div className="flex items-center justify-center text-center mb-4">
                              <span className="text-2xl font-bold text-gray-900">
                                $99
                              </span>
                              <span className="text-sm text-gray-500">
                                /year
                              </span>
                            </div>
                            <ul className="space-y-2 mb-4 text-sm text-gray-600">
                              <li className="flex items-center justify-center gap-2">
                                <CheckIcon className="h-4 w-4 text-orange-600" />
                                +350 detailed guides
                              </li>
                              <li className="flex items-center justify-center gap-2">
                                <CheckIcon className="h-4 w-4 text-orange-600" />
                                Monthly updates
                              </li>
                            </ul>
                            <div className="text-xs text-gray-500 mb-4">
                              <span className="text-orange-600 font-medium">
                                271 learners
                              </span>{" "}
                              strong
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              window.open(
                                "https://buy.stripe.com/dR6g2I5Jw2yo3zGeVm",
                                "_blank"
                              )
                            }
                            className="w-full rounded-lg px-4 py-2 text-sm font-medium bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors mt-auto"
                          >
                            Try Our Guides
                          </button>
                        </div>

                        {/* Unlimited Option */}
                        <div className="bg-white rounded-xl p-6 border border-gray-200 hover:border-yellow-200 transition-colors flex flex-col h-full">
                          <div className="text-center">
                            <h4 className="font-semibold text-gray-900 mb-2">
                              Want it all? Done for you.
                            </h4>
                            <p className="text-sm text-gray-500 mb-4">
                              One request at a time, <br /> 48hr avg turnaround.
                            </p>
                            <div className="flex items-center justify-center gap-2 mb-4">
                              <ClockIcon className="h-5 w-5 text-yellow-600" />
                              <span className="text-sm text-gray-600">
                                48hr Turnaround Avg
                              </span>
                            </div>
                            <ul className="space-y-2 mb-4 text-sm text-gray-600">
                              <li className="flex items-center justify-center gap-2">
                                <CheckIcon className="h-4 w-4 text-yellow-600" />
                                Unlimited requests
                              </li>
                              <li className="flex items-center justify-center gap-2">
                                <CheckIcon className="h-4 w-4 text-yellow-600" />
                                Priority support
                              </li>
                            </ul>
                            <div className="text-xs text-gray-500 mb-4">
                              <span className="text-yellow-600 font-medium">
                                Only 1 spot
                              </span>{" "}
                              per month
                            </div>
                          </div>
                          <button
                            onClick={handleUnlimitedCheckout}
                            className="w-full rounded-lg px-4 py-2 text-sm font-medium bg-yellow-50 text-yellow-700 hover:bg-yellow-100 transition-colors mt-auto"
                          >
                            Unlimited Automations
                          </button>
                        </div>
                      </div>
                      {/* Call to Action */}
                      <div className="bg-gray-900 rounded-2xl p-8 mb-8 relative overflow-hidden">
                        {/* Subtle gradient overlay */}
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 via-indigo-500/5 to-gray-500/5" />

                        <div className="flex items-center justify-between relative z-10">
                          <div>
                            <h3 className="text-xl font-semibold text-white">
                              Ready to transform your company?
                            </h3>
                            <p className="mt-2 text-gray-300">
                              Get started today and see the difference.
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            {/* Referral Button */}
                            <button
                              onClick={() => {
                                setShowReferralModal(true);
                                setReferralEmail(
                                  user?.primaryEmailAddress?.emailAddress || ""
                                );
                              }}
                              className="rounded-lg px-6 py-3 text-sm font-medium bg-white/10 text-white 
                               border border-white/20 hover:bg-white/15 
                                transition-all duration-200 flex items-center gap-2"
                            >
                              <span>Refer an Upgrader</span>
                            </button>

                            {/* Get Started Button */}
                            <button
                              onClick={() => handleBuyNow(selectedCard)}
                              className="rounded-lg px-6 py-3 text-sm font-medium
                                bg-white text-gray-900 hover:bg-gray-100
                                transition-all duration-200 shadow-lg hover:shadow-xl"
                            >
                              Get Started
                            </button>
                          </div>
                        </div>
                      </div>
                      {/* {selectedCard?.Image && (
                        <div className="relative rounded-xl overflow-hidden bg-gray-100">
                          <img
                            src={selectedCard.Image[0].url}
                            alt={`Original image for ${selectedCard.title}`}
                            className="w-full h-full"
                          />
                        </div>
                      )} */}
                    </div>

                    {/* Community Notes Section */}
                    <div className="mt-8 border-t border-gray-200 pt-8 mb-12 px-8">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Community Notes
                        </h3>
                        <button
                          onClick={() => setShowAddNoteForm(true)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full 
                            bg-gray-900 text-white hover:bg-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                          <PlusIcon className="h-4 w-4 mr-1" />
                          Add Note
                        </button>
                      </div>

                      {/* Notes List */}
                      <div className="space-y-4">
                        {notes.map((note) => (
                          <div
                            key={note.id}
                            className="bg-gray-50 rounded-xl p-4 border border-gray-100"
                          >
                            {/* Note Header + Content in single line */}
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <UserCircleIcon className="h-5 w-5 text-gray-400" />
                                  <span className="text-sm font-medium text-gray-900">
                                    {note.userName}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {formatDistanceToNow(
                                      new Date(note.createdAt)
                                    )}{" "}
                                    ago
                                  </span>
                                </div>
                                <p className="text-sm text-gray-800">
                                  {note.content}
                                </p>
                              </div>

                              {/* Compact Rating Buttons */}
                              <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                                <button
                                  onClick={() =>
                                    handleNoteRating(note.id, true)
                                  }
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs
                                    transition-colors duration-200
                                    ${
                                      note.userRating === true
                                        ? "bg-green-50 text-green-700"
                                        : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                                    }`}
                                >
                                  <HandThumbUpIcon className="h-3 w-3 mr-1" />
                                  <span>{note.helpfulCount}</span>
                                </button>
                                <button
                                  onClick={() =>
                                    handleNoteRating(note.id, false)
                                  }
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs
                                    transition-colors duration-200
                                    ${
                                      note.userRating === false
                                        ? "bg-red-50 text-red-700"
                                        : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                                    }`}
                                >
                                  <HandThumbDownIcon className="h-3 w-3 mr-1" />
                                  <span>{note.notHelpfulCount}</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Empty State - More Compact */}
                      {notes.length === 0 && (
                        <div className="text-center py-8 bg-gray-50 rounded-xl">
                          <ChatBubbleLeftRightIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <h4 className="text-sm font-medium text-gray-900 mb-1">
                            No notes yet
                          </h4>
                          <p className="text-xs text-gray-500 mb-3">
                            Be the first to add a community note
                          </p>
                          <button
                            onClick={() => setShowAddNoteForm(true)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full 
                              bg-gray-900 text-white hover:bg-gray-800 transition-all duration-200"
                          >
                            <PlusIcon className="h-4 w-4 mr-1" />
                            Add Note
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="absolute right-4 top-4 flex space-x-2">
                      {/* Guide button - New */}
                      <div className="relative group">
                        <button
                          type="button"
                          className="rounded-md bg-white text-gray-600 hover:text-gray-700"
                          onClick={() => {
                            debugLog("Guide image button clicked");
                            debugLog(
                              "Current showGuideImage state:",
                              showGuideImage
                            );
                            debugLog(
                              "Selected card image:",
                              selectedCard?.Image
                            );
                            setShowGuideImage((prev) => {
                              debugLog("Setting showGuideImage to:", !prev);
                              return !prev;
                            });
                          }}
                        >
                          <span className="sr-only">Toggle Guide Image</span>
                          <ClipboardDocumentListIcon
                            className="h-6 w-6"
                            aria-hidden="true"
                          />
                        </button>
                        <span className="absolute left-1/2 -translate-x-1/2 mt-4 min-w-[140px] px-4 py-2 rounded bg-black text-white text-xs opacity-0 group-hover:opacity-100 transition pointer-events-none z-10 text-center whitespace-pre-line">
                          Open Guide Image
                        </span>
                      </div>

                      {/* Requirements Form button */}
                      <div className="relative group">
                        <button
                          type="button"
                          className="rounded-md bg-white text-gray-600 hover:text-gray-700"
                          onClick={() => setShowRequirementsForm(true)}
                        >
                          <span className="sr-only">
                            Show Requirements Form
                          </span>
                          <DocumentTextIcon
                            className="h-6 w-6"
                            aria-hidden="true"
                          />
                        </button>
                        <span className="absolute left-1/2 -translate-x-1/2 mt-4 min-w-[140px] px-4 py-2 rounded bg-black text-white text-xs opacity-0 group-hover:opacity-100 transition pointer-events-none z-10 text-center whitespace-pre-line">
                          Get Started on this Automation!
                        </span>
                      </div>

                      {/* Sparkles button - always visible */}
                      {admin && (
                        <>
                          <div className="relative group">
                            <button
                              type="button"
                              className="rounded-md bg-white text-gray-600 hover:text-gray-700"
                              // ... existing code ...
                              onClick={handleAutoComplete}
                              disabled={isLoading}
                            >
                              <span className="sr-only">Auto Complete</span>
                              {isLoading ? (
                                <div className="animate-spin h-6 w-6 border-2 border-purple-600 border-t-transparent rounded-full" />
                              ) : (
                                <SparklesIcon
                                  className="h-6 w-6"
                                  aria-hidden="true"
                                />
                              )}
                            </button>

                            {/* Image Scrape button - Add onClick logging */}
                            <button
                              type="button"
                              className={`rounded-md bg-white text-gray-600 hover:text-gray-700 ${
                                isLoading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
                              onClick={() => {
                                debugLog("Image scrape button clicked");
                                handleImageScrape();
                              }}
                              disabled={isLoading}
                            >
                              <span className="sr-only">Scrape Images</span>
                              <PhotoIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            </button>

                            {/* Edit button */}
                            <button
                              type="button"
                              className={`rounded-md bg-white ${
                                isEditing
                                  ? "text-green-600 hover:text-green-700"
                                  : "text-gray-600 hover:text-gray-600"
                              }`}
                              onClick={() => {
                                if (isEditing) {
                                  handleSaveEdits();
                                } else {
                                  setEditedCard({ ...selectedCard });
                                  setIsEditing(true);
                                }
                              }}
                            >
                              <span className="sr-only">
                                {isEditing ? "Save" : "Edit"}
                              </span>
                              {isEditing ? (
                                <CheckIcon
                                  className="h-6 w-6"
                                  aria-hidden="true"
                                />
                              ) : (
                                <PencilIcon
                                  className="h-6 w-6"
                                  aria-hidden="true"
                                />
                              )}
                            </button>
                          </div>
                        </>
                      )}

                      {/* Share and close buttons */}
                      <div className="relative">
                        <button
                          type="button"
                          className="rounded-md bg-white text-gray-600 hover:text-gray-500"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              getShareableLink(selectedCard)
                            );
                            setShowCopyFeedback(true);
                            setTimeout(() => {
                              setShowCopyFeedback(false);
                            }, 2000);
                          }}
                        >
                          <span className="sr-only">Share</span>
                          <ArrowUpOnSquareIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                        {showCopyFeedback && (
                          <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-md whitespace-nowrap shadow-lg z-[70]">
                            Copied
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="rounded-md bg-white text-gray-600 hover:text-gray-500"
                        onClick={handleModalClose}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
        {/* Requirements Form Modal */}
        <Transition.Root show={showRequirementsForm} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-[60]"
            onClose={setShowRequirementsForm}
          >
            {/* Removed console.log to prevent infinite re-renders */}
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-[60] overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6">
                    <div className="absolute right-2 top-5 pr-4">
                      <button
                        type="button"
                        className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                        onClick={() => setShowRequirementsForm(false)}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="sm:flex sm:items-start">
                      <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                        <div className="flex items-center mb-4">
                          <Dialog.Title
                            as="h3"
                            className="text-lg font-semibold leading-6 text-gray-900"
                          >
                            Required Information
                            {isAdmin && (
                              <button
                                type="button"
                                className="ml-2 p-1 rounded hover:bg-gray-100"
                                onClick={() => {
                                  setEditedRequirementsText(
                                    JSON.stringify(
                                      selectedCard?.requirements,
                                      null,
                                      2
                                    )
                                  );
                                  setIsEditingRequirements(true);
                                }}
                                title="Edit Required Information"
                              >
                                <PencilIcon className="h-5 w-5 text-gray-500" />
                              </button>
                            )}
                          </Dialog.Title>
                        </div>

                        <form
                          onSubmit={handleFormSubmit}
                          className="space-y-6 mb-2"
                        >
                          {(() => {
                            const requirements =
                              selectedCard?.requirements || {};

                            return (
                              <>
                                {/* Client Requirements Section */}
                                {requirements.fromClient &&
                                  requirements.fromClient.length > 0 && (
                                    <div className="mb-8">
                                      <div className="flex items-center gap-x-2 mb-4">
                                        <h4 className="text-sm font-semibold text-gray-900">
                                          Client Requirements
                                        </h4>
                                        <button
                                          type="button"
                                          className="px-3 py-1 rounded bg-zinc-700 text-white text-xs font-semibold hover:bg-zinc-800 transition"
                                          onClick={() =>
                                            handleAutoFillClientRequirements(
                                              requirements
                                            )
                                          }
                                          disabled={isSubmitting}
                                        >
                                          {isSubmitting
                                            ? "Auto-Filling..."
                                            : "Auto-Fill"}
                                        </button>
                                      </div>
                                      {requirements.fromClient.map(
                                        (requirement) => (
                                          <RequirementTextArea
                                            key={requirement.name}
                                            name={`client-${requirement.name}`}
                                            label={requirement.name}
                                            value={
                                              formData[
                                                `client-${requirement.name}`
                                              ]
                                            }
                                            onChange={(value) =>
                                              setFormData((prev) => ({
                                                ...prev,
                                                [`client-${requirement.name}`]:
                                                  value,
                                              }))
                                            }
                                            placeholder={requirement.example}
                                            error={
                                              formErrors[
                                                `client-${requirement.name}`
                                              ]
                                            }
                                          />
                                        )
                                      )}
                                    </div>
                                  )}

                                {/* Technical Requirements Section */}
                                {requirements.technical &&
                                  requirements.technical.length > 0 && (
                                    <div>
                                      <h4 className="text-sm font-semibold text-gray-900 mb-4">
                                        Technical Requirements
                                      </h4>
                                      {requirements.technical.map(
                                        (requirement) => (
                                          <RequirementTextArea
                                            key={requirement.name}
                                            name={`tech-${requirement.name}`}
                                            label={requirement.name}
                                            value={
                                              formData[
                                                `tech-${requirement.name}`
                                              ]
                                            }
                                            onChange={(value) =>
                                              setFormData((prev) => ({
                                                ...prev,
                                                [`tech-${requirement.name}`]:
                                                  value,
                                              }))
                                            }
                                            placeholder={requirement.example}
                                            error={
                                              formErrors[
                                                `tech-${requirement.name}`
                                              ]
                                            }
                                          />
                                        )
                                      )}
                                    </div>
                                  )}
                              </>
                            );
                          })()}

                          {/* Additional Inputs */}
                          <div className="space-y-4 mt-6">
                            {/* 1. Examples Input */}
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-1">
                                Examples (optional)
                              </label>
                              <input
                                type="text"
                                name="examples"
                                value={formData.examples || ""}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    examples: e.target.value,
                                  }))
                                }
                                placeholder="e.g. Example 1, Example 2"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                              />
                            </div>
                            {/* 2. Affiliate Link */}
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-1">
                                Affiliate Link
                              </label>
                              <input
                                type="text"
                                name="affiliateLink"
                                value={
                                  formData.affiliateLink ||
                                  (userRecord?.fields?.ReferralParam
                                    ? `https://getupgraded.ca${userRecord.fields.ReferralParam}`
                                    : user?.publicMetadata?.ReferralParam ||
                                        user?.unsafeMetadata?.ReferralParam ||
                                        user?.ReferralParam
                                      ? `https://getupgraded.ca${
                                          user.publicMetadata?.ReferralParam ||
                                          user.unsafeMetadata?.ReferralParam ||
                                          user?.ReferralParam
                                        }`
                                      : "")
                                }
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    affiliateLink: e.target.value,
                                  }))
                                }
                                placeholder="https://getupgraded.ca?grsf=your-code"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                              />
                            </div>
                            {/* 3. Promoted Website */}
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-1">
                                Promoted Website
                              </label>
                              <input
                                type="text"
                                name="promotedWebsite"
                                defaultValue={
                                  formData.promotedWebsite ||
                                  (user?.primaryEmailAddress?.emailAddress
                                    ? user.primaryEmailAddress.emailAddress.split(
                                        "@"
                                      )[1]
                                    : "")
                                }
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                                onBlur={(e) => {
                                  if (
                                    e.target.value !== formData.promotedWebsite
                                  ) {
                                    setFormData((prev) => ({
                                      ...prev,
                                      promotedWebsite: e.target.value,
                                    }));
                                  }
                                }}
                              />
                            </div>
                          </div>

                          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                            <button
                              type="submit"
                              // className="inline-flex w-full justify-center rounded-md bg-gradient-to-r from-yellow-400 via-orange-500 to-purple-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:from-yellow-500 hover:via-orange-600 hover:to-purple-700 sm:ml-3 sm:w-auto"
                              className="inline-flex shadow-lg justify-center rounded-lg text-sm font-semibold py-2.5 px-4 bg-zinc-900 text-white hover:bg-zinc-700 dark:bg-zinc-600 dark:hover:bg-zinc-700 transition-colors duration-150 ml-3"
                            >
                              Start AI Automation
                            </button>
                            {/* <button
                              className="inline-flex shadow-lg justify-center rounded-lg text-sm font-semibold py-2.5 px-4 bg-zinc-900 text-white hover:bg-zinc-700 dark:bg-zinc-600 dark:hover:bg-zinc-700 transition-colors duration-150"
                            >
                              Start AI
                            </button> */}
                            <button
                              type="button"
                              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                              onClick={() => setShowRequirementsForm(false)}
                            >
                              Cancel
                            </button>
                          </div>
                        </form>

                        {/* Combined Debug Section - Collapsible */}
                        {isAdmin && (
                          <div className="mt-4">
                            <button
                              type="button"
                              onClick={() => setShowDebugInfo(!showDebugInfo)}
                              className="flex items-center justify-between w-full p-2 text-xs font-semibold text-gray-700 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors"
                            >
                              <span>Debug Info</span>
                              {showDebugInfo ? (
                                <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                              ) : (
                                <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                              )}
                            </button>
                            {showDebugInfo && (
                              <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                                <div className="text-xs text-gray-600 space-y-3">
                                  {/* Emails to be notified */}
                                  <div>
                                    <strong className="text-gray-700">Will Email:</strong>
                                    <div className="mt-1 break-all text-gray-500">
                                      {generalAutomationEmails.length > 0
                                        ? generalAutomationEmails.join(", ")
                                        : "(none)"}
                                    </div>
                                  </div>
                                  
                                  {/* Company Data */}
                                  <div>
                                    <strong className="text-gray-700">Company Data:</strong>
                                    <pre className="mt-1 bg-white p-2 rounded text-xs overflow-auto max-h-20">
                                      {JSON.stringify(companyData?.fields, null, 2)}
                                    </pre>
                                  </div>
                                  
                                  {/* Form Data */}
                                  <div>
                                    <strong className="text-gray-700">Form Data:</strong>
                                    <pre className="mt-1 bg-white p-2 rounded text-xs overflow-auto max-h-20">
                                      {JSON.stringify(formData, null, 2)}
                                    </pre>
                                  </div>
                                  
                                  {/* AI Responses */}
                                  <div>
                                    <strong className="text-gray-700">AI Responses:</strong>
                                    <pre className="mt-1 bg-white p-2 rounded text-xs overflow-auto max-h-20">
                                      {JSON.stringify(newDebugInfo, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Add Note Form Modal */}
        <Transition.Root show={showAddNoteForm} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-[60]"
            onClose={setShowAddNoteForm}
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" />

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <AddNoteForm
                  onSubmit={handleSubmitNote}
                  onClose={() => setShowAddNoteForm(false)}
                />
              </div>
            </div>
          </Dialog>
        </Transition.Root>

        <AuthPromptModal isOpen={showAuthModal} setIsOpen={setShowAuthModal} />
        {/* Add this CSS to your stylesheet */}
        <style jsx>{`
          .hide-scrollbar {
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none; /* Chrome, Safari and Opera */
          }
          .notification-wrapper {
            position: fixed;
            top: 24px;
            right: 24px;
            z-index: 100;
          }
          @keyframes todoComplete {
            0% {
              max-height: 200px;
              opacity: 1;
              padding-top: 1rem;
              padding-bottom: 1rem;
              margin-bottom: 0.5rem;
              border-color: rgb(229, 231, 235);
            }
            20% {
              max-height: 200px;
              opacity: 1;
              padding-top: 1rem;
              padding-bottom: 1rem;
              margin-bottom: 0.5rem;
              border-color: rgb(156, 163, 175);
              background-color: rgb(243, 244, 246);
            }
            45% {
              max-height: 200px;
              opacity: 0.8;
              padding-top: 1rem;
              padding-bottom: 1rem;
              margin-bottom: 0.5rem;
            }
            65% {
              max-height: 100px;
              opacity: 0.4;
              padding-top: 0.5rem;
              padding-bottom: 0.5rem;
              margin-bottom: 0.25rem;
            }
            85% {
              max-height: 0;
              opacity: 0;
              padding-top: 0;
              padding-bottom: 0;
              margin-bottom: 0;
              overflow: hidden;
            }
            100% {
              max-height: 0;
              opacity: 0;
              padding-top: 0;
              padding-bottom: 0;
              margin-bottom: 0;
              overflow: hidden;
              pointer-events: none;
            }
          }
          @keyframes checkmark-appear {
            0% {
              opacity: 0;
              transform: scale(0.3);
            }
            20% {
              opacity: 1;
              transform: scale(1.15);
            }
            35% {
              transform: scale(0.95);
            }
            45% {
              transform: scale(1);
              opacity: 1;
            }
            55% {
              opacity: 0.5;
              transform: scale(0.9);
            }
            100% {
              opacity: 0;
              transform: scale(0.8);
            }
          }
          @keyframes checkmark-bounce {
            0%, 100% {
              transform: scale(1);
            }
            25% {
              transform: scale(1.15);
            }
            40% {
              transform: scale(0.95);
            }
            55% {
              transform: scale(1);
            }
          }
          .animate-todo-complete {
            animation: todoComplete 2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
            position: relative;
            will-change: transform, opacity;
          }
          .animate-checkmark-appear {
            animation: checkmark-appear 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
          }
          .animate-checkmark-bounce {
            animation: checkmark-bounce 1.2s cubic-bezier(0.34, 1.56, 0.64, 1);
          }
          @keyframes thinking {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            25% {
              transform: scale(1.1);
              opacity: 0.9;
            }
            50% {
              transform: scale(0.95);
              opacity: 0.8;
            }
            75% {
              transform: scale(1.05);
              opacity: 0.9;
            }
          }
          .animate-thinking {
            animation: thinking 2s ease-in-out infinite;
          }
          .sparkles-gradient-wrapper svg {
            filter: drop-shadow(0 0 2px rgba(96, 165, 250, 0.3));
          }
          .sparkles-gradient-wrapper svg path {
            fill: url(#sparklesGradient);
            transition: opacity 0.2s ease;
          }
          @keyframes subtle-pulse {
            0%, 100% {
              opacity: 0.08;
              transform: scale(1);
            }
            50% {
              opacity: 0.2;
              transform: scale(1.08);
            }
          }
          .animate-subtle-pulse {
            animation: subtle-pulse 4s ease-in-out infinite;
          }
        `}</style>
        {/* SVG Gradient Definition for Sparkles Icon */}
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <linearGradient id="sparklesGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FFB82C" />
              <stop offset="33%" stopColor="#FF6B2C" />
              <stop offset="66%" stopColor="#CD4899" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
          </defs>
        </svg>
        {/* Image Expansion Modal */}
        <Transition.Root show={!!expandedImage} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-[60]"
            onClose={() => setExpandedImage(null)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="relative transform rounded-lg bg-white shadow-xl transition-all max-w-[90vw] max-h-[90vh]">
                    <div className="absolute right-0 top-0 pr-4 pt-4 z-10">
                      <button
                        type="button"
                        className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                        onClick={() => setExpandedImage(null)}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                    {expandedImage && (
                      <img
                        src={expandedImage.imageUrl}
                        alt={expandedImage.description}
                        className="w-full h-full object-contain rounded-lg"
                      />
                    )}
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
        {/* Add this debug panel to your JSX, perhaps below the main content */}
        {process.env.NODE_ENV === "development" && debugInfo.lastAttempt && (
          <div className="fixed bottom-4 right-4 p-4 bg-white rounded-lg shadow-lg border border-gray-200 max-w-md">
            <h4 className="font-semibold mb-2">Debug Info</h4>
            <div className="text-sm space-y-1">
              <p>
                Last attempt: {new Date(debugInfo.lastAttempt).toLocaleString()}
              </p>
              {debugInfo.lastError && (
                <p className="text-red-500">Error: {debugInfo.lastError}</p>
              )}
              <p>
                Selected fields: {debugInfo.selectedFields.join(", ") || "none"}
              </p>
              <p>
                Completed fields:{" "}
                {debugInfo.completedFields.join(", ") || "none"}
              </p>
            </div>
          </div>
        )}
        <Joyride
          steps={tourSteps}
          run={runTour}
          continuous={true}
          showProgress={true}
          showSkipButton={true}
          callback={handleJoyrideCallback}
          styles={{
            options: {
              zIndex: 1000,
              primaryColor: "#6366f1",
            },
          }}
        />
      </div>

      {/* Add this right before the closing UsersProvider tag */}
      <div className="fixed top-0 right-0 z-[100] p-4 pointer-events-none">
        <Notification
          showNotif={notification.showNotif}
          setShowNotif={(val) =>
            setNotification((prev) => ({ ...prev, showNotif: val }))
          }
          contents={notification.contents}
          zindex={100}
        />
      </div>
      {/* Referral Modal */}
      <Transition appear show={showReferralModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setShowReferralModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Refer an Upgrader
                  </Dialog.Title>
                  <div className="mt-2">
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-indigo-100 shadow-sm">
                      <h4 className="font-semibold text-indigo-800 mb-2">
                        Earn Passive Income
                      </h4>
                      <p className="text-sm text-gray-700">
                        Enter the email address of the person you'd like to
                        refer as an Upgrader.
                      </p>
                      <div className="mt-3 bg-white p-3 rounded-md border border-indigo-100">
                        <span className="text-lg font-bold text-indigo-600">
                          5% lifetime revenue
                        </span>
                        <p className="text-sm text-gray-600 mt-1">
                          You'll earn 5% of all revenue generated by your
                          referrals in the "Automate My Work" directory —
                          forever.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <input
                      type="email"
                      value={referralEmail}
                      onChange={(e) => setReferralEmail(e.target.value)}
                      placeholder={
                        user?.primaryEmailAddress?.emailAddress ||
                        "Enter email address"
                      }
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      autoComplete="email"
                    />
                  </div>

                  <div className="mt-4 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                      onClick={() => setShowReferralModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 shadow-lg hover:shadow-xl"
                      onClick={() => {
                        handleInterestSubmission(referralEmail);
                        setReferralEmail("");
                        setShowReferralModal(false);
                      }}
                      disabled={isSubmittingInterest}
                    >
                      {isSubmittingInterest ? (
                        <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mx-auto" />
                      ) : (
                        "Start Earning Now"
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      {isEditingRequirements && (
        <Dialog
          as="div"
          className="relative z-50"
          open={isEditingRequirements}
          onClose={() => setIsEditingRequirements(false)}
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="fixed inset-0 flex items-center justify-center">
            <div
              className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] shadow-xl overflow-y-auto"
              style={{ minWidth: 600 }}
            >
              <h3 className="text-lg font-semibold mb-4">
                Edit Required Information
              </h3>
              <textarea
                className="w-full h-[60vh] border rounded p-2 mb-4 text-xs font-mono resize-vertical"
                value={editedRequirementsText}
                onChange={(e) => setEditedRequirementsText(e.target.value)}
                style={{ minHeight: 300 }}
              />
              <div className="flex justify-end gap-2">
                <button
                  className="px-4 py-2 rounded bg-gray-200"
                  onClick={() => setIsEditingRequirements(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded bg-blue-600 text-white"
                  onClick={async () => {
                    try {
                      const newReqs = JSON.parse(editedRequirementsText);
                      await base("automationsAIGrowthUpgrades").update([
                        {
                          id: selectedCard.id,
                          fields: {
                            requirements: JSON.stringify(newReqs),
                          },
                        },
                      ]);
                      setSelectedCard((prev) => ({
                        ...prev,
                        requirements: newReqs,
                      }));
                      setIsEditingRequirements(false);
                    } catch (e) {
                      alert("Invalid JSON or failed to save.");
                    }
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </Dialog>
      )}

      {/* Edit Automation Modal */}
      <EditAutomationModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingAutomation(null);
        }}
        automation={editingAutomation}
        onSave={handleRefreshAutomations}
      />
    </UsersProvider>
  );
};

export default AutomationDirectory;